import { getIconPath, map } from "@glide/icon-util";
import * as React from "react";
import styled from "styled-components";

import { SVG } from "../utility/svg";
import { getColorsGivenPrimary } from "./color-gradient-utils";
import { Pallettes } from "./color-types";
import type { IconProps, NamedIcon as NamedIconType } from "./named-icons-lite";
import { namedIconsLite } from "./named-icons-lite";

export type NamedIcon = NamedIconType;

function createThemedIcon(colors: string[]) {
    return (
        <svg width="60" height="12" viewBox="0 0 60 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect y="-0.00012207" width="12" height="12" fill={colors[0]} />
            <rect x="12" y="-0.00012207" width="12" height="12" fill={colors[1]} />
            <rect x="24" y="-0.00012207" width="12" height="12" fill={colors[2]} />
            <rect x="36" y="-0.00012207" width="12" height="12" fill={colors[3]} />
            <rect x="48" y="-0.00012207" width="12" height="12" fill={colors[4]} />
        </svg>
    );
}

const namedIcons = {
    barragan: (_props: IconProps) => {
        return createThemedIcon(Pallettes.Barragan.slice(0, 5));
    },
    hockney: (_props: IconProps) => {
        return createThemedIcon(Pallettes.Hockney.slice(0, 5));
    },
    kahlo: (_props: IconProps) => {
        return createThemedIcon(Pallettes.Kahlo.slice(0, 5));
    },
    quinquela: (_props: IconProps) => {
        return createThemedIcon(Pallettes.Quinquela.slice(0, 5));
    },
    magritte: (_props: IconProps) => {
        return createThemedIcon(Pallettes.Magritte.slice(0, 5));
    },
    pamplemousse: (_props: IconProps) => {
        return createThemedIcon(Pallettes.Pamplemousse.slice(0, 5));
    },
    missy: (_props: IconProps) => {
        return createThemedIcon(Pallettes.Missy.slice(0, 5));
    },
    rupaul: (_props: IconProps) => {
        return createThemedIcon(Pallettes.RuPaul.slice(0, 5));
    },
    vermeer: (_props: IconProps) => {
        return createThemedIcon(Pallettes.Vermeer.slice(0, 5));
    },
    ansel: (_props: IconProps) => {
        return createThemedIcon(Pallettes.Ansel.slice(0, 5));
    },
    turrell: (props: IconProps) => {
        const accentColor = props.accentColor ?? "#1522B1";
        return createThemedIcon(getColorsGivenPrimary(accentColor, 5));
    },
    pieChart: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M12.2094 0.0299072C14.4187 0.06847 16.5744 0.716272 18.439 1.90191C20.3036 3.08755 21.8046 4.76498 22.7766 6.74931L12 12.0281L12.2094 0.0299072Z"
                    fill={bg}
                    fillOpacity={0.2}
                />
                <path
                    d="M22.7878 6.77228C23.687 8.61788 24.0976 10.6633 23.9804 12.713C23.8633 14.7626 23.2222 16.7479 22.1186 18.479L12 12.0281L22.7878 6.77228Z"
                    fill={bg}
                />
                <path
                    d="M18.8829 21.8299C17.3317 22.9161 15.5454 23.6197 13.6701 23.8833C11.7948 24.1468 9.88371 23.9628 8.09316 23.3463C6.30261 22.7297 4.68339 21.6982 3.3679 20.3359C2.05241 18.9737 1.078 17.3195 0.52433 15.5085C-0.0293352 13.6975 -0.146534 11.7812 0.182318 9.91622C0.511169 8.05127 1.27673 6.29062 2.41641 4.77823C3.5561 3.26583 5.03754 2.04464 6.73962 1.2145C8.44169 0.384353 10.3161 -0.0311738 12.2095 0.00189055L12 12.0001L18.8829 21.8299Z"
                    fill={bg}
                />
                <path
                    d="M22.1748 18.4236C21.323 19.7797 20.2084 20.9516 18.8967 21.8702L12.0129 12.041L22.1748 18.4236Z"
                    fill={bg}
                />
                <circle cx="12" cy="12" r="12" fill={bg} fillOpacity={0.2} />
                <path
                    d="M22.3923 6C23.7131 8.2877 24.2421 10.9473 23.8973 13.5663C23.5525 16.1853 22.3532 18.6174 20.4853 20.4853C18.6174 22.3532 16.1853 23.5525 13.5663 23.8973C10.9473 24.2421 8.28771 23.7131 6.00001 22.3923C3.71231 21.0715 1.92435 19.0327 0.91345 16.5922C-0.097453 14.1517 -0.274809 11.4458 0.408887 8.89418C1.09258 6.34258 2.59912 4.08788 4.69485 2.47977C6.79058 0.871655 9.35837 3.28549e-06 12 0L12 12L22.3923 6Z"
                    fill={bg}
                />
            </svg>
        );
    },
    donutChart: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24ZM12 19.0001C15.866 19.0001 19 15.8661 19 12.0001C19 8.13407 15.866 5.00006 12 5.00006C8.13401 5.00006 5 8.13407 5 12.0001C5 15.8661 8.13401 19.0001 12 19.0001Z"
                    fill={bg}
                    fillOpacity={0.2}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M23.8973 13.5663C24.2421 10.9473 23.7131 8.2877 22.3923 6L18.0632 8.49942C18.659 9.52915 19 10.7248 19 12C19 15.866 15.866 19 12 19C8.13401 19 5 15.866 5 12C5 8.13401 8.13401 5 12 5L12 0C9.35838 3.28549e-06 6.79058 0.871655 4.69486 2.47977C2.59913 4.08788 1.09259 6.34258 0.408891 8.89418C-0.274805 11.4458 -0.0974487 14.1517 0.913454 16.5922C1.92436 19.0327 3.71231 21.0715 6.00001 22.3923C8.28771 23.7131 10.9473 24.2421 13.5663 23.8973C16.1853 23.5525 18.6174 22.3532 20.4853 20.4853C22.3532 18.6174 23.5525 16.1853 23.8973 13.5663Z"
                    fill={bg}
                />
            </svg>
        );
    },
    donutChartReverse: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10 16C6.68629 16 4 13.3137 4 10C4 6.68629 6.68629 4 10 4C13.3137 4 16 6.68629 16 10C16 13.3137 13.3137 16 10 16ZM10 20C4.47715 20 0 15.5228 0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10C20 15.5228 15.5228 20 10 20Z"
                    fill={bg}
                    fillOpacity={0.2}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4 10H0C0 4.47715 4.47715 0 10 0V4C6.68629 4 4 6.68629 4 10Z"
                    fill={bg}
                />
            </svg>
        );
    },
    barChart: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M8.68964 9.50006C8.68964 8.39549 9.58507 7.50006 10.6896 7.50006H13.3103C14.4149 7.50006 15.3103 8.39549 15.3103 9.50006V22.0001C15.3103 23.1046 14.4149 24.0001 13.3103 24.0001H10.6896C9.58506 24.0001 8.68964 23.1046 8.68964 22.0001V9.50006Z"
                    fill={bg}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M17 2.00006C17 0.895491 17.8954 6.10352e-05 19 6.10352e-05H22.0345C23.1391 6.10352e-05 24.0345 0.895492 24.0345 2.00006V22.0001C24.0345 23.1046 23.1391 24.0001 22.0345 24.0001H19C17.8954 24.0001 17 23.1046 17 22.0001V2.00006Z"
                    fill={bg}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M0 16.0001C0 14.8955 0.895431 14.0001 2 14.0001H5C6.10457 14.0001 7 14.8955 7 16.0001V22.0001C7 23.1046 6.10457 24.0001 5 24.0001H2C0.89543 24.0001 0 23.1046 0 22.0001V16.0001Z"
                    fill={bg}
                />
            </svg>
        );
    },
    gLogo: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M10.0016 8.5912V11.648H14.3576C13.788 13.5 12.24 14.8256 10.0016 14.8256C7.336 14.8256 5.1752 12.6648 5.1752 10C5.1752 7.3352 7.336 5.1744 10.0016 5.1744C11.2 5.1744 12.2944 5.6136 13.1384 6.3368L15.3896 4.0856C13.968 2.7904 12.0768 2 10.0016 2C5.5824 2 2 5.5816 2 10C2 14.4184 5.5824 18 10.0016 18C16.7184 18 18.2008 11.72 17.5424 8.6016L10.0016 8.5912Z"
                    fill={bg}
                />
            </svg>
        );
    },
    glideLogo: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 6.6 L6.6 0 V3.24 C6.6 5.09568 5.09568 6.6 3.24 6.6 H0 Z" fill={bg} />
                <path d="M5.4 8.76 C5.4 6.90432 6.90432 5.4 8.76 5.4 H12 L5.4 12 V8.76 Z" fill={bg} />
            </svg>
        );
    },
    stackedBarChart: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M0 7H4V13H0L0 7Z" fill={bg} />
                <path fillRule="evenodd" clipRule="evenodd" d="M5 7H7V13H5V7Z" fill={bg} fillOpacity={0.2} />
                <path fillRule="evenodd" clipRule="evenodd" d="M14 7H20V13H14V7Z" fill={bg} fillOpacity={0.2} />
                <path fillRule="evenodd" clipRule="evenodd" d="M8 7H13V13H8V7Z" fill={bg} />
            </svg>
        );
    },
    userCircle: (props: IconProps) => {
        const size = props.size ?? 20;
        return (
            <AppIcon
                color={props.bgColor}
                size={size}
                icon={"17-18-single-neutral-circle"}
                className={props.className}
            />
        );
    },
    containingScreenContext: (props: IconProps) => {
        const fg = props.fgColor ?? "white";
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M14 9.51852H16.4C17.8607 9.51852 19 8.3168 19 6.88889V3.62963C19 2.187 17.8463 1 16.4 1H14C12.6029 1 11.4998 2.09933 11.4064 3.44444H8.60163C8.50792 2.0868 7.39189 1 6.008 1H3.6C2.1505 1 1 2.19843 1 3.62963V6.88889C1 8.33152 2.15372 9.51852 3.6 9.51852H6C6.93814 9.51852 7.74369 9.02284 8.2 8.28991V13.1193C8.2 14.9365 9.60486 16.4523 11.406 16.5505C11.4973 17.9105 12.6144 19 14 19H16.4C17.8607 19 19 17.7983 19 16.3704V13.1111C19 11.6685 17.8463 10.4815 16.4 10.4815H14C13.0619 10.4815 12.2563 10.9772 11.8 11.7101V8.28693C12.2582 9.0222 13.0672 9.51852 14 9.51852Z"
                    fill={bg}
                    stroke={fg}
                    strokeWidth="2"
                />
            </svg>
        );
    },
    containingScreenOverlay: (props: IconProps) => {
        const fg = props.fgColor ?? "white";
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M12 8.88889H13.8C15.0387 8.88889 16 7.87051 16 6.66667V4.22222C16 3.0057 15.0263 2 13.8 2H12C10.8973 2 10.0145 2.80694 9.83389 3.83333H8.17229C7.99094 2.7986 7.09874 2 6.006 2H4.2C2.9705 2 2 3.01509 2 4.22222V6.66667C2 7.88319 2.97372 8.88889 4.2 8.88889H6C6.53754 8.88889 7.02283 8.69712 7.4 8.38164V11.3394C7.4 12.7603 8.44652 13.9615 9.82969 14.143C10.0015 15.1892 10.8989 16 12 16H13.8C15.0387 16 16 14.9816 16 13.7778V11.3333C16 10.1168 15.0263 9.11111 13.8 9.11111H12C11.4625 9.11111 10.9772 9.30288 10.6 9.61836V8.37948C10.9784 8.69644 11.4647 8.88889 12 8.88889Z"
                    fill={bg}
                    stroke={fg}
                    strokeWidth="2"
                />
            </svg>
        );
    },
    leftAlign: (props: IconProps) => {
        return <AppIcon color={props.bgColor} size={20} icon={"01-37-paragraph-left-align"} />;
    },
    centerAlign: (props: IconProps) => {
        return <AppIcon color={props.bgColor} size={20} icon={"01-37-paragraph-center-align"} />;
    },
    rightAlign: (props: IconProps) => {
        return <AppIcon color={props.bgColor} size={20} icon={"01-37-paragraph-right-align"} />;
    },
    justifyAlign: (props: IconProps) => {
        return <AppIcon color={props.bgColor} size={20} icon={"01-37-paragraph-justified-align"} />;
    },
    noColorGSheet: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2 6.37915C2 5.27458 2.89543 4.37915 4 4.37915H20C21.1046 4.37915 22 5.27458 22 6.37915V19.3792C22 20.4837 21.1046 21.3792 20 21.3792H4C2.89543 21.3792 2 20.4837 2 19.3792V6.37915ZM7 6.37915C7 5.82687 7.44772 5.37915 8 5.37915C8.55228 5.37915 9 5.82687 9 6.37915V9.37915L20 9.37915C20.5523 9.37915 21 9.82687 21 10.3792C21 10.9314 20.5523 11.3792 20 11.3792L9 11.3791V19.3792C9 19.9314 8.55228 20.3792 8 20.3792C7.44772 20.3792 7 19.9314 7 19.3792V11.3791H4C3.44772 11.3791 3 10.9314 3 10.3791C3 9.82687 3.44772 9.37915 4 9.37915H7V6.37915Z"
                    fill={bg}
                />
            </svg>
        );
    },
    randomSort: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4.22222 2H19.7778C21 2 22 3 22 4.22222L21.9889 19.7778C21.9889 21 21 22 19.7778 22H4.22222C3 22 2 21 2 19.7778V4.22222C2 3 3 2 4.22222 2ZM5.94733 9.66667C5.54483 9.66608 5.21817 9.33942 5.21875 8.93692C5.21933 8.53442 5.54542 8.20833 5.94792 8.20833H8.73333C9.84167 8.25675 10.8543 8.84883 11.44 9.79092C11.6681 10.1176 11.5911 10.5668 11.2679 10.7995C10.9413 11.0346 10.4851 10.9605 10.25 10.6338C9.92742 10.0884 9.36392 9.72908 8.73333 9.66667H5.94792H5.94733ZM18.6833 8.61083C18.8139 8.80683 18.8139 9.06233 18.6833 9.25833L17.5697 10.9308C17.4612 11.0929 17.2792 11.1903 17.0843 11.1903C17.0272 11.1909 16.97 11.1822 16.9152 11.1653C16.669 11.0906 16.501 10.8643 16.501 10.607V9.8125C16.501 9.732 16.4357 9.66667 16.3552 9.66667H15.4137C14.422 9.66667 13.5406 11.0515 12.6895 12.3914C11.6255 14.0633 10.5253 15.7917 8.73333 15.7917H5.94792C5.54542 15.7917 5.21875 15.465 5.21875 15.0625C5.21875 14.66 5.54542 14.3333 5.94792 14.3333H8.73333C9.72331 14.3333 10.6022 12.9521 11.4531 11.6149L11.4575 11.608C12.5215 9.93617 13.6217 8.20775 15.4137 8.20775H16.3552C16.4357 8.20775 16.501 8.14242 16.501 8.06192V7.26683C16.501 7.072 16.5984 6.89 16.7606 6.7815C17.0289 6.60242 17.3912 6.67475 17.5697 6.94308L18.6833 8.61083ZM17.0843 17.3153C17.0272 17.3159 16.97 17.3072 16.9152 17.2903C16.6673 17.2162 16.4981 16.9881 16.4987 16.7297V15.9346C16.4987 15.8541 16.4333 15.7888 16.3528 15.7888H15.4113C14.31 15.7403 13.2752 15.248 12.5431 14.4238C12.2631 14.1338 12.2712 13.6718 12.5612 13.3918C12.8511 13.1118 13.3131 13.12 13.5931 13.4099C14.0545 13.9454 14.709 14.2762 15.4137 14.3304H16.3552C16.4357 14.3304 16.501 14.2651 16.501 14.1846V13.3895C16.501 13.1947 16.5984 13.0127 16.7606 12.9042C17.0289 12.7251 17.3912 12.7974 17.5697 13.0658L18.6833 14.7358C18.8139 14.9318 18.8139 15.1873 18.6833 15.3833L17.5697 17.0558C17.4612 17.2179 17.2792 17.3153 17.0843 17.3153Z"
                    fill={bg}
                />
            </svg>
        );
    },
    textArea: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M12 3H13C14.1046 3 15 3.89543 15 5V19C15 20.1046 14.1046 21 13 21H12"
                    stroke={bg}
                    strokeWidth="2"
                />
                <path
                    d="M18 3H17C15.8954 3 15 3.89543 15 5V19C15 20.1046 15.8954 21 17 21H18"
                    stroke={bg}
                    strokeWidth="2"
                />
                <path d="M12 7.5H3V16.5H12" stroke={bg} strokeWidth="2" />
                <path d="M15 7.5H21V16.5H15" stroke={bg} strokeWidth="2" />
            </svg>
        );
    },
    faceGravity: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M12.8675 14.4217C12.6112 14.5944 12.3091 14.6866 12 14.6866C11.6909 14.6866 11.3888 14.5944 11.1325 14.4217C11.0008 14.3259 10.837 14.2853 10.6758 14.3084C10.5147 14.3314 10.3688 14.4164 10.2693 14.5452C10.1697 14.674 10.1243 14.8366 10.1427 14.9984C10.161 15.1601 10.2416 15.3084 10.3675 15.4117C10.8378 15.7664 11.4109 15.9583 12 15.9583C12.5891 15.9583 13.1622 15.7664 13.6325 15.4117C13.7584 15.3084 13.839 15.1601 13.8573 14.9984C13.8757 14.8366 13.8303 14.674 13.7307 14.5452C13.6312 14.4164 13.4853 14.3314 13.3242 14.3084C13.163 14.2853 12.9992 14.3259 12.8675 14.4217V14.4217Z"
                    fill={bg}
                />
                <path
                    d="M13.6666 13.25C14.1269 13.25 14.5 12.8769 14.5 12.4167C14.5 11.9564 14.1269 11.5833 13.6666 11.5833C13.2064 11.5833 12.8333 11.9564 12.8333 12.4167C12.8333 12.8769 13.2064 13.25 13.6666 13.25Z"
                    fill={bg}
                />
                <path
                    d="M10.3333 13.25C10.7936 13.25 11.1667 12.8769 11.1667 12.4167C11.1667 11.9564 10.7936 11.5833 10.3333 11.5833C9.8731 11.5833 9.5 11.9564 9.5 12.4167C9.5 12.8769 9.8731 13.25 10.3333 13.25Z"
                    fill={bg}
                />
                <path
                    d="M17.8334 12.8333V10.75C17.8334 9.2029 17.2188 7.71917 16.1248 6.62521C15.0308 5.53125 13.5471 4.91667 12 4.91667C10.4529 4.91667 8.96919 5.53125 7.87523 6.62521C6.78127 7.71917 6.16669 9.2029 6.16669 10.75V12.8333C6.16669 14.3804 6.78127 15.8642 7.87523 16.9581C8.96919 18.0521 10.4529 18.6667 12 18.6667C13.5471 18.6667 15.0308 18.0521 16.1248 16.9581C17.2188 15.8642 17.8334 14.3804 17.8334 12.8333ZM12 17C10.8954 16.9987 9.83632 16.5593 9.0552 15.7781C8.27409 14.997 7.83468 13.938 7.83335 12.8333V10.9583C7.83335 10.9031 7.8553 10.8501 7.89437 10.811C7.93344 10.7719 7.98643 10.75 8.04169 10.75H8.66669C9.74252 10.75 11.9234 11.015 13.5317 9.23917C13.5488 9.22035 13.5696 9.2053 13.5929 9.19497C13.6161 9.18464 13.6413 9.17926 13.6667 9.17917C13.6921 9.1791 13.7173 9.18441 13.7406 9.19475C13.7639 9.20509 13.7847 9.22023 13.8017 9.23917C14.3932 9.89748 15.1695 10.3621 16.0292 10.5725C16.0686 10.5825 16.1036 10.6054 16.1285 10.6376C16.1534 10.6698 16.1668 10.7093 16.1667 10.75V12.8333C16.1654 13.938 15.726 14.997 14.9448 15.7781C14.1637 16.5593 13.1047 16.9987 12 17V17Z"
                    fill={bg}
                />
                <path
                    d="M7.41667 20.3333H3.875C3.81975 20.3333 3.76676 20.3114 3.72769 20.2723C3.68862 20.2332 3.66667 20.1803 3.66667 20.125V16.5833C3.66667 16.3623 3.57887 16.1504 3.42259 15.9941C3.26631 15.8378 3.05435 15.75 2.83333 15.75C2.61232 15.75 2.40036 15.8378 2.24408 15.9941C2.0878 16.1504 2 16.3623 2 16.5833V20.3333C2 20.7754 2.17559 21.1993 2.48816 21.5118C2.80072 21.8244 3.22464 22 3.66667 22H7.41667C7.63768 22 7.84964 21.9122 8.00592 21.7559C8.1622 21.5996 8.25 21.3877 8.25 21.1667C8.25 20.9457 8.1622 20.7337 8.00592 20.5774C7.84964 20.4211 7.63768 20.3333 7.41667 20.3333Z"
                    fill={bg}
                />
                <path
                    d="M2.83333 8.25C3.05435 8.25 3.26631 8.1622 3.42259 8.00592C3.57887 7.84964 3.66667 7.63768 3.66667 7.41667V3.875C3.66667 3.81975 3.68862 3.76676 3.72769 3.72769C3.76676 3.68862 3.81975 3.66667 3.875 3.66667H7.41667C7.63768 3.66667 7.84964 3.57887 8.00592 3.42259C8.1622 3.26631 8.25 3.05435 8.25 2.83333C8.25 2.61232 8.1622 2.40036 8.00592 2.24408C7.84964 2.0878 7.63768 2 7.41667 2H3.66667C3.22464 2 2.80072 2.17559 2.48816 2.48816C2.17559 2.80072 2 3.22464 2 3.66667L2 7.41667C2 7.63768 2.0878 7.84964 2.24408 8.00592C2.40036 8.1622 2.61232 8.25 2.83333 8.25Z"
                    fill={bg}
                />
                <path
                    d="M21.1667 15.75C20.9457 15.75 20.7337 15.8378 20.5774 15.9941C20.4211 16.1504 20.3333 16.3623 20.3333 16.5833V20.125C20.3333 20.1803 20.3114 20.2332 20.2723 20.2723C20.2332 20.3114 20.1803 20.3333 20.125 20.3333H16.5833C16.3623 20.3333 16.1504 20.4211 15.9941 20.5774C15.8378 20.7337 15.75 20.9457 15.75 21.1667C15.75 21.3877 15.8378 21.5996 15.9941 21.7559C16.1504 21.9122 16.3623 22 16.5833 22H20.3333C20.7754 22 21.1993 21.8244 21.5118 21.5118C21.8244 21.1993 22 20.7754 22 20.3333V16.5833C22 16.3623 21.9122 16.1504 21.7559 15.9941C21.5996 15.8378 21.3877 15.75 21.1667 15.75Z"
                    fill={bg}
                />
                <path
                    d="M20.3333 2H16.5833C16.3623 2 16.1504 2.0878 15.9941 2.24408C15.8378 2.40036 15.75 2.61232 15.75 2.83333C15.75 3.05435 15.8378 3.26631 15.9941 3.42259C16.1504 3.57887 16.3623 3.66667 16.5833 3.66667H20.125C20.1803 3.66667 20.2332 3.68862 20.2723 3.72769C20.3114 3.76676 20.3333 3.81975 20.3333 3.875V7.41667C20.3333 7.63768 20.4211 7.84964 20.5774 8.00592C20.7337 8.1622 20.9457 8.25 21.1667 8.25C21.3877 8.25 21.5996 8.1622 21.7559 8.00592C21.9122 7.84964 22 7.63768 22 7.41667V3.66667C22 3.22464 21.8244 2.80072 21.5118 2.48816C21.1993 2.17559 20.7754 2 20.3333 2V2Z"
                    fill={bg}
                />
            </svg>
        );
    },
    centerGravity: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M21.1667 11.1667H19.4508C19.0575 7.6875 16.3125 4.94167 12.8333 4.54667V2.83333C12.8333 2.37333 12.46 2 12 2C11.54 2 11.1667 2.37333 11.1667 2.83333V4.54667C7.6875 4.94167 4.9425 7.6875 4.54917 11.1667H2.83333C2.37333 11.1667 2 11.54 2 12C2 12.46 2.37333 12.8333 2.83333 12.8333H4.54917C4.94417 16.3108 7.68917 19.055 11.1667 19.4492V21.1667C11.1667 21.6267 11.54 22 12 22C12.46 22 12.8333 21.6267 12.8333 21.1667V19.4492C16.3108 19.055 19.0558 16.3108 19.4508 12.8333H21.1667C21.6267 12.8333 22 12.46 22 12C22 11.54 21.6267 11.1667 21.1667 11.1667ZM17.7258 10.9167C17.7483 11.03 17.675 11.14 17.5617 11.1625C17.5483 11.165 17.5342 11.1667 17.5208 11.1667H15.8117C15.7175 11.1667 15.635 11.1042 15.6108 11.0133C15.26 9.7375 14.2625 8.74 12.9867 8.38917C12.8958 8.36417 12.8333 8.28083 12.8333 8.18667V6.4775C12.8333 6.36167 12.9275 6.26833 13.0425 6.26833C13.0558 6.26833 13.07 6.27 13.0833 6.2725C15.4367 6.72167 17.2775 8.5625 17.7258 10.9167ZM12 14.0833C10.8492 14.0833 9.91667 13.1508 9.91667 12C9.91667 10.8492 10.8492 9.91667 12 9.91667C13.1508 9.91667 14.0833 10.8492 14.0833 12C14.0833 13.1508 13.1508 14.0833 12 14.0833ZM10.9167 6.2725C11.03 6.25 11.14 6.32333 11.1625 6.43667C11.165 6.45 11.1667 6.46417 11.1667 6.4775V8.18667C11.1667 8.28083 11.1042 8.36333 11.0133 8.3875C9.7375 8.73833 8.74 9.73583 8.38917 11.0117C8.365 11.1025 8.2825 11.1658 8.18833 11.1667H6.47917C6.36333 11.1667 6.27 11.0725 6.27 10.9575C6.27 10.9442 6.27167 10.93 6.27417 10.9167C6.7225 8.5625 8.56333 6.72167 10.9167 6.2725ZM6.27417 13.0783C6.25083 12.9658 6.32417 12.8558 6.43667 12.8325C6.45083 12.83 6.465 12.8283 6.47917 12.8283H8.18833C8.2825 12.8283 8.365 12.8908 8.38917 12.9817C8.74 14.2575 9.7375 15.255 11.0133 15.6058C11.1042 15.6308 11.1667 15.7125 11.1667 15.8067V17.5158C11.1667 17.5775 11.1392 17.6367 11.0908 17.6758C11.0433 17.7158 10.98 17.7317 10.9192 17.7208C8.565 17.2733 6.72333 15.4325 6.27417 13.0783ZM13.0833 17.7242C13.0225 17.735 12.9592 17.7192 12.9117 17.6792C12.8625 17.6408 12.8342 17.5817 12.8333 17.5192V15.81C12.8333 15.7158 12.8958 15.6333 12.9867 15.6092C14.2625 15.2583 15.26 14.2608 15.6108 12.985C15.6358 12.895 15.7183 12.8325 15.8117 12.8333H17.5208C17.6358 12.8325 17.7292 12.9242 17.73 13.0392C17.73 13.0542 17.7283 13.0692 17.7258 13.0833C17.2767 15.4358 15.4358 17.2758 13.0833 17.7242Z"
                    fill={bg}
                />
            </svg>
        );
    },
    borderCircle: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="4" y="4" width="16" height="16" rx="8" stroke={bg} fill="none" strokeWidth="2" />
            </svg>
        );
    },
    borderSquare: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M19 3H5C3.9 3 3 4.0125 3 5.25V18.75C3 19.9875 3.9 21 5 21H19C20.1 21 21 19.9875 21 18.75V5.25C21 4.0125 20.1 3 19 3ZM18 18.75H6C5.45 18.75 5 18.2438 5 17.625V6.375C5 5.75625 5.45 5.25 6 5.25H18C18.55 5.25 19 5.75625 19 6.375V17.625C19 18.2438 18.55 18.75 18 18.75Z"
                    fill={bg}
                />
            </svg>
        );
    },
    border3by2: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M19 6H5C3.9 6 3 6.9 3 8V16C3 17.1 3.9 18 5 18H19C20.1 18 21 17.1 21 16V8C21 6.9 20.1 6 19 6ZM18 16H6C5.45 16 5 15.55 5 15V9C5 8.45 5.45 8 6 8H18C18.55 8 19 8.45 19 9V15C19 15.55 18.55 16 18 16Z"
                    fill={bg}
                />
            </svg>
        );
    },
    border3by1: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M19 7H5C3.9 7 3 7.9 3 9V15C3 16.1 3.9 17 5 17H19C20.1 17 21 16.1 21 15V9C21 7.9 20.1 7 19 7ZM18 15H6C5.45 15 5 14.55 5 14V10C5 9.45 5.45 9 6 9H18C18.55 9 19 9.45 19 10V14C19 14.55 18.55 15 18 15Z"
                    fill={bg}
                />
            </svg>
        );
    },
    borderOriginal: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="17" height="11" viewBox="0 0 17 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M3.97656 10.1333C4.5415 10.1333 4.92236 9.75244 4.92236 9.1748V1.77344C4.92236 1.11328 4.51611 0.700684 3.83691 0.700684C3.44336 0.700684 3.13867 0.757812 2.69434 1.0625L0.878906 2.31934C0.580566 2.52881 0.466309 2.74463 0.466309 3.03662C0.466309 3.44287 0.751953 3.72217 1.13916 3.72217C1.33594 3.72217 1.46924 3.67773 1.64697 3.55078L2.98633 2.61768H3.02441V9.1748C3.02441 9.75244 3.41162 10.1333 3.97656 10.1333ZM9.22607 4.22998C9.79102 4.22998 10.248 3.77295 10.248 3.20801C10.248 2.63672 9.79102 2.18604 9.22607 2.18604C8.65479 2.18604 8.2041 2.63672 8.2041 3.20801C8.2041 3.77295 8.65479 4.22998 9.22607 4.22998ZM9.22607 8.76855C9.79102 8.76855 10.248 8.31152 10.248 7.74658C10.248 7.17529 9.79102 6.72461 9.22607 6.72461C8.65479 6.72461 8.2041 7.17529 8.2041 7.74658C8.2041 8.31152 8.65479 8.76855 9.22607 8.76855ZM16.0117 10.1333C16.5767 10.1333 16.9575 9.75244 16.9575 9.1748V1.77344C16.9575 1.11328 16.5513 0.700684 15.8721 0.700684C15.4785 0.700684 15.1738 0.757812 14.7295 1.0625L12.9141 2.31934C12.6157 2.52881 12.5015 2.74463 12.5015 3.03662C12.5015 3.44287 12.7871 3.72217 13.1743 3.72217C13.3711 3.72217 13.5044 3.67773 13.6821 3.55078L15.0215 2.61768H15.0596V9.1748C15.0596 9.75244 15.4468 10.1333 16.0117 10.1333Z"
                    fill={bg}
                />
            </svg>
        );
    },
    border2by3: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect
                    x="17"
                    y="4"
                    width="16"
                    height="10"
                    rx="1"
                    transform="rotate(90 17 4)"
                    stroke={bg}
                    strokeWidth="2"
                    fill="none"
                />
            </svg>
        );
    },
    border3by4: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect
                    x="18"
                    y="4"
                    width="16"
                    height="12"
                    rx="1"
                    transform="rotate(90 18 4)"
                    stroke={bg}
                    strokeWidth="2"
                    fill="none"
                />
            </svg>
        );
    },
    border4by3: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="4" y="6" width="16" height="12" rx="1" stroke={bg} strokeWidth="2" fill="none" />
            </svg>
        );
    },
    buttonFilled: (props: IconProps) => {
        const BtnFilled = styled.svg`
            path {
                fill: ${p => p.theme.n0};
            }
        `;
        const bg = props.bgColor ?? "currentColor";
        return (
            <BtnFilled width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="22" height="22" rx="2" fill={bg} />
                <path d="M9.5 14.3333H13.6667L14.4167 16.1667H16.1667L12.2083 7H10.9583L7 16.1667H8.75L9.5 14.3333ZM11.5833 8.65L13.1417 12.8333H10.025L11.5833 8.65Z" />
            </BtnFilled>
        );
    },
    buttonTransparent: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="22" height="22" rx="2" fill={bg} fillOpacity="0.4" />
                <path
                    d="M9.5 14.3333H13.6667L14.4167 16.1667H16.1667L12.2083 7H10.9583L7 16.1667H8.75L9.5 14.3333ZM11.5833 8.65L13.1417 12.8333H10.025L11.5833 8.65Z"
                    fill={bg}
                />
            </svg>
        );
    },
    buttonBordered: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const BtnBordered = styled.svg`
            rect {
                fill: ${p => p.theme.n0};
            }
        `;
        return (
            <BtnBordered width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="22" height="22" rx="2" stroke={bg} strokeWidth="2" strokeLinecap="square" />
                <path
                    d="M9.5 14.3333H13.6667L14.4167 16.1667H16.1667L12.2083 7H10.9583L7 16.1667H8.75L9.5 14.3333ZM11.5833 8.65L13.1417 12.8333H10.025L11.5833 8.65Z"
                    fill={bg}
                />
            </BtnBordered>
        );
    },
    buttonSimple: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M9.5 14.3333H13.6667L14.4167 16.1667H16.1667L12.2083 7H10.9583L7 16.1667H8.75L9.5 14.3333ZM11.5833 8.65L13.1417 12.8333H10.025L11.5833 8.65Z"
                    fill={bg}
                />
            </svg>
        );
    },
    buttonFloating: (props: IconProps) => {
        const BtnFloating = styled.svg`
            path {
                fill: ${p => p.theme.n0};
            }
        `;
        const bg = props.bgColor ?? "currentColor";
        return (
            <BtnFloating width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill={bg} />
                <ellipse opacity="0.2" cx="12" cy="13.5" rx="10" ry="10.5" fill={bg} />
                <path d="M9.91675 14.3333H14.0834L14.8334 16.1667H16.5834L12.625 7H11.375L7.41675 16.1667H9.16675L9.91675 14.3333ZM12 8.65L13.5584 12.8333H10.4417L12 8.65Z" />
            </BtnFloating>
        );
    },
    cornersSharp: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 2H22V22H2V2Z" fill={bg} />
            </svg>
        );
    },
    cornersRound: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 23C2 11.9543 10.9543 3 22 3V23H2Z" fill={bg} />
            </svg>
        );
    },
    calendar: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M1 8C0.447715 8 0 8.44772 0 9V17C0 18.6569 1.34315 20 3 20H17C18.6569 20 20 18.6569 20 17V9C20 8.44772 19.5523 8 19 8H1ZM4 10C3.44772 10 3 10.4477 3 11C3 11.5523 3.44772 12 4 12H16C16.5523 12 17 11.5523 17 11C17 10.4477 16.5523 10 16 10H4ZM4.5 14C3.67157 14 3 14.6716 3 15.5C3 16.3284 3.67157 17 4.5 17C5.32843 17 6 16.3284 6 15.5C6 14.6716 5.32843 14 4.5 14Z"
                />
                <path
                    d="M4 1C4 0.447715 4.44772 0 5 0C5.55228 0 6 0.447715 6 1V2H13V1C13 0.447715 13.4477 0 14 0C14.5523 0 15 0.447715 15 1V2H17C18.6569 2 20 3.34315 20 5V6C20 6.55228 19.5523 7 19 7H1C0.447716 7 0 6.55228 0 6V5C0 3.34315 1.34315 2 3 2H4V1Z"
                    fill={bg}
                />
            </svg>
        );
    },
    tinder: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.58248 3.97081C6.81307 2.33008 8.33007 1.18693 9.9708 1.41752L16.6968 2.3628C18.3376 2.59339 19.4807 4.1104 19.2501 5.75113L17.669 17.0013C17.4384 18.642 15.9214 19.7852 14.2807 19.5546L7.55466 18.6093C5.91393 18.3787 4.77078 16.8617 5.00137 15.221L6.58248 3.97081Z" />
                <path
                    d="M0.204365 4.47383C-0.0139749 2.83143 1.14046 1.32299 2.78286 1.10466L10.7131 0.0504187C12.3555 -0.167921 13.8639 0.986512 14.0823 2.62892L15.7954 15.5155C16.0137 17.1579 14.8593 18.6664 13.2169 18.8847L5.28667 19.939C3.64427 20.1573 2.13584 19.0029 1.9175 17.3605L0.204365 4.47383Z"
                    fill={bg}
                />
            </svg>
        );
    },
    columns: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="8" height="8" rx="2.5" fill={bg} />
                <rect x="1" y="11" width="8" height="8" rx="2" />
                <rect x="11" y="1" width="8" height="8" rx="2" />
                <rect x="11" y="11" width="8" height="8" rx="2" />
            </svg>
        );
    },
    cards: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4 0C2.34315 0 1 1.34315 1 3V13C1 13.5523 1.44772 14 2 14H18C18.5523 14 19 13.5523 19 13V3C19 1.34315 17.6569 0 16 0H4ZM4 2C3.44772 2 3 2.44772 3 3C3 3.55228 3.44772 4 4 4H9C9.55228 4 10 3.55228 10 3C10 2.44772 9.55229 2 9 2H4Z"
                    fill={bg}
                />
                <path d="M1 16C1 15.4477 1.44772 15 2 15H18C18.5523 15 19 15.4477 19 16V18C19 19.1046 18.1046 20 17 20H3C1.89543 20 1 19.1046 1 18V16Z" />
            </svg>
        );
    },
    details: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="18" height="12" rx="2.5" fill={bg} />
                <path d="M1 15C1 15.5523 1.44772 16 2 16H12C12.5523 16 13 15.5523 13 15C13 14.4477 12.5523 14 12 14H2C1.44772 14 1 14.4477 1 15Z" />
                <path d="M1 18C1 18.5523 1.44772 19 2 19H17C17.5523 19 18 18.5523 18 18C18 17.4477 17.5523 17 17 17H2C1.44772 17 1 17.4477 1 18Z" />
            </svg>
        );
    },
    gallery: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M1 1H7V7H1V1ZM9 1H15V7H9V1ZM23 1H17V7H23V1ZM1 9H7V15H1V9ZM15 9H9V15H15V9ZM17 9H23V15H17V9ZM7 17H1V23H7V17ZM9 17H15V23H9V17ZM23 17H17V23H23V17Z"
                    fill={bg}
                />
            </svg>
        );
    },
    grid: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M1 1H23V7H1V1ZM1 9H23V15H1V9ZM23 17H1V23H23V17Z"
                    fill={bg}
                />
            </svg>
        );
    },
    list: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M1 3C1 1.34315 2.34315 0 4 0H16C17.6569 0 19 1.34315 19 3V4.5C19 5.32843 18.3284 6 17.5 6H2.5C1.67157 6 1 5.32843 1 4.5V3Z"
                    fill={bg}
                />
                <path d="M1 8.5C1 7.67157 1.67157 7 2.5 7H17.5C18.3284 7 19 7.67157 19 8.5V11.5C19 12.3284 18.3284 13 17.5 13H2.5C1.67157 13 1 12.3284 1 11.5V8.5Z" />
                <path d="M1 15.5C1 14.6716 1.67157 14 2.5 14H17.5C18.3284 14 19 14.6716 19 15.5V17C19 18.6569 17.6569 20 16 20H4C2.34315 20 1 18.6569 1 17V15.5Z" />
            </svg>
        );
    },
    checklist: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 9C1.34315 9 0 10.3431 0 12V17C0 18.6569 1.34315 20 3 20H8C9.65685 20 11 18.6569 11 17H7C4.79086 17 3 15.2091 3 13V9Z" />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7 1C4.79086 1 3 2.79086 3 5V13C3 15.2091 4.79086 17 7 17H15C17.2091 17 19 15.2091 19 13V5C19 2.79086 17.2091 1 15 1H7ZM16.2571 5.4626C16.5126 5.13656 16.4554 4.66515 16.1293 4.40966C15.8033 4.15417 15.3319 4.21136 15.0764 4.5374L9.85641 11.1988L7.09528 8.39131C6.80483 8.09598 6.32998 8.09203 6.03465 8.38247C5.73933 8.67291 5.73537 9.14777 6.02582 9.44309L9.3855 12.8592C9.53678 13.013 9.74696 13.0943 9.96236 13.0821C10.1778 13.07 10.3775 12.9657 10.5106 12.7959L16.2571 5.4626Z"
                    fill={bg}
                />
            </svg>
        );
    },
    map: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10 21C13.866 21 17 20.5523 17 20C17 19.4477 13.866 19 10 19C6.13401 19 3 19.4477 3 20C3 20.5523 6.13401 21 10 21Z"
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M17.9699 8.93663C17.9699 14.1659 12.315 18.7669 10.5176 20.0972C10.1931 20.3374 9.75904 20.3374 9.43455 20.0972C7.63714 18.7669 1.9823 14.1659 1.9823 8.93663C1.9823 4.52178 5.56124 0.942841 9.97609 0.942841C14.3909 0.942841 17.9699 4.52178 17.9699 8.93663ZM9.97614 11.6012C11.4478 11.6012 12.6407 10.4082 12.6407 8.93662C12.6407 7.46501 11.4478 6.27203 9.97614 6.27203C8.50452 6.27203 7.31154 7.46501 7.31154 8.93662C7.31154 10.4082 8.50452 11.6012 9.97614 11.6012Z"
                    fill={bg}
                />
            </svg>
        );
    },
    table: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g>
                    <path d="M10 6C9.44772 6 9 6.44772 9 7V9C9 9.55229 9.44772 10 10 10H18C18.5523 10 19 9.55229 19 9V7C19 6.44772 18.5523 6 18 6H10Z" />
                    <path d="M9 12C9 11.4477 9.44772 11 10 11H18C18.5523 11 19 11.4477 19 12V14C19 14.5523 18.5523 15 18 15H10C9.44772 15 9 14.5523 9 14V12Z" />
                    <path d="M10 16C9.44772 16 9 16.4477 9 17V19C9 19.5523 9.44772 20 10 20H16.3333C17.8061 20 19 18.8061 19 17.3333C19 16.597 18.403 16 17.6667 16H10Z" />
                    <path d="M2 6C1.44772 6 1 6.44772 1 7V9C1 9.55229 1.44772 10 2 10H7C7.55228 10 8 9.55229 8 9V7C8 6.44772 7.55228 6 7 6H2Z" />
                    <path d="M1 12C1 11.4477 1.44772 11 2 11H7C7.55228 11 8 11.4477 8 12V14C8 14.5523 7.55228 15 7 15H2C1.44772 15 1 14.5523 1 14V12Z" />
                    <path d="M2.33333 16C1.59695 16 1 16.597 1 17.3333C1 18.8061 2.19391 20 3.66667 20H7C7.55228 20 8 19.5523 8 19V17C8 16.4477 7.55228 16 7 16H2.33333Z" />
                </g>
                <g fill={bg}>
                    <path d="M1 3.66667C1 2.19391 2.19391 1 3.66667 1H7C7.55228 1 8 1.44772 8 2V4C8 4.55228 7.55228 5 7 5H2.33333C1.59695 5 1 4.40305 1 3.66667Z" />
                    <path d="M9 2C9 1.44772 9.44772 1 10 1H16.3333C17.8061 1 19 2.19391 19 3.66667C19 4.40305 18.403 5 17.6667 5H10C9.44772 5 9 4.55228 9 4V2Z" />
                </g>
            </svg>
        );
    },
    listView: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="44" height="44" rx="4" fill="none" />
                <g clipPath="url(#clip0)">
                    <path d="M18 16H31" stroke={bg} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M18 22H31" stroke={bg} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M18 28H31" stroke={bg} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="13" cy="16" r="1" fill={bg} />
                    <circle cx="13" cy="22" r="1" fill={bg} />
                    <circle cx="13" cy="28" r="1" fill={bg} />
                </g>
                <defs>
                    <clipPath id="clip0">
                        <rect width="24" height="24" fill="white" transform="translate(10 10)" />
                    </clipPath>
                </defs>
            </svg>
        );
    },
    mapView: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M8.66667 3C8.51092 3 8.36517 3.04197 8.2405 3.11495L2.41992 6.38056C2.16025 6.5262 2 6.79726 2 7.09091V20.1818C2 20.4735 2.15817 20.7431 2.41508 20.8895C2.672 21.0358 2.98883 21.0369 3.24675 20.8922L8.69292 17.8367L14.9457 20.9062C15.0538 20.9622 15.176 20.9954 15.3057 20.9996C15.3149 20.9998 15.3242 21 15.3333 21C15.4891 21 15.6348 20.958 15.7595 20.885L21.5801 17.6194C21.8398 17.4738 22 17.2027 22 16.9091V3.81818C22 3.5265 21.8418 3.25691 21.5849 3.11054C21.328 2.96416 21.0112 2.9631 20.7532 3.10784L15.3071 6.16325L9.05433 3.09376C8.93858 3.03387 8.80667 3 8.66667 3ZM16.1667 18.7719L20.3333 16.4343V5.22807L16.1667 7.5657V18.7719ZM14.5 7.59655L9.5 5.142V16.4035L14.5 18.858V7.59655ZM3.66667 7.5657L7.83333 5.22807V16.4343L3.66667 18.7719V7.5657Z"
                    fill={bg}
                />
            </svg>
        );
    },
    paddingTight: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C12 1.44772 12.4477 1 13 1H23V23H13C12.4477 23 12 22.5523 12 22V2Z" fill={bg} />
                <path d="M0 1H10C10.5523 1 11 1.44772 11 2V22C11 22.5523 10.5523 23 10 23H0V1Z" fill={bg} />
            </svg>
        );
    },
    paddingLoose: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1H4C4.55228 1 5 1.44772 5 2V22C5 22.5523 4.55228 23 4 23H1V1Z" fill={bg} />
                <path d="M19 2C19 1.44772 19.4477 1 20 1H23V23H20C19.4477 23 19 22.5523 19 22V2Z" fill={bg} />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.82322 13.1767C5.72559 13.0791 5.72559 12.9208 5.82322 12.8231L7.41421 11.2322C7.51184 11.1345 7.67014 11.1345 7.76777 11.2322C7.8654 11.3298 7.8654 11.4881 7.76777 11.5857L6.60355 12.7499L17.3964 12.7499L16.2322 11.5857C16.1346 11.4881 16.1346 11.3298 16.2322 11.2322C16.3299 11.1345 16.4882 11.1345 16.5858 11.2322L18.1768 12.8231C18.2744 12.9208 18.2744 13.0791 18.1768 13.1767L16.5858 14.7677C16.4882 14.8653 16.3299 14.8653 16.2322 14.7677C16.1346 14.6701 16.1346 14.5118 16.2322 14.4141L17.3964 13.2499L6.60355 13.2499L7.76777 14.4141C7.8654 14.5118 7.8654 14.6701 7.76777 14.7677C7.67014 14.8653 7.51184 14.8653 7.41421 14.7677L5.82322 13.1767Z"
                    fill={bg}
                />
                <path
                    d="M5.82322 12.8231L6.17678 13.1767H6.17678L5.82322 12.8231ZM5.82322 13.1767L6.17678 12.8231H6.17678L5.82322 13.1767ZM7.41421 11.2322L7.76777 11.5857L7.41421 11.2322ZM7.76777 11.5857L7.41421 11.2322L7.76777 11.5857ZM6.60355 12.7499L6.25 12.3964L5.39645 13.2499H6.60355L6.60355 12.7499ZM17.3964 12.7499V13.2499H18.6036L17.75 12.3964L17.3964 12.7499ZM16.2322 11.5857L15.8787 11.9393L15.8787 11.9393L16.2322 11.5857ZM16.2322 11.2322L15.8787 10.8786L15.8787 10.8786L16.2322 11.2322ZM16.5858 11.2322L16.9393 10.8786L16.9393 10.8786L16.5858 11.2322ZM18.1768 12.8231L17.8232 13.1767L17.8232 13.1767L18.1768 12.8231ZM18.1768 13.1767L17.8232 12.8231L17.8232 12.8231L18.1768 13.1767ZM16.5858 14.7677L16.9393 15.1212L16.9393 15.1212L16.5858 14.7677ZM16.2322 14.7677L15.8787 15.1212L15.8787 15.1212L16.2322 14.7677ZM16.2322 14.4141L15.8787 14.0606L15.8787 14.0606L16.2322 14.4141ZM17.3964 13.2499L17.75 13.6035L18.6036 12.7499H17.3964V13.2499ZM6.60355 13.2499L6.60355 12.7499H5.39645L6.25 13.6035L6.60355 13.2499ZM7.76777 14.4141L8.12132 14.0606L7.76777 14.4141ZM7.41421 14.7677L7.06066 15.1212L7.41421 14.7677ZM5.46967 12.4696C5.17678 12.7625 5.17678 13.2374 5.46967 13.5303L6.17678 12.8231C6.27441 12.9208 6.27441 13.0791 6.17678 13.1767L5.46967 12.4696ZM7.06066 10.8786L5.46967 12.4696L6.17678 13.1767L7.76777 11.5857L7.06066 10.8786ZM8.12132 10.8786C7.82843 10.5857 7.35355 10.5857 7.06066 10.8786L7.76777 11.5857C7.67014 11.6833 7.51184 11.6833 7.41421 11.5857L8.12132 10.8786ZM8.12132 11.9393C8.41421 11.6464 8.41421 11.1715 8.12132 10.8786L7.41421 11.5857C7.31658 11.4881 7.31658 11.3298 7.41421 11.2322L8.12132 11.9393ZM6.95711 13.1035L8.12132 11.9393L7.41421 11.2322L6.25 12.3964L6.95711 13.1035ZM17.3964 12.2499L6.60355 12.2499L6.60355 13.2499L17.3964 13.2499V12.2499ZM15.8787 11.9393L17.0429 13.1035L17.75 12.3964L16.5858 11.2322L15.8787 11.9393ZM15.8787 10.8786C15.5858 11.1715 15.5858 11.6464 15.8787 11.9393L16.5858 11.2322C16.6834 11.3298 16.6834 11.4881 16.5858 11.5857L15.8787 10.8786ZM16.9393 10.8786C16.6464 10.5857 16.1716 10.5857 15.8787 10.8786L16.5858 11.5857C16.4882 11.6833 16.3299 11.6833 16.2322 11.5857L16.9393 10.8786ZM18.5303 12.4696L16.9393 10.8786L16.2322 11.5857L17.8232 13.1767L18.5303 12.4696ZM18.5303 13.5303C18.8232 13.2374 18.8232 12.7625 18.5303 12.4696L17.8232 13.1767C17.7256 13.0791 17.7256 12.9208 17.8232 12.8231L18.5303 13.5303ZM16.9393 15.1212L18.5303 13.5303L17.8232 12.8231L16.2322 14.4141L16.9393 15.1212ZM15.8787 15.1212C16.1716 15.4141 16.6464 15.4141 16.9393 15.1212L16.2322 14.4141C16.3299 14.3165 16.4882 14.3165 16.5858 14.4141L15.8787 15.1212ZM15.8787 14.0606C15.5858 14.3535 15.5858 14.8284 15.8787 15.1212L16.5858 14.4141C16.6834 14.5118 16.6834 14.6701 16.5858 14.7677L15.8787 14.0606ZM17.0429 12.8964L15.8787 14.0606L16.5858 14.7677L17.75 13.6035L17.0429 12.8964ZM6.60355 13.7499L17.3964 13.7499V12.7499L6.60355 12.7499L6.60355 13.7499ZM8.12132 14.0606L6.95711 12.8964L6.25 13.6035L7.41421 14.7677L8.12132 14.0606ZM8.12132 15.1212C8.41421 14.8284 8.41421 14.3535 8.12132 14.0606L7.41421 14.7677C7.31658 14.6701 7.31658 14.5118 7.41421 14.4141L8.12132 15.1212ZM7.06066 15.1212C7.35355 15.4141 7.82843 15.4141 8.12132 15.1212L7.41421 14.4141C7.51184 14.3165 7.67014 14.3165 7.76777 14.4141L7.06066 15.1212ZM5.46967 13.5303L7.06066 15.1212L7.76777 14.4141L6.17678 12.8231L5.46967 13.5303Z"
                    fill={bg}
                />
            </svg>
        );
    },
    alignmentTopLeft: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M22 5C22 3.34315 20.6569 2 19 2L5 2C3.34315 2 2 3.34315 2 5L2 19C2 20.6569 3.34315 22 5 22H19C20.6569 22 22 20.6569 22 19V5ZM20 5C20 4.44772 19.5523 4 19 4L5 4C4.44772 4 4 4.44772 4 5L4 19C4 19.5523 4.44772 20 5 20H19C19.5523 20 20 19.5523 20 19V5Z"
                    fill={bg}
                />
                <path
                    d="M6.66 6C6.29549 6 6 6.29549 6 6.66V11.2033C6 11.4973 6.35546 11.6445 6.56335 11.4367L11.4367 6.56335C11.6445 6.35546 11.4973 6 11.2033 6L6.66 6Z"
                    fill={bg}
                />
            </svg>
        );
    },
    alignmentBottomLeft: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5 2C3.34315 2 2 3.34315 2 5V19C2 20.6569 3.34315 22 5 22H19C20.6569 22 22 20.6569 22 19V5C22 3.34315 20.6569 2 19 2H5ZM5 4C4.44772 4 4 4.44772 4 5V19C4 19.5523 4.44772 20 5 20H19C19.5523 20 20 19.5523 20 19V5C20 4.44772 19.5523 4 19 4H5Z"
                    fill={bg}
                />
                <path
                    d="M6 17.34C6 17.7045 6.29549 18 6.66 18H11.2033C11.4973 18 11.6445 17.6445 11.4367 17.4367L6.56335 12.5633C6.35546 12.3555 6 12.5027 6 12.7967V17.34Z"
                    fill={bg}
                />
            </svg>
        );
    },
    alignmentTopRight: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M19 22C20.6569 22 22 20.6569 22 19L22 5C22 3.34315 20.6569 2 19 2L5 2C3.34314 2 2 3.34315 2 5L2 19C2 20.6569 3.34314 22 5 22L19 22ZM19 20C19.5523 20 20 19.5523 20 19L20 5C20 4.44772 19.5523 4 19 4L5 4C4.44771 4 4 4.44772 4 5L4 19C4 19.5523 4.44771 20 5 20L19 20Z"
                    fill={bg}
                />
                <path
                    d="M18 6.66C18 6.29549 17.7045 6 17.34 6H12.7967C12.5027 6 12.3555 6.35546 12.5633 6.56335L17.4367 11.4367C17.6445 11.6445 18 11.4973 18 11.2033L18 6.66Z"
                    fill={bg}
                />
            </svg>
        );
    },
    alignmentBottomRight: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2 19C2 20.6569 3.34314 22 5 22L19 22C20.6569 22 22 20.6569 22 19L22 5C22 3.34315 20.6569 2 19 2L5 2C3.34315 2 2 3.34315 2 5L2 19ZM4 19C4 19.5523 4.44771 20 5 20L19 20C19.5523 20 20 19.5523 20 19L20 5C20 4.44772 19.5523 4 19 4L5 4C4.44771 4 4 4.44772 4 5L4 19Z"
                    fill={bg}
                />
                <path
                    d="M17.34 18C17.7045 18 18 17.7045 18 17.34V12.7967C18 12.5027 17.6445 12.3555 17.4367 12.5633L12.5633 17.4367C12.3555 17.6445 12.5027 18 12.7967 18L17.34 18Z"
                    fill={bg}
                />
            </svg>
        );
    },
    alignmentCenter: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M19 22C20.6569 22 22 20.6569 22 19L22 5C22 3.34315 20.6569 2 19 2L5 2C3.34315 2 2 3.34315 2 5L2 19C2 20.6569 3.34315 22 5 22L19 22ZM19 20C19.5523 20 20 19.5523 20 19L20 5C20 4.44772 19.5523 4 19 4L5 4C4.44772 4 4 4.44772 4 5L4 19C4 19.5523 4.44772 20 5 20L19 20Z"
                    fill={bg}
                />
                <path
                    d="M13.5 13H10.5C9.94772 13 9.5 13.4477 9.5 14C9.5 14.5523 9.94772 15 10.5 15H13.5C14.0523 15 14.5 14.5523 14.5 14C14.5 13.4477 14.0523 13 13.5 13Z"
                    fill={bg}
                />
                <path
                    d="M15 10H9C8.44772 10 8 10.4477 8 11C8 11.5523 8.44772 12 9 12H15C15.5523 12 16 11.5523 16 11C16 10.4477 15.5523 10 15 10Z"
                    fill={bg}
                />
            </svg>
        );
    },
    textPositionOverlay: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3 4C3 3.44772 3.44772 3 4 3H20C20.5523 3 21 3.44772 21 4V20C21 20.5523 20.5523 21 20 21H4C3.44772 21 3 20.5523 3 20V4ZM6 11C6 10.4477 6.44772 10 7 10H17C17.5523 10 18 10.4477 18 11C18 11.5523 17.5523 12 17 12H7C6.44772 12 6 11.5523 6 11ZM9 13C8.44772 13 8 13.4477 8 14C8 14.5523 8.44772 15 9 15H15C15.5523 15 16 14.5523 16 14C16 13.4477 15.5523 13 15 13H9Z"
                    fill={bg}
                />
            </svg>
        );
    },
    textPositionBelow: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3 4C3 3.44772 3.44772 3 4 3H20C20.5523 3 21 3.44772 21 4V14C21 14.5523 20.5523 15 20 15H4C3.44772 15 3 14.5523 3 14V4ZM3 17C3 16.4477 3.44772 16 4 16H14C14.5523 16 15 16.4477 15 17C15 17.5523 14.5523 18 14 18H4C3.44772 18 3 17.5523 3 17ZM4 19C3.44772 19 3 19.4477 3 20C3 20.5523 3.44772 21 4 21H10C10.5523 21 11 20.5523 11 20C11 19.4477 10.5523 19 10 19H4Z"
                    fill={bg}
                />
            </svg>
        );
    },
    cardFloat: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V12H3V5Z" fill={bg} />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M21 12H3V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V12ZM6 14C5.44772 14 5 14.4477 5 15C5 15.5523 5.44772 16 6 16H12C12.5523 16 13 15.5523 13 15C13 14.4477 12.5523 14 12 14H6Z"
                    fill={bg}
                    fillOpacity="0.5"
                />
                <path
                    d="M5 15C5 14.4477 5.44772 14 6 14H12C12.5523 14 13 14.4477 13 15C13 15.5523 12.5523 16 12 16H6C5.44772 16 5 15.5523 5 15Z"
                    fill={bg}
                />
                <mask
                    id="maskCardFloat"
                    mask-type="alpha"
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="0"
                    width="24"
                    height="24">
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M24 0H0V24H24V0ZM5 3C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3H5Z"
                        fill="#C4C4C4"
                    />
                </mask>
                <g mask="url(#maskCardFloat)">
                    <g filter="url(#filter_cardFloat)">
                        <rect x="5" y="5" width="18" height="18" rx="2" fill={bg} fillOpacity="0.55" />
                    </g>
                </g>
                <defs>
                    <filter
                        id="filter_cardFloat"
                        x="3"
                        y="3"
                        width="22"
                        height="22"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB">
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                        <feGaussianBlur stdDeviation="1" result="effect1_foregroundBlur" />
                    </filter>
                </defs>
            </svg>
        );
    },
    cardOutline: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2.5" y="2.5" width="19" height="19" rx="2.5" stroke={bg} strokeLinecap="square" />
                <path
                    d="M3 5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V12H3V5Z"
                    fill={bg}
                    fillOpacity={0.5}
                />
                <rect x="5" y="14" width="7" height="2" rx="1" fill={bg} />
            </svg>
        );
    },
    cardNone: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M3 5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V12H3V5Z"
                    fill={bg}
                    fillOpacity={0.5}
                />
                <rect x="5" y="14" width="7" height="2" rx="1" fill={bg} />
            </svg>
        );
    },
    fullSizeCard: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V16H3V5Z" fill={bg} />
                <path
                    d="M3 16H21V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V16Z"
                    fill={bg}
                    fillOpacity={0.5}
                />
            </svg>
        );
    },
    halfSizeCard: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 6C3 5.44772 3.44772 5 4 5H10C10.5523 5 11 5.44772 11 6V15H3V6Z" fill={bg} />
                <path d="M13 6C13 5.44772 13.4477 5 14 5H20C20.5523 5 21 5.44772 21 6V15H13V6Z" fill={bg} />
                <path
                    d="M3 15H11V18C11 18.5523 10.5523 19 10 19H4C3.44772 19 3 18.5523 3 18V15Z"
                    fill={bg}
                    fillOpacity={0.5}
                />
                <path
                    d="M13 15H21V18C21 18.5523 20.5523 19 20 19H14C13.4477 19 13 18.5523 13 18V15Z"
                    fill={bg}
                    fillOpacity={0.5}
                />
            </svg>
        );
    },
    textSmall: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M14.8935 18H17L13.0989 7H10.8935L7 18H8.98479L9.92776 15.1566H13.9658L14.8935 18ZM11.8897 9.06584H12.019L13.5095 13.6396H10.384L11.8897 9.06584Z"
                    fill={bg}
                />
            </svg>
        );
    },
    textMedium: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M8 14C8 13.4477 8.44772 13 9 13H15C15.5523 13 16 13.4477 16 14V14C16 14.5523 15.5523 15 15 15H9C8.44772 15 8 14.5523 8 14V14Z"
                    fill={bg}
                />
                <path
                    d="M16.051 19H19L13.5384 4H10.451L5 19H7.77871L9.09886 15.1227H14.7521L16.051 19ZM11.8456 6.81705H12.0266L14.1133 13.0541H9.73764L11.8456 6.81705Z"
                    fill={bg}
                />
            </svg>
        );
    },
    textLarge: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M16.6297 21H20L13.7582 3H10.2297L4 21H7.17567L8.68441 16.3472H15.1452L16.6297 21ZM11.8236 6.38046H12.0304L14.4152 13.8649H9.41445L11.8236 6.38046Z"
                    fill={bg}
                />
            </svg>
        );
    },
    compactList: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="2" height="2" rx="1" fill={bg} />
                <path
                    d="M4 2C4 1.44772 4.44772 1 5 1H18C18.5523 1 19 1.44772 19 2C19 2.55228 18.5523 3 18 3H5C4.44772 3 4 2.55228 4 2Z"
                    fill={bg}
                    fillOpacity={0.5}
                />
                <rect x="1" y="5" width="2" height="2" rx="1" fill={bg} />
                <path
                    d="M4 6C4 5.44772 4.44772 5 5 5H18C18.5523 5 19 5.44772 19 6C19 6.55228 18.5523 7 18 7H5C4.44772 7 4 6.55228 4 6Z"
                    fill={bg}
                    fillOpacity={0.5}
                />
                <rect x="1" y="9" width="2" height="2" rx="1" fill={bg} />
                <path
                    d="M4 10C4 9.44772 4.44772 9 5 9H18C18.5523 9 19 9.44772 19 10C19 10.5523 18.5523 11 18 11H5C4.44772 11 4 10.5523 4 10Z"
                    fill={bg}
                    fillOpacity={0.5}
                />
                <rect x="1" y="13" width="2" height="2" rx="1" fill={bg} />
                <path
                    d="M4 14C4 13.4477 4.44772 13 5 13H18C18.5523 13 19 13.4477 19 14C19 14.5523 18.5523 15 18 15H5C4.44772 15 4 14.5523 4 14Z"
                    fill={bg}
                    fillOpacity={0.5}
                />
                <rect x="1" y="17" width="2" height="2" rx="1" fill={bg} />
                <path
                    d="M4 18C4 17.4477 4.44772 17 5 17H18C18.5523 17 19 17.4477 19 18C19 18.5523 18.5523 19 18 19H5C4.44772 19 4 18.5523 4 18Z"
                    fill={bg}
                    fillOpacity={0.5}
                />
            </svg>
        );
    },
    listOrientationHorizontal: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M21 4L21 20L19 20C18.4477 20 18 19.5523 18 19L18 5C18 4.44772 18.4477 4 19 4L21 4ZM15 4C15.5523 4 16 4.44772 16 5L16 19C16 19.5523 15.5523 20 15 20L9 20C8.44772 20 8 19.5523 8 19L8 5C8 4.44771 8.44772 4 9 4L15 4ZM6 5C6 4.44772 5.55229 4 5 4L3 4L3 20L5 20C5.55228 20 6 19.5523 6 19L6 5Z"
                    fill={bg}
                />
            </svg>
        );
    },
    listOrientationVertical: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2.83325 3.83325C2.83325 3.28097 3.28097 2.83325 3.83325 2.83325H20.1666C20.7189 2.83325 21.1666 3.28097 21.1666 3.83325V6.83325C21.1666 7.38554 20.7189 7.83325 20.1666 7.83325H3.83325C3.28097 7.83325 2.83325 7.38554 2.83325 6.83325V3.83325ZM2.83325 10.4999C2.83325 9.94764 3.28097 9.49992 3.83325 9.49992H20.1666C20.7189 9.49992 21.1666 9.94764 21.1666 10.4999V13.4999C21.1666 14.0522 20.7189 14.4999 20.1666 14.4999H3.83325C3.28097 14.4999 2.83325 14.0522 2.83325 13.4999V10.4999ZM3.83325 16.1666C3.28097 16.1666 2.83325 16.6143 2.83325 17.1666V20.1666C2.83325 20.7189 3.28097 21.1666 3.83325 21.1666H20.1666C20.7189 21.1666 21.1666 20.7189 21.1666 20.1666V17.1666C21.1666 16.6143 20.7189 16.1666 20.1666 16.1666H3.83325Z"
                    fill={bg}
                />
            </svg>
        );
    },
    itemSizeCompact: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M3.78442 2.83325C3.23214 2.83325 2.78442 3.28097 2.78442 3.83325V5.58173C2.78442 6.13402 3.23214 6.58173 3.78442 6.58173H20.1666C20.7189 6.58173 21.1666 6.13402 21.1666 5.58173V3.83325C21.1666 3.28097 20.7189 2.83325 20.1666 2.83325H3.78442Z"
                    fill={bg}
                />
                <path
                    d="M3.78442 7.69485C3.23214 7.69485 2.78442 8.14257 2.78442 8.69485V10.4433C2.78442 10.9956 3.23214 11.4433 3.78442 11.4433H20.1666C20.7189 11.4433 21.1666 10.9956 21.1666 10.4433V8.69485C21.1666 8.14257 20.7189 7.69485 20.1666 7.69485H3.78442Z"
                    fill={bg}
                />
                <path
                    d="M2.78442 13.5565C2.78442 13.0042 3.23214 12.5565 3.78442 12.5565H20.1666C20.7189 12.5565 21.1666 13.0042 21.1666 13.5565V15.305C21.1666 15.8573 20.7189 16.305 20.1666 16.305H3.78442C3.23214 16.305 2.78442 15.8573 2.78442 15.305V13.5565Z"
                    fill={bg}
                />
                <path
                    d="M3.78442 17.4181C3.23214 17.4181 2.78442 17.8658 2.78442 18.4181V20.1666C2.78442 20.7189 3.23214 21.1666 3.78442 21.1666H20.1666C20.7189 21.1666 21.1666 20.7189 21.1666 20.1666V18.4181C21.1666 17.8658 20.7189 17.4181 20.1666 17.4181H3.78442Z"
                    fill={bg}
                />
            </svg>
        );
    },
    circleAddButton: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="8" fill={bg} />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12 7C11.4477 7 11 7.44772 11 8V11L8 11C7.44772 11 7 11.4477 7 12C7 12.5523 7.44772 13 8 13H11V16C11 16.5523 11.4477 17 12 17C12.5523 17 13 16.5523 13 16V13H16C16.5523 13 17 12.5523 17 12C17 11.4477 16.5523 11 16 11L13 11V8C13 7.44772 12.5523 7 12 7Z"
                    fill="white"
                />
            </svg>
        );
    },
    smallCheckedBox: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M12.4444 0H1.55556C0.7 0 0 0.7 0 1.55556V12.4444C0 13.3 0.7 14 1.55556 14H12.4444C13.3 14 14 13.3 14 12.4444V1.55556C14 0.7 13.3 0 12.4444 0ZM5.99667 10.3367C5.69333 10.64 5.20333 10.64 4.9 10.3367L2.10778 7.54444C1.80444 7.24111 1.80444 6.75111 2.10778 6.44778C2.41111 6.14444 2.90111 6.14444 3.20444 6.44778L5.44444 8.68778L10.7956 3.33667C11.0989 3.03333 11.5889 3.03333 11.8922 3.33667C12.1956 3.64 12.1956 4.13 11.8922 4.43333L5.99667 10.3367Z"
                    fill={bg}
                />
            </svg>
        );
    },
    smallUncheckedBox: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M2.33333 12.9444H11.6667C12.3706 12.9444 12.9444 12.3706 12.9444 11.6667V2.33333C12.9444 1.62941 12.3706 1.05556 11.6667 1.05556H2.33333C1.62941 1.05556 1.05556 1.62941 1.05556 2.33333V11.6667C1.05556 12.3706 1.62941 12.9444 2.33333 12.9444ZM1.55556 0.5H12.4444C13.0239 0.5 13.5 0.976142 13.5 1.55556V12.4444C13.5 13.0239 13.0239 13.5 12.4444 13.5H1.55556C0.976142 13.5 0.5 13.0239 0.5 12.4444V1.55556C0.5 0.976142 0.976142 0.5 1.55556 0.5Z"
                    stroke={bg}
                />
            </svg>
        );
    },
    headerRowID: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    d="M16.9 2.79999H15.1C15.0202 2.79999 14.944 2.83179 14.8876 2.88819L9.16956 8.60679C6.90876 7.57419 4.23936 8.57019 3.20676 10.831C2.17416 13.0918 3.17016 15.7612 5.43096 16.7938C7.69176 17.8264 10.3612 16.8304 11.3938 14.5696C11.9362 13.3822 11.9362 12.0184 11.3938 10.831L12.781 9.44319L13.9408 9.82959C14.2558 9.93279 14.5948 9.76119 14.698 9.44619C14.7274 9.35679 14.7352 9.26259 14.7208 9.16959L14.5042 7.71999L14.9398 7.28439L15.8134 7.68159C16.1146 7.81899 16.471 7.68639 16.6084 7.38459C16.6672 7.25559 16.6786 7.10979 16.6402 6.97359L16.3366 5.88819L17.1124 5.11239C17.1688 5.05599 17.2 4.97979 17.2 4.89999V3.09999C17.2 2.93439 17.0656 2.79999 16.9 2.79999ZM6.99996 13.9C6.99996 14.3968 6.59676 14.8 6.09996 14.8C5.60316 14.8 5.19996 14.3968 5.19996 13.9C5.19996 13.4032 5.60316 13 6.09996 13C6.59676 13 6.99996 13.4032 6.99996 13.9Z"
                    fill={bg}
                />
            </svg>
        );
    },
    headerCode: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    opacity="0.65"
                    d="M7.71695 6.58528L4.35646 9.82639C4.25708 9.92224 4.25708 10.0776 4.35646 10.1735L7.71695 13.4147"
                    stroke={bg}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                />
                <path
                    d="M12.283 6.58528L15.6435 9.82639C15.7429 9.92224 15.7429 10.0776 15.6435 10.1735L12.283 13.4147"
                    stroke={bg}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                />
            </svg>
        );
    },
    headerJSON: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    opacity="0.65"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.41861 5.44579C5.82794 4.85925 6.45897 4.75 7 4.75C7.41421 4.75 7.75 5.08579 7.75 5.5C7.75 5.91421 7.41421 6.25 7 6.25C6.81287 6.25 6.7194 6.27044 6.67667 6.28612C6.65525 6.29398 6.65353 6.29669 6.65032 6.30175C6.64984 6.30251 6.64932 6.30332 6.6487 6.30421C6.6336 6.32585 6.59267 6.3992 6.55897 6.58387C6.52548 6.7674 6.50587 7.02194 6.50587 7.375V7.39281C6.50587 7.84724 6.50587 8.26406 6.48495 8.62014C6.4641 8.97503 6.41999 9.33451 6.3018 9.65216C6.24952 9.79266 6.18164 9.92768 6.09477 10.0521C6.18842 10.1998 6.25799 10.3563 6.31006 10.5118C6.50665 11.0988 6.50625 11.8609 6.5059 12.5393C6.50588 12.568 6.50587 12.5966 6.50587 12.625C6.50587 12.9795 6.52467 13.235 6.55747 13.4195C6.59052 13.6055 6.63085 13.6781 6.64499 13.6986C6.64554 13.6994 6.64591 13.7001 6.64626 13.7007C6.64798 13.704 6.6491 13.7061 6.66933 13.7135C6.71256 13.7293 6.80788 13.75 7 13.75C7.41421 13.75 7.75 14.0858 7.75 14.5C7.75 14.9142 7.41421 15.25 7 15.25C6.4555 15.25 5.81969 15.1427 5.41105 14.5514C5.07014 14.0582 5.00587 13.3684 5.00587 12.625C5.00587 11.8243 4.99676 11.3138 4.88771 10.9882C4.84246 10.8531 4.79926 10.8162 4.78388 10.805C4.769 10.7942 4.70197 10.75 4.5 10.75C4.08579 10.75 3.75 10.4142 3.75 10C3.75 9.58579 4.08579 9.25 4.5 9.25C4.75182 9.25 4.84008 9.21013 4.85758 9.20026C4.86152 9.19804 4.8618 9.19766 4.86243 9.19679C4.86255 9.19662 4.86268 9.19644 4.86285 9.19623C4.86561 9.19276 4.87884 9.17508 4.89597 9.12905C4.93476 9.02481 4.96914 8.84522 4.98753 8.53216C5.00559 8.22491 5.00587 7.85111 5.00587 7.375C5.00587 6.62693 5.07489 5.93832 5.41861 5.44579Z"
                    fill={bg}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12.25 5.5C12.25 5.08579 12.5858 4.75 13 4.75C13.5445 4.75 14.1803 4.85735 14.5889 5.44856C14.9299 5.94179 14.9941 6.63163 14.9941 7.375C14.9941 8.17565 15.0032 8.68619 15.1123 9.01183C15.1575 9.14693 15.2007 9.18376 15.2161 9.19499C15.231 9.20585 15.298 9.25 15.5 9.25C15.9142 9.25 16.25 9.58579 16.25 10C16.25 10.4142 15.9142 10.75 15.5 10.75C15.2482 10.75 15.1599 10.7899 15.1424 10.7997C15.1385 10.802 15.1382 10.8023 15.1376 10.8032C15.1375 10.8034 15.1373 10.8036 15.1372 10.8038C15.1344 10.8072 15.1212 10.8249 15.104 10.871C15.0652 10.9752 15.0309 11.1548 15.0125 11.4678C14.9944 11.7751 14.9941 12.1489 14.9941 12.625C14.9941 13.3731 14.9251 14.0617 14.5814 14.5542C14.1721 15.1408 13.541 15.25 13 15.25C12.5858 15.25 12.25 14.9142 12.25 14.5C12.25 14.0858 12.5858 13.75 13 13.75C13.1871 13.75 13.2806 13.7296 13.3233 13.7139C13.3447 13.706 13.3465 13.7033 13.3497 13.6983C13.3502 13.6975 13.3507 13.6967 13.3513 13.6958C13.3664 13.6742 13.4073 13.6008 13.441 13.4161C13.4745 13.2326 13.4941 12.9781 13.4941 12.625L13.4941 12.6072C13.4941 12.1528 13.4941 11.7359 13.515 11.3799C13.5359 11.025 13.58 10.6655 13.6982 10.3478C13.7505 10.2073 13.8184 10.0723 13.9052 9.94788C13.8116 9.80015 13.742 9.64367 13.6899 9.48817C13.4933 8.90115 13.4937 8.13915 13.4941 7.46066C13.4941 7.43195 13.4941 7.40339 13.4941 7.375C13.4941 7.02053 13.4753 6.76502 13.4425 6.58046C13.4095 6.3945 13.3691 6.32189 13.355 6.30144C13.3545 6.30064 13.3541 6.29994 13.3537 6.29928C13.352 6.29601 13.3509 6.29388 13.3307 6.28649C13.2874 6.27069 13.1921 6.25 13 6.25C12.5858 6.25 12.25 5.91421 12.25 5.5Z"
                    fill={bg}
                />
            </svg>
        );
    },
    headerAI: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7.7332 3.13073C8.51175 1.02673 11.4876 1.02674 12.2662 3.13073L13.3631 6.09517C13.4559 6.34608 13.6538 6.54391 13.9047 6.63675L16.8691 7.73369C18.9731 8.51224 18.9731 11.4881 16.8691 12.2666L13.9047 13.3636C13.6538 13.4564 13.4559 13.6543 13.3631 13.9052L12.2662 16.8696C11.4876 18.9736 8.51175 18.9736 7.7332 16.8696L6.63626 13.9052C6.54342 13.6543 6.34559 13.4564 6.09468 13.3636L3.13024 12.2666C1.02624 11.4881 1.02625 8.51224 3.13024 7.73369L6.09468 6.63675C6.34559 6.54391 6.54342 6.34608 6.63626 6.09517L7.7332 3.13073ZM10.8594 3.65129C10.5641 2.85322 9.43529 2.85322 9.13998 3.65128L8.04304 6.61572C7.79827 7.27721 7.27672 7.79875 6.61524 8.04353L3.6508 9.14047C2.85273 9.43578 2.85273 10.5646 3.65079 10.8599L6.61524 11.9568C7.27672 12.2016 7.79827 12.7231 8.04304 13.3846L9.13998 16.349C9.43529 17.1471 10.5641 17.1471 10.8594 16.349L11.9563 13.3846C12.2011 12.7231 12.7226 12.2016 13.3841 11.9568L16.3486 10.8599C17.1466 10.5646 17.1466 9.43578 16.3486 9.14047L13.3841 8.04353C12.7226 7.79875 12.2011 7.27721 11.9563 6.61572L10.8594 3.65129Z"
                    fill={bg}
                />
            </svg>
        );
    },
    headerWorkflow: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                className={className}
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M7.5 2.5H4.16667C3.24619 2.5 2.5 3.24619 2.5 4.16667V7.5C2.5 8.42048 3.24619 9.16667 4.16667 9.16667H7.5C8.42048 9.16667 9.16667 8.42048 9.16667 7.5V4.16667C9.16667 3.24619 8.42048 2.5 7.5 2.5Z"
                    stroke={bg}
                    strokeWidth="1.5"
                    vectorEffect="non-scaling-stroke"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M5.83325 9.16675V12.5001C5.83325 12.9421 6.00885 13.366 6.32141 13.6786C6.63397 13.9912 7.05789 14.1667 7.49992 14.1667H10.8333"
                    stroke={bg}
                    strokeWidth="1.5"
                    vectorEffect="non-scaling-stroke"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M15.8333 10.8333H12.4999C11.5794 10.8333 10.8333 11.5794 10.8333 12.4999V15.8333C10.8333 16.7537 11.5794 17.4999 12.4999 17.4999H15.8333C16.7537 17.4999 17.4999 16.7537 17.4999 15.8333V12.4999C17.4999 11.5794 16.7537 10.8333 15.8333 10.8333Z"
                    stroke={bg}
                    strokeWidth="1.5"
                    vectorEffect="non-scaling-stroke"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        );
    },
    headerIntegration: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12.1482 6.13277L15.5267 9.4661C15.6696 9.60703 15.75 9.79933 15.75 9.99999C15.75 10.2007 15.6696 10.393 15.5267 10.5339L12.1482 13.8672L11.0947 12.7994L13.1719 10.75H8.74999V9.24999H13.1719L11.0947 7.20055L12.1482 6.13277Z"
                    fill={bg}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M11.6667 4.08333C9.79852 4.08333 8.13291 4.94826 7.04737 6.30243C6.8388 6.56261 6.65183 6.84068 6.48912 7.13389L5.17752 6.40609C5.38155 6.03839 5.61585 5.68999 5.877 5.36423C7.23498 3.67019 9.32415 2.58333 11.6667 2.58333C15.7628 2.58333 19.0833 5.90388 19.0833 9.99999C19.0833 14.0961 15.7628 17.4167 11.6667 17.4167C9.32415 17.4167 7.23498 16.3298 5.877 14.6358C5.61585 14.31 5.38155 13.9616 5.17752 13.5939L6.48912 12.8661C6.65183 13.1593 6.8388 13.4374 7.04737 13.6976C8.13291 15.0517 9.79852 15.9167 11.6667 15.9167C14.9343 15.9167 17.5833 13.2677 17.5833 9.99999C17.5833 6.73231 14.9343 4.08333 11.6667 4.08333Z"
                    fill={bg}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4.99999 7.41666C3.57325 7.41666 2.41666 8.57326 2.41666 9.99999C2.41666 11.4267 3.57325 12.5833 4.99999 12.5833C6.42673 12.5833 7.58332 11.4267 7.58332 9.99999C7.58332 8.57326 6.42673 7.41666 4.99999 7.41666ZM0.916656 9.99999C0.916656 7.74483 2.74483 5.91666 4.99999 5.91666C7.25515 5.91666 9.08332 7.74483 9.08332 9.99999C9.08332 12.2552 7.25515 14.0833 4.99999 14.0833C2.74483 14.0833 0.916656 12.2552 0.916656 9.99999Z"
                    fill={bg}
                />
            </svg>
        );
    },
    headerGlideAIGenerateText: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    d="M5.83364 10H14.167M5.83364 6.66667H14.167M5.83364 2.5H14.167C16.0079 2.5 17.5003 3.99238 17.5003 5.83333V10.8333C17.5003 12.6743 16.0079 14.1667 14.167 14.1667H13.1108C12.7166 14.1667 12.3352 14.3064 12.0342 14.561L8.45526 17.5894C7.91366 18.0477 7.08364 17.6627 7.08364 16.9532V15C7.08364 14.5398 6.71054 14.1667 6.25031 14.1667H5.83364C3.99269 14.1667 2.50031 12.6743 2.50031 10.8333V5.83333C2.50031 3.99238 3.99269 2.5 5.83364 2.5Z"
                    stroke={bg}
                    strokeWidth="1.5"
                />
            </svg>
        );
    },
    headerGlideAIChoice: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    d="M6.66699 7.49999L10.0003 4.16666L13.3337 7.49999M13.3337 12.5L10.0003 15.8333L6.66699 12.5"
                    stroke={bg}
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                />
            </svg>
        );
    },
    headerGlideAITextToDate: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    d="M2.5 5.83334C2.5 4.45263 3.61929 3.33334 5 3.33334H15C16.3807 3.33334 17.5 4.45263 17.5 5.83334V15C17.5 16.3807 16.3807 17.5 15 17.5H5C3.61929 17.5 2.5 16.3807 2.5 15V5.83334Z"
                    stroke={bg}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                />
                <path d="M13.3333 2.5V4.16667" stroke={bg} strokeWidth="1.5" strokeLinecap="round" />
                <path d="M2.5 7.5H17.5" stroke={bg} strokeWidth="1.5" />
                <path d="M6.66669 2.5V4.16667" stroke={bg} strokeWidth="1.5" strokeLinecap="round" />
            </svg>
        );
    },
    headerGlideAIBoolean: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <g clipPath="url(#clip0_6256_301)">
                    <rect x="2.50031" y="2.5" width="15" height="15" rx="4" stroke={bg} strokeWidth="1.5" />
                    <path
                        d="M6.25031 10.0888L8.6201 12.2083L13.8336 6.79166"
                        stroke={bg}
                        strokeWidth="1.5"
                        strokeLinecap="square"
                    />
                </g>
                <defs>
                    <clipPath id="clip0_6256_301">
                        <rect width="20" height="20" fill="white" transform="translate(0.000305176)" />
                    </clipPath>
                </defs>
            </svg>
        );
    },

    headerGlideAINumber: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path d="M3.25406 7H16.7354" stroke={bg} strokeWidth="1.5" />
                <path d="M2.9953 13H16.4766" stroke={bg} strokeWidth="1.5" />
                <path d="M5.62137 16.6737L8.37831 2.94785" stroke={bg} strokeWidth="1.5" />
                <path d="M11.6211 16.6737L14.378 2.94785" stroke={bg} strokeWidth="1.5" />
            </svg>
        );
    },

    headerGlideAIAudio: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    d="M7.29199 4.37499V9.79166C7.29199 11.2874 8.50455 12.5 10.0003 12.5C11.4961 12.5 12.7087 11.2874 12.7087 9.79166V4.37499C12.7087 2.87922 11.4961 1.66666 10.0003 1.66666C8.50455 1.66666 7.29199 2.87922 7.29199 4.37499Z"
                    stroke={bg}
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                />
                <path
                    d="M4.16699 10.7919L4.58191 11.6347C5.5962 13.6951 7.69312 15 9.98958 15V15C12.2445 15 14.3108 13.7414 15.3453 11.7378L15.8337 10.7919"
                    stroke={bg}
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                />
                <path d="M10.0003 15V17.9167" stroke={bg} strokeWidth="1.5" strokeLinejoin="round" />
            </svg>
        );
    },

    headerGlideAIImage: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <g clipPath="url(#clip0_6256_297)">
                    <path
                        d="M1.66699 5.83333C1.66699 3.99238 3.15938 2.5 5.00033 2.5H15.0003C16.8413 2.5 18.3337 3.99238 18.3337 5.83333V14.1667C18.3337 16.0076 16.8413 17.5 15.0003 17.5H5.00033C3.15938 17.5 1.66699 16.0076 1.66699 14.1667V5.83333Z"
                        stroke={bg}
                        strokeWidth="1.5"
                    />
                    <path
                        d="M1.66699 13.75L3.58611 11.8309C4.36716 11.0498 5.63349 11.0498 6.41454 11.8309L7.273 12.6893C7.85879 13.2751 8.80853 13.2751 9.39432 12.6893L12.3992 9.68442C13.3755 8.70811 14.9584 8.70811 15.9348 9.68442L18.3639 12.1136"
                        stroke={bg}
                        strokeWidth="1.5"
                    />
                    <ellipse
                        cx="7.50029"
                        cy="6.66667"
                        rx="1.66667"
                        ry="1.66667"
                        stroke={bg}
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                    />
                </g>
                <defs>
                    <clipPath id="clip0_6256_297">
                        <rect width="20" height="20" fill="white" transform="translate(0.000305176)" />
                    </clipPath>
                </defs>
            </svg>
        );
    },

    headerNumber: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    d="M3.78711 14.4942C4.26514 14.4942 4.5874 14.1719 4.5874 13.6832V7.42047C4.5874 6.86188 4.24365 6.51276 3.66895 6.51276C3.33594 6.51276 3.07812 6.5611 2.70215 6.81891L1.16602 7.88239C0.913574 8.05963 0.816895 8.24225 0.816895 8.48932C0.816895 8.83307 1.05859 9.0694 1.38623 9.0694C1.55273 9.0694 1.66553 9.0318 1.81592 8.92438L2.94922 8.13483H2.98145V13.6832C2.98145 14.1719 3.30908 14.4942 3.78711 14.4942Z"
                    fill={bg}
                />
                <path
                    opacity="0.75"
                    d="M7.08496 14.3814H11.3765C11.7954 14.3814 12.0479 14.1182 12.0479 13.7369C12.0479 13.3448 11.7954 13.0923 11.3765 13.0923H8.45459V13.0601L10.1304 11.4488C11.3979 10.251 11.8599 9.66022 11.8599 8.7149C11.8599 7.38287 10.7534 6.45905 9.10986 6.45905C7.64355 6.45905 6.67139 7.25934 6.4082 8.11871C6.37061 8.23151 6.34912 8.3443 6.34912 8.46783C6.34912 8.87067 6.60693 9.12848 7.04199 9.12848C7.39111 9.12848 7.5791 8.97809 7.76172 8.65582C8.04102 8.03278 8.4707 7.72125 9.09375 7.72125C9.78662 7.72125 10.2808 8.1778 10.2808 8.81696C10.2808 9.37555 10.0337 9.75153 9.07764 10.67L6.76807 12.8775C6.44043 13.1783 6.32227 13.3824 6.32227 13.6885C6.32227 14.0967 6.57471 14.3814 7.08496 14.3814Z"
                    fill={bg}
                />
                <path
                    opacity="0.6"
                    d="M15.8506 14.5533C17.6499 14.5533 18.8691 13.6187 18.8691 12.2115C18.8691 11.1426 18.1226 10.4659 16.9678 10.3585V10.3262C17.8809 10.1382 18.5845 9.49371 18.5845 8.5108C18.5845 7.27008 17.478 6.45905 15.8398 6.45905C14.4702 6.45905 13.5894 7.09283 13.2456 7.85016C13.165 8.03278 13.1274 8.18854 13.1274 8.37115C13.1274 8.76324 13.3584 9.02643 13.8096 9.02643C14.1748 9.02643 14.3628 8.89215 14.54 8.52692C14.7925 7.97906 15.1899 7.70514 15.8452 7.70514C16.6294 7.70514 17.0483 8.11871 17.0483 8.74176C17.0483 9.37555 16.522 9.81598 15.7109 9.81598H15.394C15.002 9.81598 14.7764 10.0523 14.7764 10.3907C14.7764 10.7398 15.002 10.9708 15.394 10.9708H15.7324C16.6724 10.9708 17.2256 11.3897 17.2202 12.1417C17.2202 12.7862 16.6724 13.2481 15.8774 13.2481C15.0718 13.2481 14.6528 12.9258 14.3789 12.3834C14.2178 12.088 14.0137 11.959 13.6914 11.959C13.2456 11.959 12.9771 12.2222 12.9771 12.6412C12.9771 12.8023 13.0146 12.9742 13.1006 13.1514C13.4658 13.9141 14.3735 14.5533 15.8506 14.5533Z"
                    fill={bg}
                />
            </svg>
        );
    },
    headerDuration: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                className={className}>
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.4 6.40002C9.06863 6.40002 8.8 6.66865 8.8 7.00002V10.5964C8.79999 10.5976 8.79999 10.5988 8.79999 10.6C8.79999 10.6841 8.81731 10.7642 8.84858 10.8369C8.94045 11.0505 9.15276 11.2 9.4 11.2C9.40182 11.2 9.40364 11.2 9.40546 11.2H13C13.3314 11.2 13.6 10.9314 13.6 10.6C13.6 10.2686 13.3314 10 13 10L10 10V7.00002C10 6.66865 9.73137 6.40002 9.4 6.40002Z"
                    fill={bg}
                />
                <path
                    opacity="0.5"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M17.25 10C17.25 14.0041 14.0041 17.25 10 17.25C5.99594 17.25 2.75 14.0041 2.75 10C2.75 5.99594 5.99594 2.75 10 2.75C14.0041 2.75 17.25 5.99594 17.25 10ZM10 16C13.3137 16 16 13.3137 16 10C16 6.68629 13.3137 4 10 4C6.68629 4 4 6.68629 4 10C4 13.3137 6.68629 16 10 16Z"
                    fill={bg}
                />
                <path
                    d="M7 1.625C7 1.27982 7.27982 1 7.625 1L12.375 1C12.7202 1 13 1.27982 13 1.625V1.625C13 1.97018 12.7202 2.25 12.375 2.25L7.625 2.25C7.27982 2.25 7 1.97018 7 1.625V1.625Z"
                    fill={bg}
                />
            </svg>
        );
    },
    headerString: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    d="M7.81813 12.88H12.1818L12.9672 14.8H14.8L10.6545 5.20001H9.34541L5.19995 14.8H7.03268L7.81813 12.88ZM9.99995 6.92801L11.632 11.3091H8.36795L9.99995 6.92801Z"
                    fill={bg}
                />
            </svg>
        );
    },
    headerBoolean: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7.2 6C4.8804 6 3 7.79086 3 10C3 12.2091 4.8804 14 7.2 14H12.8C15.1196 14 17 12.2091 17 10C17 7.79086 15.1196 6 12.8 6H7.2ZM13 13C14.6569 13 16 11.6569 16 10C16 8.34315 14.6569 7 13 7C11.3431 7 10 8.34315 10 10C10 11.6569 11.3431 13 13 13Z"
                    fill={bg}
                />
            </svg>
        );
    },
    headerAudioUri: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10.3471 3.9361C11.9338 2.4023 14.4559 2.42424 16.0156 3.9854C17.5754 5.54656 17.5973 8.07086 16.0649 9.65892L16.0545 9.66948L14.1392 11.5865C13.3097 12.417 12.1608 12.846 10.9905 12.7621C9.82028 12.6782 8.74412 12.0897 8.04136 11.1494C7.75975 10.7726 7.83666 10.2386 8.21314 9.95673C8.58963 9.67487 9.12313 9.75184 9.40474 10.1287C9.8116 10.6731 10.4346 11.0138 11.1122 11.0623C11.7897 11.1109 12.4549 10.8625 12.9351 10.3817L12.9352 10.3816L14.845 8.47014C15.7274 7.55045 15.7131 6.09261 14.8117 5.19039C13.91 4.2879 12.4529 4.2739 11.5341 5.15789L10.4406 6.24598C10.1072 6.57775 9.56815 6.57618 9.23667 6.24246C8.90519 5.90875 8.90676 5.36926 9.24018 5.03748L10.3384 3.94473L10.3471 3.9361Z"
                    fill={bg}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.86082 8.41344C6.69028 7.58301 7.83919 7.15407 9.00939 7.23795C10.1796 7.32184 11.2558 7.91032 11.9586 8.85065C12.2402 9.22747 12.1633 9.76144 11.7868 10.0433C11.4103 10.3252 10.8768 10.2482 10.5952 9.87137C10.1883 9.32697 9.56528 8.98627 8.88777 8.93771C8.21025 8.88914 7.54506 9.1375 7.06485 9.61831L5.15495 11.5299C4.27254 12.4496 4.28679 13.9074 5.18821 14.8097C6.08962 15.7119 7.54616 15.7261 8.46504 14.8429L9.5512 13.7558C9.88365 13.4231 10.4227 13.4231 10.7551 13.7558C11.0876 14.0886 11.0876 14.6281 10.7551 14.9608L9.66332 16.0536L9.65277 16.0639C8.06612 17.5977 5.54407 17.5758 3.98429 16.0146C2.42451 14.4535 2.4026 11.9292 3.93503 10.3411L3.9454 10.3306L5.86082 8.41344Z"
                    fill={bg}
                    fillOpacity="0.65"
                />
            </svg>
        );
    },
    headerVideoUri: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10.3471 3.9361C11.9338 2.4023 14.4559 2.42424 16.0156 3.9854C17.5754 5.54656 17.5973 8.07086 16.0649 9.65892L16.0545 9.66948L14.1392 11.5865C13.3097 12.417 12.1608 12.846 10.9905 12.7621C9.82028 12.6782 8.74412 12.0897 8.04136 11.1494C7.75975 10.7726 7.83666 10.2386 8.21314 9.95673C8.58963 9.67487 9.12313 9.75184 9.40474 10.1287C9.8116 10.6731 10.4346 11.0138 11.1122 11.0623C11.7897 11.1109 12.4549 10.8625 12.9351 10.3817L12.9352 10.3816L14.845 8.47014C15.7274 7.55045 15.7131 6.09261 14.8117 5.19039C13.91 4.2879 12.4529 4.2739 11.5341 5.15789L10.4406 6.24598C10.1072 6.57775 9.56815 6.57618 9.23667 6.24246C8.90519 5.90875 8.90676 5.36926 9.24018 5.03748L10.3384 3.94473L10.3471 3.9361Z"
                    fill={bg}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.86082 8.41344C6.69028 7.58301 7.83919 7.15407 9.00939 7.23795C10.1796 7.32184 11.2558 7.91032 11.9586 8.85065C12.2402 9.22747 12.1633 9.76144 11.7868 10.0433C11.4103 10.3252 10.8768 10.2482 10.5952 9.87137C10.1883 9.32697 9.56528 8.98627 8.88777 8.93771C8.21025 8.88914 7.54506 9.1375 7.06485 9.61831L5.15495 11.5299C4.27254 12.4496 4.28679 13.9074 5.18821 14.8097C6.08962 15.7119 7.54616 15.7261 8.46504 14.8429L9.5512 13.7558C9.88365 13.4231 10.4227 13.4231 10.7551 13.7558C11.0876 14.0886 11.0876 14.6281 10.7551 14.9608L9.66332 16.0536L9.65277 16.0639C8.06612 17.5977 5.54407 17.5758 3.98429 16.0146C2.42451 14.4535 2.4026 11.9292 3.93503 10.3411L3.9454 10.3306L5.86082 8.41344Z"
                    fill={bg}
                    fillOpacity="0.65"
                />
            </svg>
        );
    },
    headerEmoji: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    opacity="0.65"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10 3.625C6.47918 3.625 3.625 6.47918 3.625 10C3.625 13.5208 6.47918 16.375 10 16.375C13.5208 16.375 16.375 13.5208 16.375 10C16.375 6.47918 13.5208 3.625 10 3.625ZM2.375 10C2.375 5.78883 5.78883 2.375 10 2.375C14.2112 2.375 17.625 5.78883 17.625 10C17.625 14.2112 14.2112 17.625 10 17.625C5.78883 17.625 2.375 14.2112 2.375 10Z"
                    fill={bg}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M13.8657 11.2016C14.1648 11.3745 14.2671 11.757 14.0942 12.0561C13.6789 12.7744 13.082 13.3707 12.3633 13.7854C11.6447 14.2 10.8296 14.4182 9.99998 14.4183C9.17031 14.4183 8.35524 14.2 7.6366 13.7854C6.91796 13.3708 6.32103 12.7744 5.90575 12.0562C5.73285 11.7571 5.83511 11.3745 6.13416 11.2016C6.43321 11.0287 6.8158 11.131 6.9887 11.43C7.29414 11.9583 7.73317 12.3969 8.26173 12.7019C8.79028 13.0068 9.38976 13.1673 9.99997 13.1673C10.6102 13.1673 11.2097 13.0068 11.7382 12.7018C12.2668 12.3969 12.7058 11.9582 13.0112 11.43C13.1841 11.1309 13.5667 11.0287 13.8657 11.2016Z"
                    fill={bg}
                />
                <path
                    d="M7.53595 8.9C8.05934 8.9 8.48363 8.47571 8.48363 7.95232C8.48363 7.42893 8.05934 7.00464 7.53595 7.00464C7.01256 7.00464 6.58827 7.42893 6.58827 7.95232C6.58827 8.47571 7.01256 8.9 7.53595 8.9Z"
                    fill={bg}
                />
                <path
                    d="M12.4639 8.9C12.9873 8.9 13.4116 8.47571 13.4116 7.95232C13.4116 7.42893 12.9873 7.00464 12.4639 7.00464C11.9405 7.00464 11.5162 7.42893 11.5162 7.95232C11.5162 8.47571 11.9405 8.9 12.4639 8.9Z"
                    fill={bg}
                />
            </svg>
        );
    },
    headerImage: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    opacity="0.5"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M13.0823 10.8347C13.2798 10.5346 13.72 10.5346 13.9175 10.8347L17.3401 16.0342C17.5589 16.3666 17.3204 16.8091 16.9224 16.8091H10.0774C9.67935 16.8091 9.44089 16.3666 9.65973 16.0342L13.0823 10.8347Z"
                    fill={bg}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7.76567 8.59977C7.96425 8.30976 8.39218 8.30976 8.59077 8.59977L13.6763 16.0266C13.9035 16.3584 13.6659 16.8091 13.2638 16.8091H3.09265C2.69049 16.8091 2.45289 16.3584 2.68011 16.0266L7.76567 8.59977Z"
                    fill={bg}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M14.5 8C15.8807 8 17 6.88071 17 5.5C17 4.11929 15.8807 3 14.5 3C13.1193 3 12 4.11929 12 5.5C12 6.88071 13.1193 8 14.5 8Z"
                    fill={bg}
                />
            </svg>
        );
    },
    headerUri: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10.3471 3.9361C11.9338 2.4023 14.4559 2.42424 16.0156 3.9854C17.5754 5.54656 17.5973 8.07086 16.0649 9.65892L16.0545 9.66948L14.1392 11.5865C13.3097 12.417 12.1608 12.846 10.9905 12.7621C9.82028 12.6782 8.74412 12.0897 8.04136 11.1494C7.75975 10.7726 7.83666 10.2386 8.21314 9.95673C8.58963 9.67487 9.12313 9.75184 9.40474 10.1287C9.8116 10.6731 10.4346 11.0138 11.1122 11.0623C11.7897 11.1109 12.4549 10.8625 12.9351 10.3817L12.9352 10.3816L14.845 8.47014C15.7274 7.55045 15.7131 6.09261 14.8117 5.19039C13.91 4.2879 12.4529 4.2739 11.5341 5.15789L10.4406 6.24598C10.1072 6.57775 9.56815 6.57618 9.23667 6.24246C8.90519 5.90875 8.90676 5.36926 9.24018 5.03748L10.3384 3.94473L10.3471 3.9361Z"
                    fill={bg}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.86082 8.41344C6.69028 7.58301 7.83919 7.15407 9.00939 7.23795C10.1796 7.32184 11.2558 7.91032 11.9586 8.85065C12.2402 9.22747 12.1633 9.76144 11.7868 10.0433C11.4103 10.3252 10.8768 10.2482 10.5952 9.87137C10.1883 9.32697 9.56528 8.98627 8.88777 8.93771C8.21025 8.88914 7.54506 9.1375 7.06485 9.61831L5.15495 11.5299C4.27254 12.4496 4.28679 13.9074 5.18821 14.8097C6.08962 15.7119 7.54616 15.7261 8.46504 14.8429L9.5512 13.7558C9.88365 13.4231 10.4227 13.4231 10.7551 13.7558C11.0876 14.0886 11.0876 14.6281 10.7551 14.9608L9.66332 16.0536L9.65277 16.0639C8.06612 17.5977 5.54407 17.5758 3.98429 16.0146C2.42451 14.4535 2.4026 11.9292 3.93503 10.3411L3.9454 10.3306L5.86082 8.41344Z"
                    fill={bg}
                    fillOpacity="0.65"
                />
            </svg>
        );
    },
    headerPhone: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    d="M6.99979 10.0498C7.6323 11.3429 8.68048 12.3864 9.97647 13.013C10.0713 13.0579 10.1762 13.0774 10.2808 13.0695C10.3854 13.0615 10.4862 13.0265 10.5732 12.9678L12.4814 11.6954C12.5658 11.6391 12.6629 11.6048 12.7639 11.5955C13.8636 11.3969 16.3133 12.2905 16.9183 13.4273C16.9843 13.5414 17.0114 13.6738 16.9956 13.8046C16.8828 14.6875 16.452 15.4991 15.7839 16.0873C15.1158 16.6755 14.2562 17 13.366 17C10.6168 17 8.51669 15.7486 6.57268 13.8046C4.62867 11.8606 3 9.38321 3 6.63396C3.00005 5.74384 3.32454 4.88425 3.91273 4.21614C4.50092 3.54804 5.31246 3.11724 6.19541 3.0044C6.32621 2.98857 6.45862 3.01568 6.57268 3.08165C7.68721 3.69213 8.57105 6.04761 8.40608 7.23657C8.39732 7.33674 8.36391 7.43318 8.30885 7.51731L7.04081 9.45483C6.9831 9.54199 6.94898 9.64263 6.94179 9.74691C6.9346 9.8512 6.95458 9.95557 6.99979 10.0498Z"
                    fill={bg}
                />
            </svg>
        );
    },
    headerMarkdown: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    d="M2.29576 14.5648C2.81939 14.5648 3.12124 14.2445 3.12124 13.6962V8.44759H3.17053L5.36977 13.8317C5.52378 14.2137 5.78251 14.3923 6.17677 14.3923C6.56487 14.3923 6.81129 14.2198 6.97145 13.8256L9.1707 8.44759H9.21998V13.6962C9.21998 14.2445 9.52184 14.5648 10.0455 14.5648C10.5691 14.5648 10.8771 14.2445 10.8771 13.6962V6.63645C10.8771 5.87873 10.4336 5.43518 9.682 5.43518C9.02901 5.43518 8.66555 5.7124 8.39449 6.39619L6.19525 11.8604H6.14597L3.94057 6.39619C3.66951 5.70624 3.31221 5.43518 2.66538 5.43518C1.90766 5.43518 1.46411 5.88489 1.46411 6.63645V13.6962C1.46411 14.2445 1.77213 14.5648 2.29576 14.5648Z"
                    fill={bg}
                />
                <path
                    opacity="0.65"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M15.6199 5.11182C16.0341 5.11182 16.3699 5.4476 16.3699 5.86182L16.3699 11.7527L18.0056 10.117C18.2985 9.82406 18.7734 9.82406 19.0663 10.117C19.3592 10.4099 19.3592 10.8847 19.0663 11.1776L16.15 14.0939C15.8571 14.3868 15.3822 14.3868 15.0893 14.0939L12.173 11.1776C11.8801 10.8847 11.8801 10.4099 12.173 10.117C12.4659 9.82406 12.9408 9.82406 13.2337 10.117L14.8699 11.7531L14.8699 5.86182C14.8699 5.4476 15.2057 5.11182 15.6199 5.11182Z"
                    fill={bg}
                />
            </svg>
        );
    },
    headerDate: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    opacity="0.65"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.40005 1.59998C6.0024 1.59998 5.68005 1.92233 5.68005 2.31998V2.67998C5.68005 2.8788 5.51887 3.03998 5.32005 3.03998H4.96005C3.76711 3.03998 2.80005 4.00704 2.80005 5.19998V14.84C2.80005 16.0329 3.76711 17 4.96005 17H15.0401C16.233 17 17.2001 16.0329 17.2001 14.84V5.19998C17.2001 4.00704 16.233 3.03998 15.0401 3.03998H13.9601C13.7612 3.03998 13.6001 2.8788 13.6001 2.67998V2.31998C13.6001 1.92233 13.2777 1.59998 12.8801 1.59998C12.4824 1.59998 12.1601 1.92233 12.1601 2.31998V2.67998C12.1601 2.8788 11.9989 3.03998 11.8001 3.03998H7.48005C7.28123 3.03998 7.12005 2.8788 7.12005 2.67998V2.31998C7.12005 1.92233 6.7977 1.59998 6.40005 1.59998ZM4.28493 7.71998C4.28493 7.32233 4.60728 6.99998 5.00493 6.99998H14.9708C15.3684 6.99998 15.6908 7.32233 15.6908 7.71998V14.4556C15.6908 15.052 15.2073 15.5356 14.6108 15.5356H5.36493C4.76846 15.5356 4.28493 15.052 4.28493 14.4556V7.71998Z"
                    fill={bg}
                />
                <path
                    d="M5.68005 2.31998C5.68005 1.92233 6.0024 1.59998 6.40005 1.59998C6.7977 1.59998 7.12005 1.92233 7.12005 2.31998V2.67998C7.12005 2.8788 7.28123 3.03998 7.48005 3.03998H11.8001C11.9989 3.03998 12.1601 2.8788 12.1601 2.67998V2.31998C12.1601 1.92233 12.4824 1.59998 12.8801 1.59998C13.2777 1.59998 13.6001 1.92233 13.6001 2.31998V2.67998C13.6001 2.8788 13.7612 3.03998 13.9601 3.03998H15.0401C16.233 3.03998 17.2001 4.00704 17.2001 5.19998V5.99998C17.2001 6.55226 16.7523 6.99998 16.2001 6.99998H3.80005C3.24776 6.99998 2.80005 6.55226 2.80005 5.99998V5.19998C2.80005 4.00704 3.76711 3.03998 4.96005 3.03998H5.32005C5.51887 3.03998 5.68005 2.8788 5.68005 2.67998V2.31998Z"
                    fill={bg}
                />
            </svg>
        );
    },
    headerTime: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.5 7C9.22386 7 9 7.22386 9 7.5V10.5C9 10.7761 9.22386 11 9.5 11H12.5C12.7761 11 13 10.7761 13 10.5C13 10.2239 12.7761 10 12.5 10L10 10V7.5C10 7.22386 9.77614 7 9.5 7Z"
                    fill={bg}
                />
                <circle cx="10" cy="10" r="5.5" stroke={bg} />
            </svg>
        );
    },
    headerEmail: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10 8.37141C9.10061 8.37141 8.37147 9.10054 8.37147 9.99998C8.37147 10.8994 9.10061 11.6286 10 11.6286C10.8995 11.6286 11.6286 10.8994 11.6286 9.99998C11.6286 9.10054 10.8995 8.37141 10 8.37141ZM6.82861 9.99998C6.82861 8.24845 8.24851 6.82855 10 6.82855C11.7516 6.82855 13.1715 8.24845 13.1715 9.99998C13.1715 11.7515 11.7516 13.1714 10 13.1714C8.24851 13.1714 6.82861 11.7515 6.82861 9.99998Z"
                    fill={bg}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7.10676 3.87815C8.48852 3.22517 10.0506 3.05881 11.5389 3.40614C13.0272 3.75346 14.3542 4.59404 15.3042 5.79119C16.2542 6.98834 16.7713 8.47166 16.7714 9.99995V10.6C16.7714 11.282 16.5005 11.936 16.0182 12.4183C15.536 12.9005 14.8819 13.1714 14.2 13.1714C13.518 13.1714 12.8639 12.9005 12.3817 12.4183C11.8994 11.936 11.6285 11.282 11.6285 10.6V7.6C11.6285 7.17395 11.9739 6.82857 12.4 6.82857C12.826 6.82857 13.1714 7.17395 13.1714 7.6V10.6C13.1714 10.8728 13.2797 11.1344 13.4726 11.3273C13.6655 11.5202 13.9272 11.6286 14.2 11.6286C14.4727 11.6286 14.7344 11.5202 14.9273 11.3273C15.1202 11.1344 15.2285 10.8728 15.2285 10.6V10C15.2285 10 15.2285 10.0001 15.2285 10C15.2284 8.81999 14.8292 7.67462 14.0956 6.75025C13.3621 5.82586 12.3374 5.17681 11.1882 4.90862C10.039 4.64043 8.8329 4.76888 7.76597 5.27309C6.69903 5.77729 5.83403 6.6276 5.31163 7.68574C4.78922 8.74388 4.64012 9.94762 4.88859 11.1012C5.13705 12.2549 5.76846 13.2905 6.68014 14.0398C7.59182 14.7891 8.73016 15.2079 9.91006 15.2282C11.09 15.2485 12.242 14.869 13.1789 14.1515C13.5172 13.8925 14.0014 13.9567 14.2604 14.295C14.5195 14.6332 14.4552 15.1174 14.117 15.3765C12.9036 16.3057 11.4116 16.7971 9.88353 16.7708C8.35546 16.7445 6.88122 16.2021 5.70052 15.2317C4.51982 14.2614 3.7021 12.9201 3.38032 11.4261C3.05854 9.93206 3.25162 8.37311 3.92819 7.00273C4.60475 5.63235 5.72499 4.53114 7.10676 3.87815Z"
                    fill={bg}
                    fillOpacity="0.65"
                />
            </svg>
        );
    },
    headerUniqueId: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path d="M6.71271 5.54541V15H5V5.54541H6.71271Z" fill={bg} />
                <path
                    d="M11.7712 15H8.56738V5.54541H11.8358C12.7745 5.54541 13.5809 5.73469 14.2549 6.11324C14.932 6.48871 15.4521 7.02884 15.8153 7.73362C16.1784 8.4384 16.36 9.28168 16.36 10.2635C16.36 11.2483 16.1769 12.0947 15.8106 12.8025C15.4475 13.5104 14.9227 14.0536 14.2364 14.4321C13.5532 14.8107 12.7314 15 11.7712 15ZM10.2801 13.5181H11.6881C12.3467 13.5181 12.8961 13.398 13.3362 13.158C13.7763 12.9148 14.1072 12.5532 14.3287 12.0731C14.5503 11.5899 14.6611 10.9867 14.6611 10.2635C14.6611 9.5402 14.5503 8.94006 14.3287 8.46302C14.1072 7.98291 13.7794 7.62436 13.3454 7.38739C12.9146 7.14733 12.3791 7.0273 11.7389 7.0273H10.2801V13.5181Z"
                    fill={bg}
                />
            </svg>
        );
    },
    headerReference: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    opacity="0.5"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7.50007 16.0472C7.08586 16.0472 6.75007 15.7114 6.75007 15.2972L6.75007 11.2972C6.75007 9.2261 8.42901 7.54717 10.5001 7.54717L14.7001 7.54717C15.1143 7.54717 15.4501 7.88295 15.4501 8.29717C15.4501 8.71138 15.1143 9.04717 14.7001 9.04717L10.5001 9.04717C9.25743 9.04717 8.25007 10.0545 8.25007 11.2972L8.25007 15.2972C8.25007 15.7114 7.91429 16.0472 7.50007 16.0472Z"
                    fill={bg}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10.6697 3.5669C10.9626 3.27401 11.4375 3.27401 11.7304 3.5669L15.9304 7.7669C16.2233 8.05979 16.2233 8.53467 15.9304 8.82756L11.7304 13.0276C11.4375 13.3205 10.9626 13.3205 10.6697 13.0276C10.3768 12.7347 10.3768 12.2598 10.6697 11.9669L14.3394 8.29723L10.6697 4.62756C10.3768 4.33467 10.3768 3.85979 10.6697 3.5669Z"
                    fill={bg}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.90015 16.0471C3.48593 16.0471 3.15015 15.7113 3.15015 15.2971L3.15015 11.2971C3.15015 9.22605 4.82908 7.54712 6.90015 7.54712H15.1409C15.5551 7.54712 15.8909 7.8829 15.8909 8.29712C15.8909 8.71133 15.5551 9.04712 15.1409 9.04712L6.90015 9.04712C5.65751 9.04712 4.65015 10.0545 4.65015 11.2971L4.65015 15.2971C4.65015 15.7113 4.31436 16.0471 3.90015 16.0471Z"
                    fill={bg}
                />
            </svg>
        );
    },
    headerFilterSortLimit: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    d="M12.4214 13.4136L15.4638 16.5244C15.7534 16.8205 16.2283 16.8258 16.5244 16.5362C16.8205 16.2465 16.8258 15.7717 16.5362 15.4756L13.474 12.3446C13.3236 12.5391 13.162 12.7246 12.9902 12.8999C12.8114 13.0824 12.6214 13.254 12.4214 13.4136Z"
                    fill={bg}
                />
                <path
                    opacity="0.65"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M8.51515 3.75C5.88343 3.75 3.75 5.88343 3.75 8.51515C3.75 11.1469 5.88343 13.2803 8.51515 13.2803C9.84866 13.2803 11.0531 12.7335 11.9189 11.85C12.7618 10.9898 13.2803 9.81378 13.2803 8.51515C13.2803 5.88343 11.1469 3.75 8.51515 3.75ZM2.25 8.51515C2.25 5.055 5.055 2.25 8.51515 2.25C11.9753 2.25 14.7803 5.055 14.7803 8.51515C14.7803 10.222 14.0968 11.7706 12.9902 12.8999C11.854 14.0594 10.2681 14.7803 8.51515 14.7803C5.055 14.7803 2.25 11.9753 2.25 8.51515Z"
                    fill={bg}
                />
            </svg>
        );
    },
    headerIfThenElse: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    opacity="0.6"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.99995 5.24261C10.4142 5.24261 10.75 5.5784 10.75 5.99261V9.24997H13.8465C14.813 9.24997 15.5965 10.0335 15.5965 11V13.5389C15.5965 13.9531 15.2607 14.2889 14.8465 14.2889C14.4323 14.2889 14.0965 13.9531 14.0965 13.5389V11C14.0965 10.8619 13.9845 10.75 13.8465 10.75H10.75V13.5389C10.75 13.9531 10.4142 14.2889 9.99995 14.2889C9.58574 14.2889 9.24995 13.9531 9.24995 13.5389V10.75L6.15344 10.75C6.01537 10.75 5.90344 10.8619 5.90344 11V13.5389C5.90344 13.9531 5.56766 14.2889 5.15344 14.2889C4.73923 14.2889 4.40344 13.9531 4.40344 13.5389V11C4.40344 10.0335 5.18694 9.24997 6.15344 9.24997L9.24995 9.24997V5.99261C9.24995 5.5784 9.58574 5.24261 9.99995 5.24261Z"
                    fill={bg}
                />
                <path
                    d="M6 4.25C6 3.55964 6.55964 3 7.25 3H12.75C13.4404 3 14 3.55964 14 4.25V4.75C14 5.44036 13.4404 6 12.75 6H7.25C6.55964 6 6 5.44036 6 4.75V4.25Z"
                    fill={bg}
                />
                <path
                    d="M8 14.25C8 13.5596 8.55964 13 9.25 13H10.75C11.4404 13 12 13.5596 12 14.25V15.75C12 16.4404 11.4404 17 10.75 17H9.25C8.55964 17 8 16.4404 8 15.75V14.25Z"
                    fill={bg}
                />
                <path
                    d="M13 14.25C13 13.5596 13.5596 13 14.25 13H15.75C16.4404 13 17 13.5596 17 14.25V15.75C17 16.4404 16.4404 17 15.75 17H14.25C13.5596 17 13 16.4404 13 15.75V14.25Z"
                    fill={bg}
                />
                <path
                    d="M3 14.25C3 13.5596 3.55964 13 4.25 13H5.75C6.44036 13 7 13.5596 7 14.25V15.75C7 16.4404 6.44036 17 5.75 17H4.25C3.55964 17 3 16.4404 3 15.75V14.25Z"
                    fill={bg}
                />
            </svg>
        );
    },
    headerSingleValue: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    opacity="0.65"
                    d="M11.0261 13.7453C11.0395 14.4028 10.6236 14.8188 9.97945 14.8188C9.32193 14.8188 8.89253 14.376 8.90595 13.7453C8.90595 13.6916 8.90595 13.6514 8.91936 13.6111L9.14748 11.2091V8.39117L8.91936 5.89526C8.90595 5.855 8.90595 5.81475 8.90595 5.77449C8.89253 5.1438 9.32193 4.68756 9.97945 4.68756C10.6236 4.68756 11.0261 5.11696 11.0127 5.77449V5.77449C11.0127 5.81475 11.0127 5.855 10.9993 5.89526L10.7712 8.39117L10.7846 11.2091L11.0127 13.6111C11.0261 13.6514 11.0261 13.6916 11.0261 13.7453Z"
                    fill={bg}
                />
                <path
                    d="M12.8645 12.7121L10.7846 11.2091C10.1453 10.8127 9.78671 10.8027 9.14748 11.2091L7.04072 12.7121C6.83943 12.8731 6.59789 12.9536 6.32952 12.9536C5.79276 12.9536 5.29626 12.5108 5.29626 11.8801C5.29626 11.4373 5.55122 11.1018 6.00746 10.9005L8.32893 9.80016L5.99405 8.69982C5.5378 8.49853 5.29626 8.16306 5.29626 7.73366C5.29626 7.10297 5.79276 6.64673 6.32952 6.64673C6.59789 6.64673 6.85285 6.74066 7.04072 6.90169L9.14748 8.39118C9.76468 8.73061 10.121 8.71592 10.7712 8.39118L12.8645 6.90169C13.0792 6.74066 13.3073 6.64673 13.5891 6.64673C14.1259 6.64673 14.609 7.08955 14.609 7.73366C14.609 8.16306 14.3674 8.49853 13.9112 8.69982L11.5763 9.80016L13.9112 10.9139C14.3674 11.1018 14.609 11.4507 14.609 11.8801C14.609 12.5108 14.1125 12.967 13.5891 12.967C13.3073 12.967 13.0792 12.8731 12.8645 12.7121Z"
                    fill={bg}
                />
            </svg>
        );
    },
    headerLookup: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    opacity="0.6"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.74988 14.0348C3.74988 13.6206 4.08566 13.2848 4.49988 13.2848L14.1929 13.2848C14.6071 13.2848 14.9429 13.6206 14.9429 14.0348C14.9429 14.449 14.6071 14.7848 14.1929 14.7848L4.49988 14.7848C4.08566 14.7848 3.74988 14.449 3.74988 14.0348Z"
                    fill={bg}
                />
                <path
                    opacity="0.6"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.74988 10C3.74988 9.58579 4.08566 9.25 4.49988 9.25L7.3344 9.25C7.74862 9.25 8.0844 9.58579 8.0844 10C8.0844 10.4142 7.74862 10.75 7.3344 10.75L4.49988 10.75C4.08566 10.75 3.74988 10.4142 3.74988 10Z"
                    fill={bg}
                />
                <path
                    opacity="0.6"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.74988 5.90442C3.74988 5.49021 4.08566 5.15442 4.49988 5.15442L7.3344 5.15442C7.74862 5.15442 8.0844 5.49021 8.0844 5.90442C8.0844 6.31863 7.74862 6.65442 7.3344 6.65442L4.49988 6.65442C4.08566 6.65442 3.74988 6.31863 3.74988 5.90442Z"
                    fill={bg}
                />
                <path
                    d="M15.8788 10.0543C16.2575 9.43917 16.4813 8.72742 16.4813 7.95416C16.4813 5.76619 14.7514 4 12.6084 4C10.4655 4 8.7356 5.76619 8.7356 7.95416C8.7356 10.1421 10.4655 11.9083 12.5998 11.9083C13.3572 11.9083 14.0629 11.6799 14.6567 11.2932L16.7186 13.3984C17.0607 13.7477 17.623 13.7477 17.9651 13.3984C18.2977 13.0589 18.2973 12.5156 17.9642 12.1766L15.8788 10.0543ZM12.6084 10.1509C11.4208 10.1509 10.4569 9.16677 10.4569 7.95416C10.4569 6.74155 11.4208 5.75741 12.6084 5.75741C13.7961 5.75741 14.76 6.74155 14.76 7.95416C14.76 9.16677 13.7961 10.1509 12.6084 10.1509Z"
                    fill={bg}
                />
            </svg>
        );
    },
    headerTextTemplate: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    opacity="0.65"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.52007 2.5775C5.82045 2.2923 6.29517 2.30461 6.58037 2.60499L9.54384 5.72621C9.81864 6.01564 9.81864 6.46959 9.54384 6.75903L6.58037 9.88024C6.29517 10.1806 5.82045 10.1929 5.52007 9.90773C5.21968 9.62253 5.20738 9.14781 5.49258 8.84743L7.38017 6.85936C6.87343 6.83012 6.23547 6.8394 5.58682 6.96563C4.87912 7.10334 4.19308 7.37332 3.64329 7.85082C3.10483 8.31848 2.65 9.0252 2.45639 10.1295C2.38485 10.5375 1.99612 10.8103 1.58813 10.7387C1.18014 10.6672 0.90739 10.2785 0.978922 9.87048C1.22756 8.45232 1.84242 7.42813 2.65971 6.71832C3.46567 6.01833 4.42146 5.66427 5.3003 5.49325C5.95461 5.36592 6.58534 5.33621 7.11621 5.34787L5.49258 3.63781C5.20738 3.33742 5.21968 2.86271 5.52007 2.5775Z"
                    fill={bg}
                />
                <path
                    d="M10.2182 15.28H14.5818L15.3672 17.2H17.2L13.0545 7.59998H11.7454L7.59998 17.2H9.4327L10.2182 15.28ZM12.4 9.32798L14.032 13.7091H10.768L12.4 9.32798Z"
                    fill={bg}
                />
            </svg>
        );
    },
    headerMath: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2.33655 6.25009C2.33655 5.83588 2.67233 5.5001 3.08655 5.5001L5.08613 5.50009L5.08613 3.50183C5.08613 3.08762 5.42191 2.75183 5.83613 2.75183C6.25034 2.75183 6.58613 3.08762 6.58613 3.50183V5.50009L8.58663 5.50009C9.00084 5.50009 9.33663 5.83588 9.33663 6.25009C9.33663 6.66431 9.00084 7.00009 8.58663 7.00009H6.58613V9.00191C6.58613 9.41612 6.25034 9.75191 5.83613 9.75191C5.42191 9.75191 5.08613 9.41612 5.08613 9.00191L5.08613 7.00009H3.08655C2.67233 7.00009 2.33655 6.66431 2.33655 6.25009Z"
                    fill={bg}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10.6127 15.1656C10.6127 14.7514 10.9485 14.4156 11.3627 14.4156H16.8628C17.277 14.4156 17.6128 14.7514 17.6128 15.1656C17.6128 15.5798 17.277 15.9156 16.8628 15.9156H11.3627C10.9485 15.9156 10.6127 15.5798 10.6127 15.1656ZM10.6127 12.1656C10.6127 11.7514 10.9485 11.4156 11.3627 11.4156L16.8628 11.4156C17.277 11.4156 17.6128 11.7514 17.6128 12.1656C17.6128 12.5798 17.277 12.9156 16.8628 12.9156L11.3627 12.9156C10.9485 12.9156 10.6127 12.5798 10.6127 12.1656Z"
                    fill={bg}
                />
                <g opacity="0.6">
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M10.6127 6.25006C10.6127 5.83585 10.9484 5.50006 11.3627 5.50006L16.8627 5.50006C17.277 5.50006 17.6127 5.83585 17.6127 6.25006C17.6127 6.66427 17.277 7.00006 16.8627 7.00006H11.3627C10.9484 7.00006 10.6127 6.66427 10.6127 6.25006Z"
                        fill={bg}
                    />
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M3.37982 11.1895C3.67271 10.8966 4.14759 10.8966 4.44048 11.1895L5.8544 12.6034L7.26738 11.1904C7.56028 10.8975 8.03515 10.8975 8.32804 11.1904C8.62094 11.4833 8.62094 11.9582 8.32804 12.2511L6.91506 13.6641L8.32962 15.0786C8.62252 15.3715 8.62252 15.8464 8.32962 16.1393C8.03673 16.4322 7.56186 16.4322 7.26896 16.1393L5.8544 14.7247L4.4389 16.1402C4.14601 16.4331 3.67113 16.4331 3.37824 16.1402C3.08535 15.8473 3.08535 15.3725 3.37824 15.0796L4.79374 13.6641L3.37982 12.2502C3.08693 11.9573 3.08693 11.4824 3.37982 11.1895Z"
                        fill={bg}
                    />
                </g>
            </svg>
        );
    },
    headerRollup: (props: IconProps) => {
        const className = props.className;
        const size = props.size ?? 20;
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    d="M10.0003 8.60467C9.22969 8.60408 8.60494 9.22883 8.60436 9.99942C8.60377 10.77 9.22852 11.3948 9.99911 11.3953C10.7697 11.3959 11.3944 10.7712 11.395 10.0006C11.3956 9.23 10.7709 8.60525 10.0003 8.60467ZM10.0003 3C6.13219 3.00233 2.99852 6.1395 3.00086 10.0076C3.00144 10.8651 3.15952 11.7156 3.46694 12.5159C3.56436 12.8228 3.89219 12.9931 4.19961 12.8957C4.50644 12.7983 4.67677 12.4704 4.57936 12.163C4.57236 12.1414 4.56477 12.1204 4.55544 12.1C3.39694 9.09583 4.89377 5.72242 7.89736 4.5645C10.9009 3.40658 14.2749 4.90342 15.4329 7.907C16.5908 10.9106 15.0939 14.2846 12.0904 15.4425C10.6215 16.0089 8.98586 15.9564 7.55611 15.2984C7.26327 15.1631 6.91619 15.2908 6.78144 15.5837C6.64611 15.8759 6.77327 16.223 7.06611 16.3583C7.98544 16.7842 8.98702 17.0029 10.0003 17C13.866 17 17.0003 13.8658 17.0003 10C17.0003 6.13425 13.866 3 10.0003 3ZM5.25777 13.6266C5.72036 13.6266 6.09486 14.0011 6.09486 14.4637C6.09486 14.9263 5.72036 15.3008 5.25777 15.3008C4.79519 15.3008 4.42069 14.9263 4.42069 14.4637C4.42069 14.0011 4.79519 13.6266 5.25777 13.6266Z"
                    fill={bg}
                />
                <path
                    opacity="0.5"
                    d="M13.6234 12.9418C14.5159 11.8375 14.8612 10.3885 14.5626 9.00018C14.4967 8.68459 14.1881 8.48217 13.8725 8.54809C13.5569 8.61401 13.3545 8.92259 13.4204 9.23818C13.6479 10.2823 13.3907 11.3732 12.7204 12.2056C11.4989 13.7042 9.29449 13.9288 7.79591 12.7073C6.29732 11.4858 6.07332 9.28134 7.29424 7.78334C7.43599 7.60951 7.59407 7.44967 7.76674 7.30559C8.57816 6.63651 9.64566 6.36351 10.6787 6.56126C10.9943 6.62659 11.3029 6.42359 11.3682 6.10801C11.4336 5.79242 11.2306 5.48384 10.915 5.41851C10.9074 5.41676 10.8998 5.41559 10.8917 5.41442C8.36116 4.92442 5.91291 6.57817 5.42291 9.10868C4.93291 11.6392 6.58666 14.0874 9.11716 14.5774C10.8071 14.9047 12.5402 14.2753 13.6257 12.9394L13.6234 12.9418ZM13.6269 7.20992C13.6269 6.74734 13.2524 6.37284 12.7898 6.37284C12.3272 6.37284 11.9527 6.74734 11.9527 7.20992C11.9527 7.67251 12.3272 8.04701 12.7898 8.04701C13.2518 8.04643 13.6269 7.67192 13.6269 7.20992Z"
                    fill={bg}
                />
            </svg>
        );
    },
    headerJoinStrings: (props: IconProps) => {
        const className = props.className;
        const size = props.size ?? 20;
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    opacity="0.65"
                    d="M12.7365 13.9616C12.861 14.2668 13.1973 14.4314 13.5054 14.3143C15.6399 13.5033 17.5416 11.6497 17.5416 8.96824C17.5416 7.06378 16.5513 5.69257 14.9363 5.69257C13.3213 5.69257 12.4529 7.03331 12.4529 8.67876C13.8546 8.77018 14.7992 9.5929 14.7992 10.7965C14.7992 11.7778 14.0956 12.5672 13.0987 13.0756C12.7729 13.2417 12.5984 13.623 12.7365 13.9616Z"
                    fill={bg}
                />
                <ellipse cx="14.9148" cy="8.13622" rx="2.51476" ry="2.44365" fill={bg} />
                <path
                    opacity="0.6"
                    d="M3.29993 14.0083L9.30816 14.0083"
                    stroke={bg}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                />
                <path
                    opacity="0.6"
                    d="M3.29993 9.97351L9.30816 9.97351"
                    stroke={bg}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                />
                <path
                    opacity="0.6"
                    d="M3.29993 5.87793L9.30816 5.87793"
                    stroke={bg}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                />
            </svg>
        );
    },
    headerSplitString: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    opacity="0.6"
                    d="M7.68144 14.5362C7.82987 14.9001 8.23074 15.0963 8.59811 14.9567C11.1429 13.9898 13.4101 11.78 13.4101 8.58313C13.4101 6.31262 12.2294 4.67786 10.304 4.67786C8.37861 4.67786 7.34326 6.27629 7.34326 8.23801C9.01436 8.347 10.1405 9.32786 10.1405 10.7628C10.1405 11.9327 9.30173 12.8739 8.11316 13.48C7.72478 13.678 7.51679 14.1326 7.68144 14.5362Z"
                    fill={bg}
                />
                <ellipse cx="10.2789" cy="7.59125" rx="2.99811" ry="2.91333" fill={bg} />
            </svg>
        );
    },
    headerGeoDistance: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    opacity="0.65"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.25 7.60001C3.25 5.86031 4.6603 4.45001 6.4 4.45001H10C10.4142 4.45001 10.75 4.7858 10.75 5.20001C10.75 5.61423 10.4142 5.95001 10 5.95001H6.4C5.48873 5.95001 4.75 6.68874 4.75 7.60001C4.75 8.51128 5.48873 9.25001 6.4 9.25001H12.4C14.1397 9.25001 15.55 10.6603 15.55 12.4C15.55 14.1397 14.1397 15.55 12.4 15.55H10C9.58579 15.55 9.25 15.2142 9.25 14.8C9.25 14.3858 9.58579 14.05 10 14.05H12.4C13.3113 14.05 14.05 13.3113 14.05 12.4C14.05 11.4887 13.3113 10.75 12.4 10.75H6.4C4.6603 10.75 3.25 9.33971 3.25 7.60001Z"
                    fill={bg}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.93435 12.3118C3.64362 12.0168 3.16876 12.0133 2.87371 12.304C2.57867 12.5947 2.57518 13.0696 2.86591 13.3646L4.28023 14.7999L2.86581 16.2353C2.57508 16.5304 2.57857 17.0052 2.87361 17.296C3.16865 17.5867 3.64351 17.5832 3.93424 17.2882L5.33317 15.8685L6.73209 17.2882C7.02282 17.5832 7.49768 17.5867 7.79272 17.296C8.08776 17.0052 8.09126 16.5304 7.80053 16.2353L6.38611 14.7999L7.80043 13.3646C8.09116 13.0696 8.08767 12.5947 7.79263 12.304C7.49759 12.0133 7.02272 12.0168 6.73199 12.3118L5.33317 13.7314L3.93435 12.3118Z"
                    fill={bg}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M13.5343 2.71181C13.2436 2.41677 12.7688 2.41327 12.4737 2.704C12.1787 2.99473 12.1752 3.46959 12.4659 3.76463L13.8802 5.19993L12.4658 6.63533C12.1751 6.93037 12.1786 7.40523 12.4736 7.69596C12.7687 7.98669 13.2435 7.9832 13.5342 7.68816L14.9332 6.26848L16.3321 7.68816C16.6228 7.9832 17.0977 7.98669 17.3927 7.69596C17.6878 7.40523 17.6913 6.93037 17.4005 6.63533L15.9861 5.19993L17.4004 3.76463C17.6912 3.46959 17.6877 2.99473 17.3926 2.704C17.0976 2.41327 16.6227 2.41677 16.332 2.71181L14.9332 4.13138L13.5343 2.71181Z"
                    fill={bg}
                />
            </svg>
        );
    },
    headerArray: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const className = props.className;
        const size = props.size ?? 20;
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}>
                <path
                    d="M15.1 6.70002C15.5971 6.70002 16 6.29708 16 5.80002C16 5.30297 15.5971 4.90002 15.1 4.90002H7.30002C6.80297 4.90002 6.40002 5.30297 6.40002 5.80002C6.40002 6.29708 6.80297 6.70002 7.30002 6.70002L15.1 6.70002Z"
                    fill={bg}
                />
                <path
                    d="M16 10C16 10.4971 15.5971 10.9 15.1 10.9L7.30002 10.9C6.80297 10.9 6.40002 10.4971 6.40002 10C6.40002 9.50297 6.80297 9.10002 7.30002 9.10002L15.1 9.10002C15.5971 9.10002 16 9.50297 16 10Z"
                    fill={bg}
                />
                <path
                    d="M16 14.2C16 14.6971 15.5971 15.1 15.1 15.1L7.30002 15.1C6.80297 15.1 6.40002 14.6971 6.40002 14.2C6.40002 13.703 6.80297 13.3 7.30002 13.3L15.1 13.3C15.5971 13.3 16 13.703 16 14.2Z"
                    fill={bg}
                />
                <path
                    opacity="0.65"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.21546 5.80029C5.21546 6.29716 4.81266 6.69995 4.3158 6.69995C3.81893 6.69995 3.41614 6.29716 3.41614 5.80029C3.41614 5.30342 3.81893 4.90063 4.3158 4.90063C4.81266 4.90063 5.21546 5.30342 5.21546 5.80029ZM5.21546 10C5.21546 10.4969 4.81266 10.8997 4.3158 10.8997C3.81893 10.8997 3.41614 10.4969 3.41614 10C3.41614 9.50313 3.81893 9.10034 4.3158 9.10034C4.81266 9.10034 5.21546 9.50313 5.21546 10ZM4.3158 15.099C4.81266 15.099 5.21546 14.6962 5.21546 14.1993C5.21546 13.7025 4.81266 13.2997 4.3158 13.2997C3.81893 13.2997 3.41614 13.7025 3.41614 14.1993C3.41614 14.6962 3.81893 15.099 4.3158 15.099Z"
                    fill={bg}
                />
            </svg>
        );
    },
    rowOwnerOverlay: (props: IconProps) => {
        const fg = props.fgColor ?? "white";
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0)">
                    <path
                        d="M2 15V16H3H15H16V15V13.5C16 12.634 15.5582 11.9494 15.0223 11.4548C14.4932 10.9664 13.8087 10.5984 13.1228 10.3236C12.3333 10.0073 11.4587 9.78207 10.6437 9.64796C12.0333 9.02114 13 7.62385 13 6C13 3.79022 11.2098 2 9 2C6.79022 2 5 3.79022 5 6C5 7.62386 5.96672 9.02114 7.35626 9.64797C6.54129 9.78207 5.6667 10.0073 4.87719 10.3236C4.19132 10.5984 3.50678 10.9664 2.97767 11.4548C2.44177 11.9494 2 12.634 2 13.5V15Z"
                        fill={bg}
                        stroke={fg}
                        strokeWidth="2"
                    />
                </g>
                <defs>
                    <clipPath id="clip0">
                        <rect width="18" height="18" fill="white" />
                    </clipPath>
                </defs>
            </svg>
        );
    },
    gSheet: (props: IconProps) => {
        const fg = props.fgColor ?? "#00C453";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5 3C3.34315 3 2 4.34315 2 6V18C2 19.6569 3.34315 21 5 21H19C20.6569 21 22 19.6569 22 18V6C22 4.34315 20.6569 3 19 3H5ZM14.875 5C15.3582 5 15.75 5.39175 15.75 5.875V8H19.125C19.6082 8 20 8.39175 20 8.875C20 9.35825 19.6082 9.75 19.125 9.75H15.75V18.125C15.75 18.6082 15.3582 19 14.875 19C14.3918 19 14 18.6082 14 18.125V9.75H4.875C4.39175 9.75 4 9.35825 4 8.875C4 8.39175 4.39175 8 4.875 8H14V5.875C14 5.39175 14.3918 5 14.875 5Z"
                    fill={fg}
                />
            </svg>
        );
    },
    gSheetFixedColor: (_props: IconProps) => {
        const fg = "#00C453";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5 3C3.34315 3 2 4.34315 2 6V18C2 19.6569 3.34315 21 5 21H19C20.6569 21 22 19.6569 22 18V6C22 4.34315 20.6569 3 19 3H5ZM14.875 5C15.3582 5 15.75 5.39175 15.75 5.875V8H19.125C19.6082 8 20 8.39175 20 8.875C20 9.35825 19.6082 9.75 19.125 9.75H15.75V18.125C15.75 18.6082 15.3582 19 14.875 19C14.3918 19 14 18.6082 14 18.125V9.75H4.875C4.39175 9.75 4 9.35825 4 8.875C4 8.39175 4.39175 8 4.875 8H14V5.875C14 5.39175 14.3918 5 14.875 5Z"
                    fill={fg}
                />
            </svg>
        );
    },
    gSheetsLogo: (props: IconProps) => {
        const bg = props.fgColor ?? "#0F9D58";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M13.8955 0.343292H4.88805C4.0138 0.343292 3.2985 1.05859 3.2985 1.93284V22.0672C3.2985 22.9414 4.0138 23.6567 4.88805 23.6567H18.6642C19.5384 23.6567 20.2537 22.9414 20.2537 22.0672V6.7015L16.5448 4.05225L13.8955 0.343292Z"
                    fill={bg}
                />
                <path
                    d="M7.53731 11.7351V19.4179H16.0149V11.7351H7.53731ZM11.2463 18.3582H8.59701V17.0336H11.2463V18.3582ZM11.2463 16.2388H8.59701V14.9142H11.2463V16.2388ZM11.2463 14.1194H8.59701V12.7948H11.2463V14.1194ZM14.9552 18.3582H12.306V17.0336H14.9552V18.3582ZM14.9552 16.2388H12.306V14.9142H14.9552V16.2388ZM14.9552 14.1194H12.306V12.7948H14.9552V14.1194Z"
                    fill="#F1F1F1"
                />
                <path d="M14.3605 6.23657L20.2537 12.1285V6.70152L14.3605 6.23657Z" fill="url(#paint0_linear)" />
                <path
                    d="M13.8955 0.343292V5.11195C13.8955 5.99018 14.6068 6.7015 15.4851 6.7015H20.2537L13.8955 0.343292Z"
                    fill="#87CEAC"
                />
                <path
                    d="M4.88805 0.343292C4.0138 0.343292 3.2985 1.05859 3.2985 1.93284V2.06531C3.2985 1.19105 4.0138 0.475755 4.88805 0.475755H13.8955V0.343292H4.88805Z"
                    fill="white"
                    fillOpacity="0.2"
                />
                <path
                    d="M18.6642 23.5243H4.88805C4.0138 23.5243 3.2985 22.809 3.2985 21.9347V22.0672C3.2985 22.9414 4.0138 23.6567 4.88805 23.6567H18.6642C19.5384 23.6567 20.2537 22.9414 20.2537 22.0672V21.9347C20.2537 22.809 19.5384 23.5243 18.6642 23.5243Z"
                    fill="#263238"
                    fillOpacity="0.2"
                />
                <path
                    d="M15.4851 6.70149C14.6068 6.70149 13.8955 5.99017 13.8955 5.11194V5.2444C13.8955 6.12263 14.6068 6.83395 15.4851 6.83395H20.2537V6.70149H15.4851Z"
                    fill="#263238"
                    fillOpacity="0.1"
                />
                <defs>
                    <linearGradient
                        id="paint0_linear"
                        x1="17.3074"
                        y1="6.74246"
                        x2="17.3074"
                        y2="12.1293"
                        gradientUnits="userSpaceOnUse">
                        <stop stopColor="#263238" stopOpacity="0.2" />
                        <stop offset="1" stopColor="#263238" stopOpacity="0.02" />
                    </linearGradient>
                </defs>
            </svg>
        );
    },
    openExternal: (props: IconProps) => {
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M6.27857 4.5H4C3.44772 4.5 3 4.94772 3 5.5V18.8C3 19.3523 3.44772 19.8 4 19.8H17.3C17.8523 19.8 18.3 19.3523 18.3 18.8V15.975"
                    vectorEffect="non-scaling-stroke"
                    stroke={fg}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                />
                <path
                    d="M12.45 3C16.2576 3 19.8 3 19.8 3M19.8 3C19.8 3 19.8 6.54239 19.8 10.35M19.8 3L9 13.8"
                    vectorEffect="non-scaling-stroke"
                    stroke={fg}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                />
            </svg>
        );
    },
    chevronRight: (props: IconProps) => {
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.59 16.59L10 18L16 12L10 6L8.59 7.41L13.17 12L8.59 16.59Z" fill={fg} />
            </svg>
        );
    },
    chevronLeft: (props: IconProps) => {
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z" fill={fg} />
            </svg>
        );
    },
    actionPointer: (_props: IconProps) => {
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M8.7925 9.24V5.5C8.7925 4.12 9.9125 3 11.2925 3C12.6725 3 13.7925 4.12 13.7925 5.5V9.24C15.0025 8.43 15.7925 7.06 15.7925 5.5C15.7925 3.01 13.7825 1 11.2925 1C8.8025 1 6.7925 3.01 6.7925 5.5C6.7925 7.06 7.5825 8.43 8.7925 9.24ZM14.2925 11.71C14.0125 11.57 13.7125 11.5 13.4025 11.5H12.7925V5.5C12.7925 4.67 12.1225 4 11.2925 4C10.4625 4 9.7925 4.67 9.7925 5.5V16.24L6.3525 15.52C5.9825 15.44 5.5925 15.56 5.3225 15.83C4.8925 16.27 4.8925 16.97 5.3225 17.41L9.3325 21.42C9.7125 21.79 10.2225 22 10.7525 22H16.8525C17.8525 22 18.6925 21.27 18.8325 20.28L19.4625 15.81C19.5825 14.96 19.1425 14.12 18.3725 13.74L14.2925 11.71Z"
                    fill="#8A5ED8"
                />
            </svg>
        );
    },
    editPencil: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const fg = props.fgColor ?? "#313139";
        const iconSize = props.size ?? 16;
        return (
            <svg width={iconSize} height={iconSize} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="8" r="8" fill={bg} />
                <path
                    d="M4.25 10.1874V11.7499H5.8125L10.4208 7.14161L8.85833 5.57911L4.25 10.1874ZM11.6292 5.93328C11.7917 5.77078 11.7917 5.50828 11.6292 5.34578L10.6542 4.37078C10.4917 4.20828 10.2292 4.20828 10.0667 4.37078L9.30417 5.13328L10.8667 6.69578L11.6292 5.93328V5.93328Z"
                    fill={fg}
                />
            </svg>
        );
    },
    warning: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M.619 12.249a1 1 0 00.865 1.501h13.032a1 1 0 00.865-1.501L8.865.995a1 1 0 00-1.73 0L.619 12.249zM8.75 10.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm0-3a.75.75 0 01-1.5 0v-1.5a.75.75 0 111.5 0v1.5z"
                    fill={bg}
                />
            </svg>
        );
    },
    lightTheme: (props: IconProps) => {
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="112" height="170" viewBox="0 0 112 170" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x=".5" y=".5" width="111" height="169" rx="5.5" fill="#fff" stroke="#D8D8E3" />
                <circle cx="94" cy="123" r="10" fill={fg} />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M94 118a1 1 0 00-1 1v3h-3a1 1 0 000 2h3v3a1 1 0 002 0v-3h3a1 1 0 000-2h-3v-3a1 1 0 00-1-1z"
                    fill="#fff"
                />
                <circle cx="13" cy="46" r="5" fill="#4F5DFF" />
                <path
                    d="M81 42.5a8.5 8.5 0 00-8.5-8.5H32c-5.523 0-10 4.477-10 10v7h50.5a8.5 8.5 0 008.5-8.5z"
                    fill="#EDEDF3"
                />
                <circle cx="99" cy="72" r="5" fill="#E14421" />
                <path
                    d="M31 65c0-5.523 4.477-10 10-10h39c5.523 0 10 4.477 10 10v12H41c-5.523 0-10-4.477-10-10v-2z"
                    fill={fg}
                />
                <path fill="#D8D8E3" d="M0 145h112v1H0z" />
                <path
                    d="M89 153.818v7.273l3.182-1.818 3.636 1.818L99 159.273V152l-3.182 1.818L92.182 152 89 153.818zM14 155.15l3.28-3.403a1 1 0 011.44 0L22 155.15v4.95a.903.903 0 01-.26.636.883.883 0 01-.629.264H14.89a.883.883 0 01-.629-.264.903.903 0 01-.26-.636v-4.95z"
                    fill="rgba(0, 0, 0, 0.9)"
                />
                <path
                    d="M56 152c-2.642 0-4.792 1.771-4.792 3.948a3.585 3.585 0 001.323 2.725l-.958 1.911a.207.207 0 00.093.279c.057.029.124.03.181.003l2.563-1.193a5.668 5.668 0 001.59.222c2.642 0 4.792-1.77 4.792-3.947 0-2.178-2.15-3.948-4.792-3.948z"
                    fill={fg}
                />
                <path fill="#D8D8E3" d="M0 22h112v1H0z" />
                <path
                    d="M50.246 15.14c1.3 0 2.254-.777 2.387-1.93h-.992c-.137.634-.68 1.032-1.395 1.032-.96 0-1.559-.789-1.559-2.062 0-1.27.598-2.059 1.555-2.059.711 0 1.254.438 1.395 1.113h.992c-.117-1.175-1.106-2.011-2.387-2.011-1.597 0-2.586 1.129-2.586 2.96 0 1.825.992 2.958 2.59 2.958zM53.691 15h.97v-2.48c0-.614.355-1.028.991-1.028.563 0 .86.332.86.98V15h.968v-2.758c0-1.015-.554-1.594-1.468-1.594-.637 0-1.094.29-1.297.77h-.07V9.074h-.954V15zM60.09 14.309c-.43 0-.738-.215-.738-.575 0-.347.254-.543.796-.578l.965-.062v.34c0 .496-.437.875-1.023.875zm-.285.761c.558 0 1.027-.242 1.265-.656h.067V15h.933v-2.918c0-.906-.617-1.441-1.715-1.441-1.015 0-1.722.48-1.8 1.234h.914c.09-.29.394-.445.84-.445.523 0 .804.234.804.652v.36l-1.101.066c-1.04.062-1.621.508-1.621 1.281 0 .781.59 1.281 1.414 1.281zM63.547 9.68v1.074h-.676v.773h.676v2.332c0 .829.39 1.16 1.371 1.16.188 0 .367-.019.508-.046v-.758c-.117.012-.192.02-.328.02-.407 0-.586-.188-.586-.61v-2.098h.914v-.773h-.914V9.68h-.965z"
                    fill="#1C1C25"
                />
            </svg>
        );
    },
    darkTheme: (props: IconProps) => {
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="112" height="170" viewBox="0 0 112 170" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="112" height="170" rx="6" fill="#1C1C25" />
                <circle cx="94" cy="123" r="10" fill={fg} />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M94 118a1 1 0 00-1 1v3h-3a1 1 0 000 2h3v3a1 1 0 002 0v-3h3a1 1 0 000-2h-3v-3a1 1 0 00-1-1z"
                    fill="#fff"
                />
                <circle cx="13" cy="46" r="5" fill="#4F5DFF" />
                <path
                    d="M81 42.5a8.5 8.5 0 00-8.5-8.5H32c-5.523 0-10 4.477-10 10v7h50.5a8.5 8.5 0 008.5-8.5z"
                    fill="#353547"
                />
                <circle cx="99" cy="72" r="5" fill="#E14421" />
                <path
                    d="M31 65c0-5.523 4.477-10 10-10h39c5.523 0 10 4.477 10 10v12H41c-5.523 0-10-4.477-10-10v-2z"
                    fill={fg}
                />
                <path d="M0 145h112v19a6 6 0 01-6 6H6a6 6 0 01-6-6v-19z" fill="#000" />
                <path
                    d="M89 153.818v7.273l3.182-1.818 3.636 1.818L99 159.273V152l-3.182 1.818L92.182 152 89 153.818zM14 155.15l3.28-3.403a1 1 0 011.44 0L22 155.15v4.95a.903.903 0 01-.26.636.883.883 0 01-.629.264H14.89a.883.883 0 01-.629-.264.903.903 0 01-.26-.636v-4.95z"
                    fill="rgba(255, 255, 255, 0.9)"
                />
                <path
                    d="M56 152c-2.642 0-4.792 1.771-4.792 3.948a3.585 3.585 0 001.323 2.725l-.958 1.911a.207.207 0 00.093.279c.057.029.124.03.181.003l2.563-1.193a5.668 5.668 0 001.59.222c2.642 0 4.792-1.77 4.792-3.947 0-2.178-2.15-3.948-4.792-3.948z"
                    fill={fg}
                />
                <path d="M0 6a6 6 0 016-6h100a6 6 0 016 6v17H0V6z" fill="#000" />
                <path
                    d="M50.246 15.14c1.3 0 2.254-.777 2.387-1.93h-.992c-.137.634-.68 1.032-1.395 1.032-.96 0-1.559-.789-1.559-2.062 0-1.27.598-2.059 1.555-2.059.711 0 1.254.438 1.395 1.113h.992c-.117-1.175-1.106-2.011-2.387-2.011-1.597 0-2.586 1.129-2.586 2.96 0 1.825.992 2.958 2.59 2.958zM53.691 15h.97v-2.48c0-.614.355-1.028.991-1.028.563 0 .86.332.86.98V15h.968v-2.758c0-1.015-.554-1.594-1.468-1.594-.637 0-1.094.29-1.297.77h-.07V9.074h-.954V15zM60.09 14.309c-.43 0-.738-.215-.738-.575 0-.347.254-.543.796-.578l.965-.062v.34c0 .496-.437.875-1.023.875zm-.285.761c.558 0 1.027-.242 1.265-.656h.067V15h.933v-2.918c0-.906-.617-1.441-1.715-1.441-1.015 0-1.722.48-1.8 1.234h.914c.09-.29.394-.445.84-.445.523 0 .804.234.804.652v.36l-1.101.066c-1.04.062-1.621.508-1.621 1.281 0 .781.59 1.281 1.414 1.281zM63.547 9.68v1.074h-.676v.773h.676v2.332c0 .829.39 1.16 1.371 1.16.188 0 .367-.019.508-.046v-.758c-.117.012-.192.02-.328.02-.407 0-.586-.188-.586-.61v-2.098h.914v-.773h-.914V9.68h-.965z"
                    fill="#fff"
                />
            </svg>
        );
    },
    boldTheme: (props: IconProps) => {
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="112" height="170" viewBox="0 0 112 170" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x=".5" y=".5" width="111" height="169" rx="5.5" fill="#fff" stroke="#D8D8E3" />
                <circle cx="94" cy="123" r="10" fill={fg} />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M94 118a1 1 0 00-1 1v3h-3a1 1 0 000 2h3v3a1 1 0 002 0v-3h3a1 1 0 000-2h-3v-3a1 1 0 00-1-1z"
                    fill="#fff"
                />
                <circle cx="13" cy="54" r="5" fill="#4F5DFF" />
                <path
                    d="M81 50.5a8.5 8.5 0 00-8.5-8.5H32c-5.523 0-10 4.477-10 10v7h50.5a8.5 8.5 0 008.5-8.5z"
                    fill="#EDEDF3"
                />
                <circle cx="99" cy="80" r="5" fill="#E14421" />
                <path
                    d="M31 73c0-5.523 4.477-10 10-10h39c5.523 0 10 4.477 10 10v12H41c-5.523 0-10-4.477-10-10v-2z"
                    fill={fg}
                />
                <path fill="#D8D8E3" d="M0 145h112v1H0z" />
                <path
                    d="M89 153.818v7.273l3.182-1.818 3.636 1.818L99 159.273V152l-3.182 1.818L92.182 152 89 153.818zM14 155.15l3.28-3.403a1 1 0 011.44 0L22 155.15v4.95a.903.903 0 01-.26.636.883.883 0 01-.629.264H14.89a.883.883 0 01-.629-.264.903.903 0 01-.26-.636v-4.95z"
                    fill="rgba(0, 0, 0, 0.9)"
                />
                <path
                    d="M56 152c-2.642 0-4.792 1.771-4.792 3.948a3.585 3.585 0 001.323 2.725l-.958 1.911a.207.207 0 00.093.279c.057.029.124.03.181.003l2.563-1.193a5.668 5.668 0 001.59.222c2.642 0 4.792-1.77 4.792-3.947 0-2.178-2.15-3.948-4.792-3.948zM0 6a6 6 0 016-6h100a6 6 0 016 6v26H0V6z"
                    fill={fg}
                />
                <path
                    d="M13.002 26.235c2.209 0 3.821-1.365 3.98-3.345h-1.866c-.184 1.009-1.01 1.656-2.108 1.656-1.447 0-2.342-1.2-2.342-3.129 0-1.93.895-3.123 2.336-3.123 1.092 0 1.923.692 2.107 1.746h1.867c-.14-1.994-1.803-3.434-3.974-3.434-2.66 0-4.297 1.834-4.297 4.811 0 2.983 1.644 4.818 4.297 4.818zM18.626 26h1.847v-4.018c0-.908.533-1.523 1.46-1.523.838 0 1.289.501 1.289 1.472V26h1.847v-4.507c0-1.682-.895-2.628-2.4-2.628-1.028 0-1.79.483-2.113 1.283h-.115v-3.822h-1.815V26zM29.392 24.718c-.642 0-1.092-.318-1.092-.838 0-.502.387-.787 1.18-.844l1.416-.09v.515c0 .71-.648 1.257-1.504 1.257zm-.61 1.39c.908 0 1.67-.38 2.032-1.022h.114V26h1.79v-4.78c0-1.498-1.048-2.374-2.901-2.374-1.758 0-2.945.813-3.066 2.108h1.701c.152-.42.597-.648 1.263-.648.762 0 1.181.33 1.181.914v.572l-1.695.101c-1.74.102-2.71.838-2.71 2.107 0 1.283.945 2.108 2.291 2.108zM35.06 17.335v1.733h-1.092v1.454h1.092v3.586c0 1.365.673 1.917 2.374 1.917.356 0 .698-.038.927-.082v-1.41a4.509 4.509 0 01-.546.026c-.628 0-.908-.28-.908-.889v-3.148h1.454v-1.454h-1.454v-1.733H35.06z"
                    fill="#fff"
                />
            </svg>
        );
    },
    boldDarkTheme: (props: IconProps) => {
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="96" height="145" viewBox="0 0 96 145" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="96" height="144" rx="6" fill="#1C1C25" />
                <path
                    d="M0 123H96V138.176C96 141.49 93.3137 144.176 90 144.176H6C2.68629 144.176 0 141.49 0 138.176V123Z"
                    fill="black"
                />
                <ellipse cx="80.5714" cy="104.188" rx="8.57143" ry="8.47059" fill={fg} />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M80.5714 99.9529C80.098 99.9529 79.7143 100.337 79.7143 100.81V103.341H77.1328C76.665 103.341 76.2858 103.72 76.2858 104.188C76.2858 104.656 76.665 105.035 77.1328 105.035H79.7143V107.566C79.7143 108.04 80.098 108.424 80.5714 108.424C81.0448 108.424 81.4286 108.04 81.4286 107.566V105.035H84.0101C84.478 105.035 84.8572 104.656 84.8572 104.188C84.8572 103.72 84.478 103.341 84.0101 103.341H81.4286V100.81C81.4286 100.337 81.0448 99.9529 80.5714 99.9529Z"
                    fill="white"
                />
                <ellipse cx="11.1429" cy="45.7412" rx="4.28571" ry="4.23529" fill="#4F5DFF" />
                <path
                    d="M69.4286 42.7765C69.4286 38.8 66.205 35.5765 62.2286 35.5765H28.8571C23.3343 35.5765 18.8571 40.0536 18.8571 45.5765V49.9765H62.2286C66.205 49.9765 69.4286 46.7529 69.4286 42.7765Z"
                    fill="#353547"
                />
                <ellipse cx="84.8571" cy="67.7647" rx="4.28571" ry="4.23529" fill="#E14421" />
                <path
                    d="M26.5714 62.6823C26.5714 57.5364 30.7431 53.3647 35.8891 53.3647H67.1428C72.6657 53.3647 77.1428 57.8419 77.1428 63.3647V72H35.8891C30.7431 72 26.5714 67.8283 26.5714 62.6823Z"
                    fill={fg}
                />
                <g clipPath="url(#clip0)">
                    <path
                        d="M76.2858 130.293V136.453L79.0131 134.913L82.13 136.453L84.8572 134.913V128.753L82.13 130.293L79.0131 128.753L76.2858 130.293Z"
                        fill="#353547"
                    />
                </g>
                <g clipPath="url(#clip1)">
                    <path
                        d="M12 131.421L14.7127 128.64C15.1051 128.238 15.752 128.238 16.1444 128.64L18.8571 131.421V135.614C18.8571 135.816 18.7768 136.01 18.6339 136.153C18.4911 136.296 18.2973 136.376 18.0952 136.376H12.7619C12.5598 136.376 12.366 136.296 12.2231 136.153C12.0802 136.01 12 135.816 12 135.614V131.421Z"
                        fill="#353547"
                    />
                </g>
                <g clipPath="url(#clip2)">
                    <path
                        d="M47.9999 128.753C45.7353 128.753 43.8928 130.253 43.8928 132.097C43.9071 132.994 44.3221 133.839 45.0267 134.405L44.2053 136.024C44.161 136.111 44.1967 136.217 44.2849 136.261C44.3339 136.285 44.3914 136.286 44.4406 136.263L46.6367 135.253C47.0799 135.378 47.5389 135.442 47.9999 135.441C50.2646 135.441 52.1071 133.941 52.1071 132.097C52.1071 130.253 50.2646 128.753 47.9999 128.753Z"
                        fill={fg}
                    />
                </g>
                <path d="M0 6C0 2.68629 2.68629 0 6 0H90C93.3137 0 96 2.68629 96 6V27.1059H0V6Z" fill={fg} />
                <path
                    d="M11.2324 22.1987C13.1016 22.1987 14.4658 21.0654 14.6001 19.4111H13.021C12.8652 20.2651 12.167 20.813 11.2378 20.813C10.0132 20.813 9.25586 19.7817 9.25586 18.1221C9.25586 16.4624 10.0132 15.4312 11.2324 15.4312C12.1562 15.4312 12.8599 16.022 13.0156 16.9189H14.5947C14.4766 15.2539 13.0693 14.0508 11.2324 14.0508C8.98193 14.0508 7.59619 15.603 7.59619 18.1221C7.59619 20.6411 8.9873 22.1987 11.2324 22.1987ZM15.9912 22H17.5542V18.5947C17.5542 17.8105 18.0054 17.2788 18.7896 17.2788C19.4985 17.2788 19.8799 17.7085 19.8799 18.5464V22H21.4429V18.1919C21.4429 16.7739 20.6855 15.9736 19.4126 15.9736C18.5425 15.9736 17.8979 16.3818 17.624 17.0532H17.5273V13.8252H15.9912V22ZM24.585 22.0913C25.353 22.0913 25.9976 21.7637 26.3037 21.2158H26.4004V22H27.915V17.9609C27.915 16.6987 27.0288 15.9575 25.4604 15.9575C23.9727 15.9575 22.9683 16.6396 22.8662 17.7246H24.3057C24.4346 17.354 24.8105 17.1553 25.3745 17.1553C26.019 17.1553 26.3735 17.4507 26.3735 17.9609V18.4497L24.9395 18.5356C23.4678 18.6216 22.646 19.2446 22.646 20.3135C22.646 21.3931 23.4463 22.0913 24.585 22.0913ZM25.1006 20.9473C24.5581 20.9473 24.1768 20.6733 24.1768 20.2168C24.1768 19.7817 24.5044 19.5293 25.1758 19.4863L26.3735 19.4058V19.8462C26.3735 20.4692 25.8257 20.9473 25.1006 20.9473ZM29.897 14.6738V16.1401H28.9731V17.3271H29.897V20.4048C29.897 21.5542 30.4663 22.0215 31.9058 22.0215C32.2065 22.0215 32.4966 21.9893 32.6899 21.9517V20.7969C32.5396 20.813 32.4321 20.8237 32.228 20.8237C31.6963 20.8237 31.46 20.5767 31.46 20.0449V17.3271H32.6899V16.1401H31.46V14.6738H29.897Z"
                    fill="white"
                />
                <defs>
                    <clipPath id="clip0">
                        <rect x="76.2858" y="127.906" width="8.57143" height="8.47059" fill="white" />
                    </clipPath>
                    <clipPath id="clip1">
                        <rect x="11.1428" y="127.906" width="8.57143" height="8.47059" fill="white" />
                    </clipPath>
                    <clipPath id="clip2">
                        <rect x="43.7142" y="127.906" width="8.57143" height="8.47059" fill="white" />
                    </clipPath>
                </defs>
            </svg>
        );
    },
    pureBlackTheme: () => {
        return (
            <svg width="112" height="170" viewBox="0 0 112 170" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="112" height="170" rx="6" fill="#000" />
                <circle cx="94" cy="123" r="10" fill="#fff" />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M94 118a1 1 0 00-1 1v3h-3a1 1 0 000 2h3v3a1 1 0 002 0v-3h3a1 1 0 000-2h-3v-3a1 1 0 00-1-1z"
                    fill="#000"
                />
                <circle cx="13" cy="54" r="5" fill="#4F5DFF" />
                <path
                    d="M81 50.5a8.5 8.5 0 00-8.5-8.5H32c-5.523 0-10 4.477-10 10v7h50.5a8.5 8.5 0 008.5-8.5z"
                    fill="#353547"
                />
                <circle cx="99" cy="80" r="5" fill="#E14421" />
                <path
                    d="M31 73c0-5.523 4.477-10 10-10h39c5.523 0 10 4.477 10 10v12H41c-5.523 0-10-4.477-10-10v-2z"
                    fill="#fff"
                />
                <path
                    d="M89 153.818v7.273l3.182-1.818 3.636 1.818L99 159.273V152l-3.182 1.818L92.182 152 89 153.818zM14 155.15l3.28-3.403a1 1 0 011.44 0L22 155.15v4.95a.903.903 0 01-.26.636.883.883 0 01-.629.264H14.89a.883.883 0 01-.629-.264.903.903 0 01-.26-.636v-4.95z"
                    fill="#4B4B58"
                />
                <path
                    d="M56 152c-2.642 0-4.792 1.771-4.792 3.948a3.585 3.585 0 001.323 2.725l-.958 1.911a.207.207 0 00.093.279c.057.029.124.03.181.003l2.563-1.193a5.668 5.668 0 001.59.222c2.642 0 4.792-1.77 4.792-3.947 0-2.178-2.15-3.948-4.792-3.948zM13.002 26.235c2.209 0 3.821-1.365 3.98-3.345h-1.866c-.184 1.009-1.01 1.656-2.108 1.656-1.447 0-2.342-1.2-2.342-3.129 0-1.93.895-3.123 2.336-3.123 1.092 0 1.923.692 2.107 1.746h1.867c-.14-1.994-1.803-3.434-3.974-3.434-2.66 0-4.297 1.834-4.297 4.811 0 2.983 1.644 4.818 4.297 4.818zM18.626 26h1.847v-4.018c0-.908.533-1.523 1.46-1.523.838 0 1.289.501 1.289 1.472V26h1.847v-4.507c0-1.682-.895-2.628-2.4-2.628-1.028 0-1.79.483-2.113 1.283h-.115v-3.822h-1.815V26zM29.392 24.718c-.642 0-1.092-.318-1.092-.838 0-.502.387-.787 1.18-.844l1.416-.09v.515c0 .71-.648 1.257-1.504 1.257zm-.61 1.39c.908 0 1.67-.38 2.032-1.022h.114V26h1.79v-4.78c0-1.498-1.048-2.374-2.901-2.374-1.758 0-2.945.813-3.066 2.108h1.701c.152-.42.597-.648 1.263-.648.762 0 1.181.33 1.181.914v.572l-1.695.101c-1.74.102-2.71.838-2.71 2.107 0 1.283.945 2.108 2.291 2.108zM35.06 17.335v1.733h-1.092v1.454h1.092v3.586c0 1.365.673 1.917 2.374 1.917.356 0 .698-.038.927-.082v-1.41a4.509 4.509 0 01-.546.026c-.628 0-.908-.28-.908-.889v-3.148h1.454v-1.454h-1.454v-1.733H35.06z"
                    fill="#fff"
                />
            </svg>
        );
    },
    pureWhiteTheme: () => {
        return (
            <svg width="112" height="170" viewBox="0 0 112 170" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x=".5" y=".5" width="111" height="169" rx="5.5" fill="#fff" stroke="#D8D8E3" />
                <circle cx="94" cy="123" r="10" fill="#000" />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M94 118a1 1 0 00-1 1v3h-3a1 1 0 000 2h3v3a1 1 0 002 0v-3h3a1 1 0 000-2h-3v-3a1 1 0 00-1-1z"
                    fill="#fff"
                />
                <circle cx="13" cy="54" r="5" fill="#4F5DFF" />
                <path
                    d="M81 50.5a8.5 8.5 0 00-8.5-8.5H32c-5.523 0-10 4.477-10 10v7h50.5a8.5 8.5 0 008.5-8.5z"
                    fill="#EDEDF3"
                />
                <circle cx="99" cy="80" r="5" fill="#E14421" />
                <path
                    d="M31 73c0-5.523 4.477-10 10-10h39c5.523 0 10 4.477 10 10v12H41c-5.523 0-10-4.477-10-10v-2z"
                    fill="#000"
                />
                <path
                    d="M89 153.818v7.273l3.182-1.818 3.636 1.818L99 159.273V152l-3.182 1.818L92.182 152 89 153.818zM14 155.15l3.28-3.403a1 1 0 011.44 0L22 155.15v4.95a.903.903 0 01-.26.636.883.883 0 01-.629.264H14.89a.883.883 0 01-.629-.264.903.903 0 01-.26-.636v-4.95z"
                    fill="#D5D5D9"
                />
                <path
                    d="M56 152c-2.642 0-4.792 1.771-4.792 3.948a3.585 3.585 0 001.323 2.725l-.958 1.911a.207.207 0 00.093.279c.057.029.124.03.181.003l2.563-1.193a5.668 5.668 0 001.59.222c2.642 0 4.792-1.77 4.792-3.947 0-2.178-2.15-3.948-4.792-3.948zM13.002 26.235c2.209 0 3.821-1.365 3.98-3.345h-1.866c-.184 1.009-1.01 1.656-2.108 1.656-1.447 0-2.342-1.2-2.342-3.129 0-1.93.895-3.123 2.336-3.123 1.092 0 1.923.692 2.107 1.746h1.867c-.14-1.994-1.803-3.434-3.974-3.434-2.66 0-4.297 1.834-4.297 4.811 0 2.983 1.644 4.818 4.297 4.818zM18.626 26h1.847v-4.018c0-.908.533-1.523 1.46-1.523.838 0 1.289.501 1.289 1.472V26h1.847v-4.507c0-1.682-.895-2.628-2.4-2.628-1.028 0-1.79.483-2.113 1.283h-.115v-3.822h-1.815V26zM29.392 24.718c-.642 0-1.092-.318-1.092-.838 0-.502.387-.787 1.18-.844l1.416-.09v.515c0 .71-.648 1.257-1.504 1.257zm-.61 1.39c.908 0 1.67-.38 2.032-1.022h.114V26h1.79v-4.78c0-1.498-1.048-2.374-2.901-2.374-1.758 0-2.945.813-3.066 2.108h1.701c.152-.42.597-.648 1.263-.648.762 0 1.181.33 1.181.914v.572l-1.695.101c-1.74.102-2.71.838-2.71 2.107 0 1.283.945 2.108 2.291 2.108zM35.06 17.335v1.733h-1.092v1.454h1.092v3.586c0 1.365.673 1.917 2.374 1.917.356 0 .698-.038.927-.082v-1.41a4.509 4.509 0 01-.546.026c-.628 0-.908-.28-.908-.889v-3.148h1.454v-1.454h-1.454v-1.733H35.06z"
                    fill="#000"
                />
            </svg>
        );
    },
    addNewSmall: (props: IconProps) => {
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="10" r="10" fill={fg} />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10 4C9.44772 4 9 4.44772 9 5V9H5C4.44772 9 4 9.44771 4 10C4 10.5523 4.44771 11 5 11H9V15C9 15.5523 9.44772 16 10 16C10.5523 16 11 15.5523 11 15V11H15C15.5523 11 16 10.5523 16 10C16 9.44772 15.5523 9 15 9H11V5C11 4.44772 10.5523 4 10 4Z"
                    fill="white"
                />
            </svg>
        );
    },
    addNewLarge: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="40" height="40" rx="20" fill={bg} />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M20.0002 13.3334C19.4742 13.3334 19.0478 13.7598 19.0478 14.2858V19.0477H14.2859C13.7599 19.0477 13.3335 19.4741 13.3335 20C13.3335 20.526 13.7599 20.9524 14.2859 20.9524H19.0478V25.7143C19.0478 26.2403 19.4742 26.6667 20.0002 26.6667C20.5261 26.6667 20.9525 26.2403 20.9525 25.7143V20.9524H25.7144C26.2404 20.9524 26.6668 20.526 26.6668 20C26.6668 19.4741 26.2404 19.0477 25.7144 19.0477H20.9525V14.2858C20.9525 13.7598 20.5261 13.3334 20.0002 13.3334Z"
                    fill="white"
                />
            </svg>
        );
    },
    cancelX: (props: IconProps) => {
        const bgColor = props.bgColor ?? "currentColor";
        return (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M11.3957 1.81286C11.7295 1.47912 11.7295 0.938023 11.3957 0.604285C11.062 0.270548 10.5209 0.270548 10.1871 0.604286L6 4.79143L1.81286 0.604285C1.47912 0.270547 0.938023 0.270548 0.604286 0.604286C0.270548 0.938023 0.270548 1.47912 0.604286 1.81286L4.79143 6L0.604285 10.1871C0.270547 10.5209 0.270548 11.062 0.604285 11.3957C0.938023 11.7295 1.47912 11.7295 1.81286 11.3957L6 7.20857L10.1871 11.3957C10.5209 11.7295 11.062 11.7295 11.3957 11.3957C11.7295 11.062 11.7295 10.5209 11.3957 10.1871L7.20857 6L11.3957 1.81286Z"
                    fill={bgColor}
                />
            </svg>
        );
    },
    basicText: (props: IconProps) => {
        const fg = props.fgColor ?? "white";
        const bg = props.bgColor ?? "currentColor";
        const accent = props.accentColor ?? "#7f7f7f";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="6" width="20" height="11" rx="2" fill={bg} />
                <rect x="5" y="12" width="13" height="2" rx="0.5" fill={accent} />
                <rect x="5" y="9" width="10" height="2" rx="0.5" fill={fg} />
            </svg>
        );
    },
    richText: (props: IconProps) => {
        const fg = props.fgColor ?? "white";
        const bg = props.bgColor ?? "currentColor";
        const accent = props.accentColor ?? "#7f7f7f";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M20.5577 6H3.4423C2.64701 6 2 6.64701 2 7.4423V17.0577C2 17.853 2.64701 18.5 3.4423 18.5H20.5577C21.353 18.5 22 17.853 22 17.0577V7.4423C22 6.64701 21.353 6 20.5577 6Z"
                    fill={bg}
                />
                <path d="M16.9822 16.6226L13.875 12.25H15.75V8.5H18.25V12.25H20.125L16.9822 16.6226Z" fill={accent} />
                <path
                    d="M13.25 15.9976L10.75 16V12.25L8.875 14.6538L7 12.25V16H4.5V8.5H7L8.875 11L10.75 8.5L13.25 8.49756V15.9976Z"
                    fill={fg}
                />
            </svg>
        );
    },
    separator: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect y="11" width="24" height="2" rx="1" fill={bg} />
            </svg>
        );
    },
    title: (props: IconProps) => {
        const fg = props.fgColor ?? "white";
        const bg = props.bgColor ?? "currentColor";
        const accent = props.accentColor ?? "#7f7f7f";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="2" width="20" height="20" rx="2" fill={bg} />
                <rect x="5" y="17" width="14" height="2" rx="0.5" fill={accent} />
                <rect x="5" y="14" width="11" height="2" rx="0.5" fill={fg} />
            </svg>
        );
    },
    audio: (props: IconProps) => {
        const fg = props.fgColor ?? "white";
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="2" width="20" height="20" rx="2" fill={bg} />
                <path
                    d="M7 15.3333V12C7 9.23858 9.23858 7 12 7C14.7614 7 17 9.23858 17 12V15.3333"
                    stroke={fg}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6 13.1111V15.8889C6 17.0548 6.94518 18 8.11111 18H8.66667C9.8326 18 10.7778 17.0548 10.7778 15.8889V14.2222C10.7778 13.0563 9.8326 12.1111 8.66667 12.1111H7C6.44772 12.1111 6 12.5588 6 13.1111ZM15.8889 18C17.0548 18 18 17.0548 18 15.8889V13.1111C18 12.5588 17.5523 12.1111 17 12.1111H15.3333C14.1674 12.1111 13.2222 13.0563 13.2222 14.2222V15.8889C13.2222 17.0548 14.1674 18 15.3333 18H15.8889Z"
                    fill={fg}
                />
            </svg>
        );
    },
    image: (props: IconProps) => {
        const fg = props.fgColor ?? "white";
        const bg = props.bgColor ?? "currentColor";
        const accent = props.accentColor ?? "#7f7f7f";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="2" width="20" height="20" rx="2" fill={bg} />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M15.0823 12.8347C15.2799 12.5346 15.72 12.5346 15.9176 12.8347L19.3401 18.0342C19.559 18.3666 19.3205 18.8091 18.9225 18.8091H12.0774C11.6794 18.8091 11.4409 18.3666 11.6598 18.0342L15.0823 12.8347Z"
                    fill={accent}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.76566 10.5998C9.96424 10.3098 10.3922 10.3098 10.5908 10.5998L15.6763 18.0266C15.9035 18.3584 15.6659 18.8091 15.2638 18.8091H5.09264C4.69047 18.8091 4.45287 18.3584 4.68009 18.0266L9.76566 10.5998Z"
                    fill={fg}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M16.5 10C17.8807 10 19 8.88071 19 7.5C19 6.11929 17.8807 5 16.5 5C15.1193 5 14 6.11929 14 7.5C14 8.88071 15.1193 10 16.5 10Z"
                    fill={fg}
                />
            </svg>
        );
    },
    mapComponent: (props: IconProps) => {
        const fg = props.fgColor ?? "white";
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="2" width="20" height="20" rx="2" fill={bg} />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12 19.3334C12 19.3334 18 15.3334 18 10.6667C18 7.35298 15.3137 4.66669 12 4.66669C8.68629 4.66669 6 7.35298 6 10.6667C6 15.3334 12 19.3334 12 19.3334ZM14 10.6667C14 11.7713 13.1046 12.6667 12 12.6667C10.8954 12.6667 10 11.7713 10 10.6667C10 9.56212 10.8954 8.66669 12 8.66669C13.1046 8.66669 14 9.56212 14 10.6667Z"
                    fill={fg}
                />
            </svg>
        );
    },
    video: (props: IconProps) => {
        const fg = props.fgColor ?? "white";
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="2" width="20" height="20" rx="2" fill={bg} />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M17.7333 6.94669C18.3721 7.12741 18.8654 7.63601 19.0266 8.28002C19.2381 9.45249 19.3408 10.642 19.3333 11.8334C19.3375 13.0069 19.2349 14.1784 19.0266 15.3334C18.8654 15.9774 18.3721 16.486 17.7333 16.6667C16.5866 16.9734 12 16.9734 12 16.9734C12 16.9734 7.41331 16.9734 6.26664 16.6667C5.64133 16.4956 5.15087 16.0102 4.97331 15.3867C4.76182 14.2142 4.65916 13.0247 4.66664 11.8334C4.6608 10.6509 4.76345 9.47039 4.97331 8.30669C5.1345 7.66268 5.62784 7.15408 6.26664 6.97335C7.41331 6.66669 12 6.66669 12 6.66669C12 6.66669 16.5866 6.66669 17.7333 6.94669ZM14.8958 11.6534L10.4998 14.6534V8.65338L14.8958 11.6534Z"
                    fill={fg}
                />
            </svg>
        );
    },
    button: (props: IconProps) => {
        const fg = props.fgColor ?? "white";
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="7" width="20" height="10" rx="2" fill={bg} />
                <rect x="9" y="11" width="6" height="2" rx="0.5" fill={fg} />
            </svg>
        );
    },
    editButton: (props: IconProps) => {
        const fg = props.fgColor ?? "#737383";
        return (
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M7.53915 9.25242L13.6791 23.9324C14.0291 24.7624 15.2091 24.7424 15.5291 23.9124L17.5991 18.5724C17.6991 18.3124 17.9091 18.1024 18.1691 18.0024L23.4991 15.9424C24.3391 15.6224 24.3591 14.4324 23.5291 14.0824L8.85915 7.93242C8.02915 7.60242 7.19915 8.43242 7.53915 9.25242Z"
                    fill={fg}
                />
            </svg>
        );
    },
    playButton: (props: IconProps) => {
        const fg = props.fgColor ?? "#737383";
        return (
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M16 6C10.48 6 6 10.48 6 16C6 21.52 10.48 26 16 26C21.52 26 26 21.52 26 16C26 10.48 21.52 6 16 6ZM14 19.5V12.5C14 12.09 14.47 11.85 14.8 12.1L19.47 15.6C19.74 15.8 19.74 16.2 19.47 16.4L14.8 19.9C14.47 20.15 14 19.91 14 19.5Z"
                    fill={fg}
                />
            </svg>
        );
    },
    undo: (props: IconProps) => {
        const fg = props.fgColor ?? "#737383";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M12.233 8.87934C9.90278 8.87934 7.79238 9.74988 6.16561 11.1656L3 8V15.914H10.914L7.73083 12.7308C8.9531 11.7108 10.5095 11.0777 12.233 11.0777C15.3459 11.0777 17.9927 13.1089 18.916 15.914L21 15.2281C19.7777 11.5437 16.3219 8.87934 12.233 8.87934Z"
                    fill={fg}
                />
            </svg>
        );
    },
    redo: (props: IconProps) => {
        const fg = props.fgColor ?? "#737383";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M17.8328 10.6C16.2053 8.99 14.0938 8 11.7625 8C7.67155 8 4.21408 11.03 3 15.22L5.07625 16C6 12.81 8.6393 10.5 11.7625 10.5C13.478 10.5 15.044 11.22 16.2669 12.38L13.0821 16H21V7L17.8328 10.6Z"
                    fill={fg}
                />
            </svg>
        );
    },
    email: (props: IconProps) => {
        const fg = props.fgColor ?? "white";
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="4" width="20" height="16" rx="2" fill={bg} />
                <path
                    d="M5 7.5L12.0363 13.5L19.3324 7.5"
                    stroke={fg}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        );
    },
    formButton: (props: IconProps) => {
        const fg = props.fgColor ?? "white";
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="7" width="20" height="10" rx="2" fill={bg} />
                <rect x="9" y="11" width="6" height="2" rx="0.5" fill={fg} />
            </svg>
        );
    },
    link: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M10.5 12.75C11.1517 13.6212 12.1497 14.1665 13.235 14.2442C14.3203 14.3219 15.3858 13.9245 16.155 13.155L18.405 10.905C19.8261 9.43355 19.8058 7.09466 18.3593 5.64816C16.9128 4.20165 14.5739 4.18133 13.1025 5.60247L11.8125 6.88497"
                    stroke={bg}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                />
                <path
                    d="M13.5 11.25C12.8483 10.3787 11.8503 9.83347 10.765 9.75575C9.67974 9.67802 8.61422 10.0755 7.845 10.845L5.595 13.095C4.17385 14.5664 4.19418 16.9053 5.64068 18.3518C7.08718 19.7983 9.42608 19.8186 10.8975 18.3975L12.18 17.115"
                    stroke={bg}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                />
            </svg>
        );
    },
    phone: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M20.2401 15.1001V17.6001C20.242 18.0694 20.046 18.5177 19.7002 18.835C19.3544 19.1522 18.8908 19.309 18.4235 19.2668C15.8592 18.9881 13.396 18.1119 11.2318 16.7084C9.21831 15.429 7.51124 13.7219 6.23179 11.7084C4.82344 9.53444 3.94699 7.05926 3.67346 4.48344C3.63135 4.0175 3.78702 3.55531 4.10243 3.20979C4.41784 2.86426 4.86395 2.66721 5.33179 2.66677H7.83179C8.66834 2.65854 9.38137 3.27174 9.49846 4.1001C9.60398 4.90016 9.79966 5.68571 10.0818 6.44177C10.3107 7.05083 10.1643 7.73744 9.70679 8.2001L8.64846 9.25844C9.83475 11.3447 11.5622 13.0721 13.6485 14.2584L14.7068 13.2001C15.1695 12.7426 15.8561 12.5962 16.4651 12.8251C17.2212 13.1072 18.0067 13.3029 18.8068 13.4084C19.6448 13.5267 20.2611 14.2541 20.2401 15.1001Z"
                    fill={bg}
                />
            </svg>
        );
    },
    relation: (props: IconProps) => {
        const fg = props.fgColor ?? "white";
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="9" width="12" height="10" rx="2" fill={bg} />
                <rect x="9" y="5" width="12" height="10" rx="2" fill={bg} />
                <path
                    d="M12.6111 9.07179V7L16.1818 10.6256L12.6111 14.2513V12.1277C10.0606 12.1277 8.27526 12.9564 7.00001 14.7692C7.51011 12.1795 9.04041 9.58974 12.6111 9.07179Z"
                    fill={fg}
                />
            </svg>
        );
    },
    switch: (props: IconProps) => {
        const fg = props.fgColor ?? "white";
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M2 12C2 8.68629 4.68629 6 8 6H16C19.3137 6 22 8.68629 22 12C22 15.3137 19.3137 18 16 18H8C4.68629 18 2 15.3137 2 12Z"
                    fill={bg}
                />
                <circle cx="16" cy="12" r="4" fill={fg} />
            </svg>
        );
    },
    thumbsUp: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M10.7898 4.73552L8.1823 10.417C8.06218 10.6787 8 10.9633 8 11.2512V18C8 19.1046 8.89543 20 10 20H17V19.9985C18.1709 19.9585 19.1623 19.1075 19.3732 17.9472L20.4641 11.9472C20.7432 10.4126 19.5642 9 18.0045 9H17.5867L17.5856 9H14.3309C14.0251 9 13.7909 8.72797 13.8364 8.4256L14.1156 6.57082C14.1156 6.19932 14.0424 5.83146 13.9002 5.48824C13.758 5.14502 13.5497 4.83316 13.287 4.57047C13.0243 4.30778 12.7124 4.0994 12.3692 3.95724C11.7402 3.69669 11.0943 4.12656 10.7898 4.73552Z"
                    fill={bg}
                />
                <rect x="3" y="10" width="4" height="10" rx="1.5" fill={bg} />
            </svg>
        );
    },
    reactionComponent: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12ZM8.74994 10.5C9.4403 10.5 9.99994 9.94036 9.99994 9.25C9.99994 8.55964 9.4403 8 8.74994 8C8.05959 8 7.49994 8.55964 7.49994 9.25C7.49994 9.94036 8.05959 10.5 8.74994 10.5ZM17.099 13.5358C17.4935 13.7638 17.6284 14.2685 17.4003 14.6629C16.8526 15.6103 16.0652 16.397 15.1173 16.9438C14.1694 17.4907 13.0944 17.7786 12 17.7786C10.9057 17.7786 9.8306 17.4908 8.8827 16.9439C7.9348 16.397 7.14745 15.6104 6.59969 14.663C6.37163 14.2686 6.50651 13.7639 6.90096 13.5359C7.29541 13.3078 7.80006 13.4427 8.02812 13.8371C8.43099 14.5339 9.01008 15.1125 9.70725 15.5147C10.4044 15.9169 11.1951 16.1286 12 16.1286C12.8049 16.1286 13.5956 15.9169 14.2928 15.5147C14.9899 15.1124 15.569 14.5339 15.9719 13.8371C16.1999 13.4426 16.7046 13.3077 17.099 13.5358ZM16.4999 9.25C16.4999 9.94036 15.9403 10.5 15.2499 10.5C14.5596 10.5 13.9999 9.94036 13.9999 9.25C13.9999 8.55964 14.5596 8 15.2499 8C15.9403 8 16.4999 8.55964 16.4999 9.25Z"
                    fill={bg}
                />
            </svg>
        );
    },
    rating: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.18879 19.4805C6.05451 20.0427 6.66605 20.4874 7.15955 20.1863C8.6015 19.3066 10.0435 18.4268 11.4854 17.5471C11.8052 17.352 12.2073 17.352 12.5271 17.5471L16.6478 20.0612C17.2172 20.4086 17.9228 19.8955 17.7679 19.2468L16.6482 14.5581C16.5611 14.1931 16.6856 13.81 16.9708 13.566L20.6349 10.4308C21.142 9.99693 20.8724 9.16661 20.2072 9.11334L15.396 8.72806C15.0224 8.69813 14.6968 8.46172 14.5527 8.11564L12.6986 3.66288C12.4423 3.04727 11.5702 3.04727 11.3139 3.66288L10.758 4.9979L10.7562 5.00215L9.45982 8.11558C9.31571 8.46165 8.99015 8.69806 8.61647 8.72799L3.56546 9.13248C2.98894 9.17865 2.7553 9.89826 3.19476 10.2743L3.37001 10.4242C3.37252 10.4264 3.37505 10.4286 3.37759 10.4308L7.04174 13.566C7.29079 13.7791 7.41732 14.0984 7.38728 14.419C7.38293 14.4654 7.37529 14.5119 7.36426 14.5581M12.1986 6.75442C12.1566 6.6535 12.0063 6.68354 12.0063 6.79286V15.6007C12.0063 15.6836 12.0735 15.7503 12.1562 15.755C12.5839 15.7792 13.007 15.907 13.3865 16.1385L15.3528 17.3383C15.4667 17.4078 15.6078 17.3051 15.5769 17.1754L15.0434 14.9414C14.8124 13.9741 15.1424 12.9589 15.898 12.3124L17.6423 10.8199C17.7437 10.7331 17.6898 10.567 17.5567 10.5564L15.2643 10.3728C14.2741 10.2935 13.4113 9.667 13.0295 8.7499L12.1986 6.75442Z"
                    fill={bg}
                />
            </svg>
        );
    },
    ratingStar: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M15.7033 5.81931C15.5573 5.42731 15.184 5.16731 14.766 5.16597H10.708C10.5667 5.16597 10.44 5.07664 10.3933 4.94331L8.93667 0.814639C8.74333 0.297305 8.16733 0.0346388 7.65 0.227972C7.37867 0.329305 7.16467 0.543305 7.06333 0.814639L7.06 0.825306L5.60667 4.94331C5.56 5.07664 5.434 5.16597 5.29267 5.16597H1.23333C0.681333 5.16531 0.232666 5.61264 0.232666 6.16531C0.232666 6.46197 0.363999 6.74331 0.591333 6.93331L4.04733 9.79997C4.15333 9.88797 4.194 10.0313 4.15067 10.162L2.69867 14.516C2.524 15.04 2.80733 15.606 3.33133 15.7806C3.63933 15.8833 3.97733 15.83 4.23867 15.6386L7.80267 13.0253C7.92 12.9393 8.07933 12.9393 8.19667 13.0253L11.7593 15.638C12.204 15.9653 12.83 15.87 13.1573 15.4253C13.35 15.1633 13.4033 14.824 13.3007 14.516L11.8487 10.1593C11.8053 10.0286 11.846 9.88531 11.952 9.79731L15.4147 6.92664C15.7333 6.65464 15.8487 6.21264 15.7033 5.81931Z"
                    fill={bg}
                />
            </svg>
        );
    },
    ratingHeart: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M15.5334 3.38401C14.426 1.22801 11.7814 0.378011 9.62538 1.48534C9.23738 1.68468 8.88138 1.94001 8.56938 2.24401L8.00005 2.76468L7.44872 2.26268C6.45338 1.27268 5.04072 0.824011 3.65672 1.05868C2.28072 1.27334 1.09272 2.13868 0.466717 3.38334C-0.379284 5.04134 -0.0526168 7.05668 1.27338 8.36268L7.52205 14.8C7.77872 15.064 8.20072 15.07 8.46472 14.8133C8.46938 14.8087 8.47338 14.8047 8.47805 14.8L14.718 8.37468C16.05 7.06734 16.38 5.04734 15.5334 3.38401Z"
                    fill={bg}
                />
            </svg>
        );
    },
    ratingMoney: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0)">
                    <path
                        d="M8 0C3.582 0 0 3.582 0 8C0 12.418 3.582 16 8 16C12.418 16 16 12.418 16 8C16 3.582 12.418 0 8 0ZM8.976 12.3913C8.894 12.4033 8.83333 12.474 8.83333 12.5567V13.0667C8.83333 13.5267 8.46 13.9 8 13.9C7.54 13.9 7.16667 13.5267 7.16667 13.0667V12.5967C7.16733 12.5047 7.09333 12.4307 7.00133 12.43C7.00067 12.43 7.00067 12.43 7 12.43H6.23533C5.77533 12.43 5.402 12.0567 5.402 11.5967C5.402 11.1367 5.77533 10.7633 6.23533 10.7633H8.62733C9.03867 10.7633 9.37267 10.4293 9.37267 10.018C9.37267 9.71333 9.18733 9.43933 8.904 9.326L6.47733 8.35467C5.24333 7.86533 4.64 6.46867 5.12933 5.23467C5.44733 4.43267 6.17067 3.86333 7.024 3.74133C7.106 3.72933 7.16667 3.65933 7.16667 3.57667V3.06667C7.16667 2.60667 7.54 2.23333 8 2.23333C8.46 2.23333 8.83333 2.60667 8.83333 3.06667V3.53733C8.83333 3.62933 8.908 3.704 9 3.704H9.76467C10.2247 3.704 10.598 4.07733 10.598 4.53733C10.598 4.99733 10.2247 5.37067 9.76467 5.37067H7.37267C6.96133 5.37 6.62733 5.70267 6.62667 6.114C6.626 6.42 6.812 6.69467 7.096 6.80733L9.52267 7.77867C10.7567 8.268 11.36 9.66467 10.8707 10.8987C10.5527 11.7007 9.83 12.27 8.976 12.3913Z"
                        fill={bg}
                    />
                </g>
                <defs>
                    <clipPath id="clip0">
                        <rect width="16" height="16" fill="white" />
                    </clipPath>
                </defs>
            </svg>
        );
    },
    ratingTime: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0)">
                    <path
                        d="M8 0C3.582 0 0 3.582 0 8C0 12.418 3.582 16 8 16C12.418 16 16 12.418 16 8C15.9953 3.584 12.416 0.00466667 8 0ZM11.4667 11.4807C11.2153 11.7467 10.798 11.7647 10.5247 11.522L7.21867 8.49133C7.08 8.36533 7.00067 8.18733 7 8V4.33333C7 3.96533 7.29867 3.66667 7.66667 3.66667C8.03467 3.66667 8.33333 3.96533 8.33333 4.33333V7.70667L11.4227 10.5387C11.6953 10.7867 11.7153 11.208 11.4673 11.4807C11.4673 11.4807 11.4667 11.4813 11.4667 11.4807Z"
                        fill={bg}
                    />
                </g>
                <defs>
                    <clipPath id="clip0">
                        <rect width="16" height="16" fill="white" />
                    </clipPath>
                </defs>
            </svg>
        );
    },
    ratingPizza: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M9.23064 6.63868C9.36264 6.69601 9.51664 6.66601 9.61664 6.56201L12.346 4.34201C12.4886 4.22601 12.51 4.01601 12.394 3.87335C12.3906 3.86935 12.3873 3.86468 12.3833 3.86068C11.5706 3.07335 10.502 2.60401 9.37264 2.53801C9.1893 2.52335 9.02864 2.66068 9.01464 2.84401C9.0133 2.85335 9.0133 2.86201 9.0133 2.87135V6.31601C9.01197 6.45801 9.09797 6.58668 9.23064 6.63868ZM9.3373 1.57935C10.7153 1.65335 12.0193 2.22801 13.004 3.19468C13.1293 3.32935 13.34 3.33735 13.4753 3.21201C13.4813 3.20668 13.4873 3.20068 13.4926 3.19468L14.0533 2.58735C14.128 2.50601 14.166 2.39735 14.158 2.28668C14.1493 2.17868 14.102 2.07735 14.0246 2.00135C12.8133 0.746013 11.1506 0.0260127 9.40664 0.00134599C9.30397 -0.00132068 9.2053 0.0386793 9.1333 0.112013C9.0553 0.187346 9.01197 0.291346 9.0133 0.400013V1.24601C9.0133 1.42668 9.15664 1.57401 9.3373 1.57935ZM13.772 5.00001C13.666 4.84935 13.4586 4.81268 13.308 4.91868C13.3013 4.92335 13.2946 4.92868 13.288 4.93335L12.5906 5.49668C12.456 5.60535 12.4273 5.79935 12.524 5.94268C14.2066 8.44201 13.544 11.832 11.0446 13.5147C8.5453 15.1973 5.1553 14.5347 3.47264 12.0353C1.78997 9.53601 2.45264 6.14601 4.95197 4.46335C5.76397 3.91668 6.7073 3.59868 7.68464 3.54268C7.8613 3.53268 7.9993 3.38668 7.9993 3.20935V2.37068C7.99664 2.26468 7.95197 2.16401 7.87464 2.09068C7.79664 2.01868 7.69264 1.98201 7.58664 1.98868C3.7253 2.22135 0.783302 5.54068 1.01664 9.40201C1.24997 13.2633 4.56864 16.2053 8.42997 15.972C12.2913 15.7387 15.2333 12.42 15 8.55868C14.9233 7.28268 14.4986 6.05201 13.772 5.00001ZM7.99997 5.00001C8.00064 4.81601 7.85197 4.66601 7.66797 4.66535C7.65864 4.66535 7.6493 4.66601 7.6393 4.66668C5.25464 4.86601 3.48264 6.96135 3.68197 9.34601C3.8813 11.7307 5.97664 13.5027 8.3613 13.3033C10.746 13.104 12.518 11.0087 12.3186 8.62401C12.2586 7.90668 12.0213 7.21601 11.6273 6.61335C11.5266 6.45935 11.32 6.41601 11.166 6.51735C11.158 6.52268 11.1506 6.52801 11.1433 6.53335L8.49997 8.60001C8.36464 8.70468 8.16997 8.68068 8.0653 8.54535C8.0233 8.49068 7.99997 8.42468 7.99997 8.35601V5.00001ZM5.66664 10.32C5.29864 10.32 4.99997 10.0213 4.99997 9.65335C4.99997 9.28535 5.29864 8.98668 5.66664 8.98668C6.03464 8.98668 6.3333 9.28535 6.3333 9.65335C6.33397 10.0213 6.03597 10.3207 5.66797 10.3213C5.6673 10.3213 5.6673 10.3213 5.66664 10.3213V10.32ZM6.3333 7.32001C5.9653 7.32001 5.66664 7.02135 5.66664 6.65335C5.66664 6.28535 5.9653 5.98668 6.3333 5.98668C6.7013 5.98668 6.99997 6.28535 6.99997 6.65335C7.00064 7.02135 6.70264 7.32068 6.33464 7.32135C6.33397 7.32135 6.33397 7.32135 6.3333 7.32135V7.32001ZM10.6666 8.98668C11.0346 8.98668 11.3333 9.28535 11.3333 9.65335C11.3333 10.0213 11.0346 10.32 10.6666 10.32C10.2986 10.32 9.99997 10.0213 9.99997 9.65335C10.0006 9.28535 10.2986 8.98801 10.6666 8.98801V8.98668ZM8.3333 10.9867C8.7013 10.9867 8.99997 11.2853 8.99997 11.6533C8.99997 12.0213 8.7013 12.32 8.3333 12.32C7.9653 12.32 7.66664 12.0213 7.66664 11.6533C7.6673 11.2853 7.9653 10.988 8.3333 10.988V10.9867Z"
                    fill={bg}
                />
            </svg>
        );
    },
    inlineList: (props: IconProps) => {
        const fg = props.fgColor ?? "white";
        const bg = props.bgColor ?? "currentColor";
        const accent = props.accentColor ?? "#7f7f7f";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="2" width="20" height="20" rx="2" fill={bg} />
                <rect x="9" y="6" width="10" height="2" rx="0.5" fill={accent} />
                <circle cx="6" cy="7" r="1" fill={fg} />
                <circle cx="6" cy="12" r="1" fill={fg} />
                <circle cx="6" cy="17" r="1" fill={fg} />
                <rect x="9" y="11" width="10" height="2" rx="0.5" fill={accent} />
                <rect x="9" y="16" width="10" height="2" rx="0.5" fill={accent} />
            </svg>
        );
    },
    choice: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4 6a2 2 0 00-2 2v7a2 2 0 002 2h16a2 2 0 002-2V8a2 2 0 00-2-2H4zm15 3h-5v1h5V9zm-1 2h-4v1h4v-1zm-4 2h5v1h-5v-1z"
                    fill={bg}
                />
            </svg>
        );
    },
    remove: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M16.3333 7.94L15.3933 7L11.6667 10.7267L7.94 7L7 7.94L10.7267 11.6667L7 15.3933L7.94 16.3333L11.6667 12.6067L15.3933 16.3333L16.3333 15.3933L12.6067 11.6667L16.3333 7.94Z"
                    fill={bg}
                />
            </svg>
        );
    },
    charts: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const accent = props.accentColor ?? "#7f7f7f";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="12" fill={accent} />
                <path
                    d="M22.3923 6C23.7131 8.2877 24.2421 10.9473 23.8973 13.5663C23.5525 16.1853 22.3532 18.6174 20.4853 20.4853C18.6174 22.3532 16.1853 23.5525 13.5663 23.8973C10.9473 24.2421 8.28771 23.7131 6.00001 22.3923C3.71231 21.0715 1.92435 19.0327 0.91345 16.5922C-0.097453 14.1517 -0.274809 11.4458 0.408887 8.89418C1.09258 6.34258 2.59912 4.08788 4.69485 2.47977C6.79058 0.871655 9.35837 3.28549e-06 12 0L12 12L22.3923 6Z"
                    fill={bg}
                />
            </svg>
        );
    },
    progressBar: (props: IconProps) => {
        const fg = props.fgColor ?? "white";
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="9" width="20" height="6" rx="3" fill={bg} />
                <path d="M3 12C3 10.8954 3.89543 10 5 10H15V14H5C3.89543 14 3 13.1046 3 12Z" fill={fg} />
            </svg>
        );
    },
    halfCircleProgressBar: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    opacity="0.4"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10 8C6.68629 8 4 10.6863 4 14C4 15.1046 3.10457 16 2 16C0.89543 16 0 15.1046 0 14C0 8.47715 4.47715 4 10 4C15.5228 4 20 8.47715 20 14C20 15.1046 19.1046 16 18 16C16.8954 16 16 15.1046 16 14C16 10.6863 13.3137 8 10 8Z"
                    fill={bg}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.4 5.77387C9.7449 6.82321 9.17384 7.95347 8.1245 8.29837C5.7269 9.08642 4 11.3441 4 14C4 15.1046 3.10457 16 2 16C0.89543 16 0 15.1046 0 14C0 9.5654 2.88524 5.8099 6.8755 4.49837C7.92484 4.15347 9.0551 4.72453 9.4 5.77387Z"
                    fill={bg}
                />
            </svg>
        );
    },
    comments: (props: IconProps) => {
        const fg = props.fgColor ?? "white";
        const bg = props.bgColor ?? "currentColor";
        const accent = props.accentColor ?? "#7f7f7f";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
                    fill={bg}
                />
                <rect x="6" y="9" width="12" height="2" rx="0.5" fill={accent} />
                <rect x="6" y="6" width="10" height="2" rx="0.5" fill={fg} />
            </svg>
        );
    },
    notes: (props: IconProps) => {
        const fg = props.fgColor ?? "white";
        const bg = props.bgColor ?? "currentColor";
        const accent = props.accentColor ?? "#7f7f7f";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="2" width="20" height="20" rx="2" fill={bg} />
                <path
                    d="M12 16.3334H18"
                    stroke={accent}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M16 5.91913C15.6249 5.91913 15.2652 6.06813 15 6.33334L7.197 14.1363L9.197 16.1363L17 8.33334C17.1313 8.20202 17.2355 8.04612 17.3066 7.87454C17.3776 7.70296 17.4142 7.51906 17.4142 7.33334C17.4142 7.14762 17.3776 6.96373 17.3066 6.79215C17.2355 6.62057 17.1313 6.46466 17 6.33334C16.8687 6.20202 16.7128 6.09785 16.5412 6.02678C16.3696 5.95571 16.1857 5.91913 16 5.91913ZM7.81814 16.8788L6.45454 15.5152L6 17.3333L7.81814 16.8788Z"
                    fill={fg}
                />
            </svg>
        );
    },
    notebook: (props: IconProps) => {
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    vectorEffect="non-scaling-stroke"
                    d="M15.801 4.79272C11.7138 8.87995 13.8636 6.73011 10.1831 10.4106C9.92678 10.667 9.7442 10.9889 9.65628 11.3405L8.89173 14.3987C8.75441 14.948 9.25195 15.4456 9.80124 15.3083L12.8595 14.5437C13.2111 14.4558 13.5323 14.2739 13.7886 14.0176L19.4073 8.39897"
                    stroke={fg}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                />
                <path
                    vectorEffect="non-scaling-stroke"
                    d="M22.2004 2.77742L21.4225 1.9996C20.6415 1.21855 19.3752 1.21855 18.5941 1.9996L17.3036 3.29007C18.2026 5.36114 19.0025 6.13352 20.9099 6.89631L22.2004 5.60584C22.9814 4.8248 22.9814 3.55847 22.2004 2.77742Z"
                    stroke={fg}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                />
                <path
                    vectorEffect="non-scaling-stroke"
                    d="M12.1125 3.59998H8.3C6.367 3.59998 4.8 5.16698 4.8 7.09998V18.1C4.8 20.033 6.367 21.6 8.3 21.6H15.925C17.858 21.6 19.425 20.033 19.425 18.1V12.6"
                    stroke={fg}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                />
            </svg>
        );
    },
    halfSpinner: (props: IconProps) => {
        const iconSize = props.size ?? 10;
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle
                    opacity="0.2"
                    cx="12"
                    cy="12"
                    r="9.16667"
                    stroke={fg}
                    strokeOpacity="0.8"
                    strokeWidth="1.5"
                    fill="none"
                    vectorEffect="non-scaling-stroke"
                />
                <path
                    d="M20.8543 14.3725C22.1646 9.48242 19.2626 4.456 14.3725 3.1457C9.4824 1.8354 4.45598 4.73741 3.14568 9.62751"
                    stroke={fg}
                    strokeOpacity="0.8"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    fill="none">
                    <animateTransform
                        attributeName="transform"
                        type="rotate"
                        from="0 12 12"
                        to="360 12 12"
                        dur="0.7s"
                        repeatCount="indefinite"
                        calcMode="linear"
                        keySplines=".05,.25,.75,.9"
                        values="0 12 12;360 12 12"
                    />
                </path>
            </svg>
        );
    },
    noBgOverlay: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="18" height="18" stroke={bg} strokeWidth="2" />
            </svg>
        );
    },
    noneBgOverlay: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="18" height="18" stroke={bg} strokeWidth="2" />
            </svg>
        );
    },
    gradientBgOverlay: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="24" height="24" fill="url(#gradientOverlayPaintLinear)" />
                <defs>
                    <linearGradient
                        id="gradientOverlayPaintLinear"
                        x1="12"
                        y1="0"
                        x2="12"
                        y2="24"
                        gradientUnits="userSpaceOnUse">
                        <stop stopColor="#B2B2C0" stopOpacity="0" />
                        <stop offset="1" stopColor={bg} />
                    </linearGradient>
                </defs>
            </svg>
        );
    },
    smallLogoSize: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect
                    x="7.5"
                    y="7.5"
                    width="10"
                    height="10"
                    stroke={bg}
                    strokeWidth="1.2"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                    strokeDasharray="2 4"
                />
            </svg>
        );
    },
    mediumLogoSize: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect
                    x="5.5"
                    y="5.5"
                    width="13"
                    height="13"
                    stroke={bg}
                    strokeWidth="1.2"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                    strokeDasharray="2 4"
                />
            </svg>
        );
    },
    largeLogoSize: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect
                    x="3.5"
                    y="3.5"
                    width="17"
                    height="17"
                    stroke={bg}
                    strokeWidth="1.2"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                    strokeDasharray="2 4"
                />
            </svg>
        );
    },
    darkBgOverlay: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="24" height="24" fill={bg} />
            </svg>
        );
    },
    whiteColorTheme: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3 3.99994C3 3.44765 3.44772 2.99994 4 2.99994H20C20.5523 2.99994 21 3.44765 21 3.99994V19.9999C21 20.5522 20.5523 20.9999 20 20.9999H4C3.44772 20.9999 3 20.5522 3 19.9999V3.99994ZM6 10.9999C6 10.4477 6.44772 9.99994 7 9.99994H17C17.5523 9.99994 18 10.4477 18 10.9999V11C18 11.5523 17.5523 12 17 12H7C6.44772 12 6 11.5523 6 11V10.9999ZM8.00516 13.8977C8.00175 13.9313 8 13.9654 8 13.9999C8 14 8 14 8 14C8 14.5523 8.44772 15 9 15H15C15.5523 15 16 14.5523 16 14C16 14 16 14 16 13.9999C16 13.4477 15.5523 12.9999 15 12.9999H9C8.48223 12.9999 8.05637 13.3934 8.00516 13.8977Z"
                    fill={bg}
                />
            </svg>
        );
    },
    blackColorTheme: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3 4C3 3.44772 3.44772 3 4 3H20C20.5523 3 21 3.44772 21 4V20C21 20.5523 20.5523 21 20 21H4C3.44772 21 3 20.5523 3 20V4ZM19 5H5V19H19V5Z"
                    fill={bg}
                />
                <rect x="6" y="10" width="12" height="2" rx="1" fill={bg} />
                <rect x="8" y="13" width="8" height="2" rx="1" fill={bg} />
            </svg>
        );
    },
    valueColumn: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const fg = props.fgColor ?? "white";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="2" width="20" height="20" rx="2" fill={bg} />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10 17v-6H7v6h3zm1 2H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1h-7zm6-8v6h-6v-6h6zm0-1V7h-6v3h6zm-7-3H7v3h3V7z"
                    fill={fg}
                />
            </svg>
        );
    },
    valueDateTime: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const fg = props.fgColor ?? "white";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="2" width="20" height="20" rx="2" fill={bg} />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9 4C8.44772 4 8 4.44772 8 5V6H6C5.44772 6 5 6.44772 5 7V10.5V18C5 18.5523 5.44772 19 6 19H18C18.5523 19 19 18.5523 19 18V10.5V7C19 6.44772 18.5523 6 18 6H16V5C16 4.44772 15.5523 4 15 4C14.4477 4 14 4.44772 14 5V6H10V5C10 4.44772 9.55228 4 9 4ZM17 10V8H7V10H17ZM7 11H17V17H7V11Z"
                    fill={fg}
                />
            </svg>
        );
    },
    valueUniqueId: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const fg = props.fgColor ?? "white";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="2" width="20" height="20" rx="2" fill={bg} />
                <path
                    d="M6.44063 10.425C6.36563 10.425 6.2875 10.4063 6.21875 10.3656C6.00938 10.2438 5.9375 9.97813 6.05938 9.76875C6.61563 8.83125 8.30626 6.64062 12 6.64062C13.6 6.64062 15.0031 7.06563 16.1688 7.90313C17.1281 8.59063 17.6656 9.36875 17.9219 9.74375C18.0594 9.94375 18.0063 10.2125 17.8063 10.35C17.6063 10.4844 17.3313 10.4344 17.1938 10.2344C16.7281 9.55938 15.3156 7.50938 12 7.50938C8.76251 7.50938 7.3 9.39688 6.82188 10.2063C6.74063 10.35 6.59375 10.425 6.44063 10.425V10.425Z"
                    fill={fg}
                />
                <path
                    d="M13.8594 19C13.8219 19 13.7875 18.9969 13.75 18.9875C11.0719 18.3157 10.0719 15.6094 10.0313 15.4969L10.025 15.4719C10.0031 15.3938 9.46563 13.5375 10.2906 12.45C10.6688 11.9532 11.2438 11.7 12.0031 11.7C12.7094 11.7 13.2188 11.9219 13.5688 12.3813C13.8563 12.7563 13.9719 13.2188 14.0844 13.6657C14.3188 14.5938 14.4875 15.0813 15.4625 15.1313C15.8906 15.1532 16.1719 14.9 16.3313 14.6844C16.7625 14.0969 16.8375 13.1375 16.5125 12.2938C16.0938 11.2 14.6125 9.14067 12 9.14067C10.8844 9.14067 9.85938 9.50317 9.03751 10.1844C8.35626 10.75 7.81563 11.5469 7.55626 12.3657C7.07501 13.8907 7.70626 16.2875 7.71251 16.3094C7.77501 16.5407 7.63438 16.7813 7.40001 16.8407C7.16563 16.9032 6.92188 16.7625 6.85938 16.5313C6.83126 16.4219 6.15626 13.8657 6.71251 12.1032C7.31876 10.1938 9.18751 8.2688 12 8.2688C13.3 8.2688 14.5281 8.71567 15.5531 9.55942C16.3469 10.2157 16.9969 11.0969 17.3344 11.9782C17.7656 13.1032 17.65 14.3657 17.0406 15.1907C16.6344 15.7407 16.0563 16.0282 15.4156 15.9969C13.7469 15.9125 13.4469 14.7344 13.2281 13.875C13.0031 12.9938 12.8594 12.5688 12 12.5688C11.5281 12.5688 11.1969 12.7 10.9906 12.9719C10.7094 13.3438 10.6875 13.925 10.7188 14.3469C10.75 14.7875 10.8438 15.1438 10.8656 15.2157C10.9344 15.3907 11.8281 17.6063 13.9688 18.1438C14.2063 18.2032 14.3469 18.4407 14.2875 18.6719C14.2344 18.8688 14.0563 19 13.8594 19V19Z"
                    fill={fg}
                />
                <path
                    d="M10.4219 18.8C10.3031 18.8 10.1875 18.7532 10.1 18.6625C9.02812 17.525 8.42187 16.2532 8.19374 14.6625V14.6532C8.06562 13.6 8.25312 12.1094 9.17187 11.0844C9.84999 10.3282 10.8031 9.94379 12 9.94379C13.4156 9.94379 14.5281 10.6094 15.2219 11.8657C15.725 12.7782 15.825 13.6875 15.8281 13.725C15.8531 13.9657 15.675 14.1782 15.4344 14.2032C15.1937 14.2282 14.975 14.0532 14.95 13.8157C14.95 13.8063 14.8625 13.025 14.4344 12.2625C13.8969 11.3032 13.0781 10.8157 11.9969 10.8157C11.0625 10.8157 10.3312 11.1 9.82812 11.6625C9.10312 12.4719 8.96249 13.7188 9.06249 14.5438C9.26249 15.95 9.79687 17.0688 10.7406 18.0688C10.9062 18.2438 10.8969 18.5219 10.7187 18.6844C10.6375 18.7594 10.5281 18.8 10.4219 18.8V18.8Z"
                    fill={fg}
                />
                <path
                    d="M15.3594 17.5469C14.4219 17.5469 13.625 17.2844 12.9875 16.7625C11.7063 15.7187 11.5625 14.0187 11.5563 13.9469C11.5375 13.7062 11.7188 13.4969 11.9625 13.4781C12.2063 13.4594 12.4157 13.6375 12.4344 13.8781C12.4375 13.9031 12.5657 15.2937 13.5532 16.0938C14.1375 16.5656 14.9188 16.7531 15.8813 16.6438C16.1219 16.6156 16.3407 16.7875 16.3688 17.0281C16.3969 17.2687 16.2219 17.4844 15.9813 17.5094C15.7688 17.5344 15.5594 17.5469 15.3594 17.5469V17.5469ZM16.1 5.95625C15.7344 5.71562 14.4375 5 12 5C9.44066 5 8.14066 5.79063 7.85316 5.99063C7.83441 6.00313 7.81566 6.01562 7.80004 6.03125C7.79691 6.03438 7.79379 6.03437 7.79379 6.03437C7.70316 6.11562 7.64691 6.23125 7.64691 6.35938C7.64691 6.6 7.84379 6.79375 8.08754 6.79375C8.18441 6.79375 8.27191 6.7625 8.34379 6.7125L8.34066 6.71563C8.35316 6.70625 9.47504 5.87188 12 5.87188C14.525 5.87188 15.6469 6.70938 15.6594 6.71563L15.6563 6.7125L15.6625 6.70625C15.7375 6.7625 15.8282 6.79375 15.9282 6.79375C16.1719 6.79375 16.3688 6.6 16.3688 6.35938C16.3688 6.17812 16.2594 6.02187 16.1 5.95625Z"
                    fill={fg}
                />
            </svg>
        );
    },
    valueUserEmail: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const fg = props.fgColor ?? "white";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="2" width="20" height="20" rx="2" fill={bg} />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12 10.417a1.583 1.583 0 1 0 0 3.166 1.583 1.583 0 0 0 0-3.166zM8.917 12a3.083 3.083 0 1 1 6.166 0 3.083 3.083 0 0 1-6.166 0z"
                    fill={fg}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.187 6.048A6.583 6.583 0 0 1 18.583 12v.583a2.5 2.5 0 0 1-5 0V9.667a.75.75 0 0 1 1.5 0v2.916a1 1 0 0 0 2 0V12a5.084 5.084 0 1 0-1.992 4.036.75.75 0 0 1 .912 1.191A6.583 6.583 0 1 1 9.187 6.048z"
                    fill={fg}
                />
            </svg>
        );
    },
    platformIOS: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12.8581 7.94322C12.8378 6.03561 14.5072 5.12064 14.5816 5.07462C13.6437 3.78065 12.1822 3.60309 11.6618 3.58309C10.4193 3.4638 9.23579 4.27354 8.60516 4.27354C7.97612 4.27354 7.00202 3.60044 5.97154 3.61746C4.61556 3.63661 3.36609 4.36142 2.66829 5.50628C1.26037 7.80998 2.3083 11.2244 3.68014 13.0943C4.35099 14.0081 5.1508 15.0367 6.20002 14.999C7.21153 14.9613 7.59341 14.3818 8.81591 14.3818C10.0384 14.3818 10.382 14.999 11.4518 14.9796C12.5393 14.961 13.2289 14.0482 13.8944 13.1305C14.6641 12.0688 14.9813 11.0408 15 10.9885C14.9765 10.9783 12.8799 10.2206 12.8581 7.94322ZM10.8478 2.4044C11.4042 1.76714 11.7813 0.88149 11.679 0C10.8757 0.0307652 9.90344 0.503666 9.32701 1.14034C8.81046 1.70528 8.35863 2.60467 8.47966 3.47029C9.37544 3.53578 10.2894 3.04049 10.8478 2.4044Z"
                    fill={bg}
                />
            </svg>
        );
    },
    platformAndroid: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M12.7067 6.08571L14.44 4.22857C14.7067 3.94286 14.7067 3.5 14.44 3.21429C14.1733 2.92857 13.76 2.92857 13.4933 3.21429L11.52 5.32857C10.4667 4.75714 9.26667 4.42857 8 4.42857C6.72 4.42857 5.52 4.75714 4.45333 5.32857L2.46667 3.21429C2.2 2.92857 1.78667 2.92857 1.52 3.21429C1.25333 3.5 1.25333 3.94286 1.52 4.22857L3.26667 6.1C1.29333 7.65714 0 10.1571 0 13H16C16 10.1571 14.7067 7.64286 12.7067 6.08571ZM5.33333 9.47619C5.33333 9.84438 5.03486 10.1429 4.66667 10.1429C4.29848 10.1429 4 9.84438 4 9.47619V9.38095C4 9.01276 4.29848 8.71429 4.66667 8.71429C5.03486 8.71429 5.33333 9.01276 5.33333 9.38095V9.47619ZM12 9.47619C12 9.84438 11.7015 10.1429 11.3333 10.1429C10.9651 10.1429 10.6667 9.84438 10.6667 9.47619V9.38095C10.6667 9.01276 10.9651 8.71429 11.3333 8.71429C11.7015 8.71429 12 9.01276 12 9.38095V9.47619Z"
                    fill={bg}
                />
            </svg>
        );
    },
    choiceDropdown: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="3" width="22" height="3" rx="1" fill={bg} />
                <rect x="1" y="8" width="17" height="3" rx="1" fill={bg} />
                <rect x="1" y="13" width="22" height="3" rx="1" fill={bg} />
                <rect x="1" y="18" width="19" height="3" rx="1" fill={bg} />
            </svg>
        );
    },
    choiceInline: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M0 10C0 8.89543 0.895431 8 2 8H22C23.1046 8 24 8.89543 24 10V14C24 15.1046 23.1046 16 22 16H2C0.89543 16 0 15.1046 0 14V10Z"
                    fill={bg}
                />
                <path
                    d="M1 10C1 9.44772 1.44772 9 2 9H12C12.5523 9 13 9.44772 13 10V14C13 14.5523 12.5523 15 12 15H2C1.44772 15 1 14.5523 1 14V10Z"
                    fill="white"
                />
            </svg>
        );
    },
    specialValue: (props: IconProps) => {
        // TODO: Redo
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip-sv)">
                    <path
                        d="M16.6875 6.375H17.25C17.457 6.375 17.625 6.543 17.625 6.75V7.3125C17.625 7.623 17.877 7.875 18.1875 7.875C18.498 7.875 18.75 7.623 18.75 7.3125V6.75C18.75 6.543 18.918 6.375 19.125 6.375H19.6875C19.998 6.375 20.25 6.123 20.25 5.8125C20.25 5.502 19.998 5.25 19.6875 5.25H19.125C18.918 5.25 18.75 5.082 18.75 4.875V4.3125C18.75 4.002 18.498 3.75 18.1875 3.75C17.877 3.75 17.625 4.002 17.625 4.3125V4.875C17.625 5.082 17.457 5.25 17.25 5.25H16.6875C16.377 5.25 16.125 5.502 16.125 5.8125C16.125 6.123 16.377 6.375 16.6875 6.375ZM6.5625 15.375H6.1875C6.084 15.375 6 15.291 6 15.1875V14.8125C6 14.502 5.748 14.25 5.4375 14.25C5.127 14.25 4.875 14.502 4.875 14.8125V15.1875C4.875 15.291 4.791 15.375 4.6875 15.375H4.3125C4.002 15.375 3.75 15.627 3.75 15.9375C3.75 16.248 4.002 16.5 4.3125 16.5H4.6875C4.791 16.5 4.875 16.584 4.875 16.6875V17.0625C4.875 17.373 5.127 17.625 5.4375 17.625C5.748 17.625 6 17.373 6 17.0625V16.6875C6 16.584 6.084 16.5 6.1875 16.5H6.5625C6.873 16.5 7.125 16.248 7.125 15.9375C7.125 15.627 6.873 15.375 6.5625 15.375ZM14.856 10.6065C14.8792 10.2383 14.6602 9.897 14.3152 9.765L12.675 9.11925C12.5347 9.06375 12.441 8.9295 12.4373 8.77875L12.3968 6.939C12.4043 6.5925 12.2003 6.276 11.8823 6.1395C11.5643 6.00825 11.199 6.0855 10.9612 6.33375L9.975 7.32375C9.87375 7.425 9.723 7.46025 9.58725 7.413L7.95 6.846H7.94325C7.48575 6.69675 6.99375 6.94725 6.84525 7.4055C6.7875 7.5825 6.78825 7.773 6.84675 7.95L7.413 9.58875C7.46025 9.7245 7.425 9.87525 7.32375 9.9765L6.33225 10.965C6.08325 11.2028 6.00675 11.5687 6.13875 11.886C6.279 12.1965 6.588 12.3967 6.9285 12.399L8.77875 12.4402C8.9295 12.4432 9.0645 12.537 9.12 12.6772L9.76425 14.3145C9.891 14.643 10.2075 14.859 10.56 14.8568C10.575 14.8568 10.5908 14.8568 10.6065 14.8568C10.983 14.841 11.3047 14.583 11.4023 14.2192L11.9602 12.222C11.9955 12.096 12.0945 11.997 12.2205 11.9618L14.2185 11.403C14.5822 11.3055 14.841 10.983 14.856 10.6065ZM5.04 5.835C5.2635 6.051 5.61975 6.045 5.83575 5.8215C6.0465 5.60325 6.0465 5.25825 5.83575 5.04L4.71075 3.915C4.48725 3.699 4.131 3.705 3.915 3.9285C3.70425 4.14675 3.70425 4.49175 3.915 4.71L5.04 5.835ZM9.5625 5.625C9.873 5.625 10.125 5.373 10.125 5.0625V3.5625C10.125 3.252 9.873 3 9.5625 3C9.252 3 9 3.252 9 3.5625V5.0625C9 5.373 9.252 5.625 9.5625 5.625ZM5.625 9.9375C5.625 9.627 5.373 9.375 5.0625 9.375H3.5625C3.252 9.375 3 9.627 3 9.9375C3 10.248 3.252 10.5 3.5625 10.5H5.0625C5.373 10.5 5.625 10.248 5.625 9.9375ZM20.5185 18.4905L13.8698 12.5775C13.7213 12.4455 13.4955 12.4522 13.3553 12.5925L12.5925 13.3553C12.4522 13.4955 12.4463 13.7205 12.5775 13.869L18.4913 20.5185C19.0065 21.1215 19.9132 21.192 20.5162 20.6768C21.1193 20.1615 21.1898 19.2548 20.6745 18.6517C20.6257 18.5947 20.5732 18.5423 20.5162 18.4935L20.5185 18.4905Z"
                        fill={bg}
                    />
                </g>
                <defs>
                    <clipPath id="clip-sv">
                        <rect x="3" y="3" width="18" height="18" fill="white" />
                    </clipPath>
                </defs>
            </svg>
        );
    },
    unlinkSheet: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M14.1667 5.83333H10.8333V7.41667H14.1667C15.5917 7.41667 16.75 8.575 16.75 10C16.75 11.1917 15.9333 12.1917 14.825 12.4833L16.0417 13.7C17.4 13.0083 18.3333 11.625 18.3333 10C18.3333 7.7 16.4667 5.83333 14.1667 5.83333ZM13.3333 9.16667H11.5083L13.175 10.8333H13.3333V9.16667ZM1.66667 3.55833L4.25834 6.15C2.74167 6.76667 1.66667 8.25833 1.66667 10C1.66667 12.3 3.53334 14.1667 5.83334 14.1667H9.16667V12.5833H5.83334C4.40834 12.5833 3.25001 11.425 3.25001 10C3.25001 8.675 4.25834 7.58333 5.55001 7.44167L7.275 9.16667H6.66667V10.8333H8.94167L10.8333 12.725V14.1667H12.275L15.6167 17.5L16.6667 16.45L2.72501 2.5L1.66667 3.55833Z"
                    fill={bg}
                />
            </svg>
        );
    },
    template: (_props: IconProps) => {
        return (
            <svg width="270" height="191" viewBox="0 0 270 191" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M178.844 37.7633L169.877 48.0157L191.943 67.3143L200.91 57.0618L178.844 37.7633Z"
                    fill="#FF574C"
                />
                <path
                    d="M118.206 40.1041C119.613 51.5717 111.457 62.0096 99.9873 63.4168C88.5179 64.8241 78.0818 56.6678 76.6746 45.2002"
                    fill="#00CB69"
                />
                <path d="M33.0045 102.271L17.877 90.9622L0 122.211L21.8422 102.78L32.0579 110.348" fill="black" />
                <path
                    d="M18.1913 101.696L18.4821 108.944"
                    stroke="white"
                    strokeWidth="0.722595"
                    strokeMiterlimit="10"
                />
                <path
                    d="M15.5955 104.388L15.8881 111.636"
                    stroke="white"
                    strokeWidth="0.722595"
                    strokeMiterlimit="10"
                />
                <path d="M12.9741 107.432L13.265 114.679" stroke="white" strokeWidth="0.722595" strokeMiterlimit="10" />
                <path d="M53.6816 153.355L35.026 161.442L56.6858 191L47.9948 163.359L60.2825 158.104" fill="black" />
                <path
                    d="M45.9841 166.025L52.4622 169.289"
                    stroke="white"
                    strokeWidth="0.722595"
                    strokeMiterlimit="10"
                />
                <path
                    d="M47.0789 169.602L53.5551 172.866"
                    stroke="white"
                    strokeWidth="0.722595"
                    strokeMiterlimit="10"
                />
                <path
                    d="M48.4663 173.372L54.9426 176.637"
                    stroke="white"
                    strokeWidth="0.722595"
                    strokeMiterlimit="10"
                />
                <path
                    d="M105.745 28.745C105.853 33.758 101.879 37.9094 96.8657 38.0177C92.4344 38.1135 88.6751 35.0172 87.7881 30.8351C87.6725 30.286 87.6056 29.7205 87.593 29.1388C87.4846 24.1277 91.4589 19.9745 96.4719 19.8662C99.0804 19.8102 101.454 20.8579 103.147 22.5831C104.711 24.1746 105.694 26.3424 105.745 28.745Z"
                    fill="#FFC200"
                />
                <path
                    d="M103.149 22.5831C100.816 27.4426 95.8992 30.846 90.1419 30.9706C89.3435 30.9887 88.5558 30.9399 87.7881 30.8351C87.6725 30.286 87.6056 29.7205 87.593 29.1388C87.4846 24.1277 91.4589 19.9745 96.4719 19.8662C99.0822 19.8102 101.454 20.8579 103.149 22.5831Z"
                    fill="black"
                />
                <path
                    d="M90.4382 21.8911C92.3488 21.8911 93.8976 20.3423 93.8976 18.4317C93.8976 16.5211 92.3488 14.9723 90.4382 14.9723C88.5276 14.9723 86.9788 16.5211 86.9788 18.4317C86.9788 20.3423 88.5276 21.8911 90.4382 21.8911Z"
                    fill="black"
                />
                <path
                    d="M80.5006 19.6943C85.939 19.6943 90.3478 15.2856 90.3478 9.84716C90.3478 4.40872 85.939 0 80.5006 0C75.0622 0 70.6534 4.40872 70.6534 9.84716C70.6534 15.2856 75.0622 19.6943 80.5006 19.6943Z"
                    fill="black"
                />
                <path
                    d="M104.027 31.8779C104.57 31.8662 104.996 31.1564 104.977 30.2926C104.959 29.4287 104.503 28.738 103.959 28.7497C103.416 28.7615 102.99 29.4713 103.009 30.3351C103.027 31.1989 103.483 31.8897 104.027 31.8779Z"
                    fill="black"
                />
                <path
                    d="M100.762 32.0567C101.306 32.0449 101.732 31.3351 101.713 30.4713C101.694 29.6074 101.238 28.9167 100.695 28.9284C100.151 28.9402 99.7256 29.65 99.7442 30.5138C99.7629 31.3776 100.219 32.0684 100.762 32.0567Z"
                    fill="black"
                />
                <path
                    d="M103.79 32.9684C103.79 32.9684 102.289 32.6397 102.345 31.2577"
                    stroke="black"
                    strokeWidth="0.722595"
                    strokeMiterlimit="10"
                />
                <path
                    d="M72.0409 92.3096L63.3896 120.361L35.2734 99.1725C35.2734 99.1725 28.3275 99.1219 24.1202 105.163C20.0158 111.054 23.0652 116.726 23.0652 116.726L70.7745 157.744L91.4913 112.571L91.6918 132.639L54.3843 149.906C54.3843 149.906 51.9184 156.66 55.479 163.29C58.9366 169.727 63.7672 169.508 63.7672 169.508L103.163 154.511C109.24 152.196 113.382 146.517 113.726 140.023L114.139 100.068C114.824 87.1178 107.045 76.5914 93.7946 75.4948C81.9621 74.5175 75.0541 81.9512 72.0409 92.3096Z"
                    fill="#4F5DFF"
                />
                <path
                    d="M57.6288 146.848C63.9533 152.509 73.9775 151.209 77.3827 144.255L96.5749 101.743"
                    stroke="white"
                    strokeWidth="0.722595"
                    strokeMiterlimit="10"
                />
                <path
                    d="M121.851 40.8393L147.608 47.8972C147.608 47.8972 173.522 38.3879 179.146 37.7376C183.387 37.2462 186.715 43.3883 185.864 43.708L182.623 39.3869C182.623 39.3869 187.091 44.6998 185.92 45.2309C184.749 45.7638 180.376 40.3082 180.376 40.3082C180.376 40.3082 183.935 45.09 182.871 45.4097C181.807 45.7295 178.248 41.3234 175.587 41.9864C172.926 42.6494 172.028 46.1829 173.305 48.2043C174.583 50.2258 178.226 49.3388 178.573 50.6774C178.824 51.6457 171.25 50.5293 168.789 51.2862C166.285 52.0558 154.619 58.7723 147.437 59.0957C139.401 59.457 119.618 52.5399 119.618 52.5399"
                    fill="white"
                />
                <path
                    d="M121.851 40.8393L147.608 47.8972C147.608 47.8972 173.522 38.3879 179.146 37.7376C183.387 37.2462 186.715 43.3883 185.864 43.708L182.623 39.3869C182.623 39.3869 187.091 44.6998 185.92 45.2309C184.749 45.7638 180.376 40.3082 180.376 40.3082C180.376 40.3082 183.935 45.09 182.871 45.4097C181.807 45.7295 178.248 41.3234 175.587 41.9864C172.926 42.6494 172.028 46.1829 173.305 48.2043C174.583 50.2258 178.226 49.3388 178.573 50.6774C178.824 51.6457 171.25 50.5293 168.789 51.2862C166.285 52.0558 154.619 58.7723 147.437 59.0957C139.401 59.457 119.618 52.5399 119.618 52.5399"
                    stroke="black"
                    strokeWidth="0.632271"
                    strokeMiterlimit="10"
                    strokeLinejoin="bevel"
                />
                <path
                    d="M74.7831 47.2144C74.7831 47.2144 53.9471 51.7775 48.1754 61.8794C44.0277 69.1361 47.7346 86.22 47.7346 86.22"
                    stroke="black"
                    strokeWidth="0.722595"
                    strokeMiterlimit="10"
                    strokeLinejoin="bevel"
                />
                <path
                    d="M79.2777 59.0016C78.6725 59.1533 58.1887 66.8634 58.1887 66.8634V87.4953"
                    stroke="black"
                    strokeWidth="0.722595"
                    strokeMiterlimit="10"
                    strokeLinejoin="bevel"
                />
                <path
                    d="M52.2291 99.3912C56.8844 99.3912 60.6582 95.6174 60.6582 90.9621C60.6582 86.3069 56.8844 82.5331 52.2291 82.5331C47.5739 82.5331 43.8 86.3069 43.8 90.9621C43.8 95.6174 47.5739 99.3912 52.2291 99.3912Z"
                    fill="#FFC200"
                />
                <path
                    d="M166.9 99.7056C175.058 99.7056 181.671 93.0922 181.671 84.934C181.671 76.7758 175.058 70.1624 166.9 70.1624C158.741 70.1624 152.128 76.7758 152.128 84.934C152.128 93.0922 158.741 99.7056 166.9 99.7056Z"
                    fill="#737383"
                />
                <path
                    d="M252.552 94.1182C256.769 94.1182 260.187 90.7002 260.187 86.4839C260.187 82.2677 256.769 78.8497 252.552 78.8497C248.336 78.8497 244.918 82.2677 244.918 86.4839C244.918 90.7002 248.336 94.1182 252.552 94.1182Z"
                    fill="#D8D8E3"
                />
                <path
                    d="M197.554 123.696C199.839 123.696 201.691 121.844 201.691 119.559C201.691 117.274 199.839 115.422 197.554 115.422C195.269 115.422 193.417 117.274 193.417 119.559C193.417 121.844 195.269 123.696 197.554 123.696Z"
                    fill="#737383"
                />
                <path
                    d="M145.323 145.677C148.502 145.677 151.08 143.099 151.08 139.92C151.08 136.74 148.502 134.162 145.323 134.162C142.143 134.162 139.565 136.74 139.565 139.92C139.565 143.099 142.143 145.677 145.323 145.677Z"
                    fill="#D8D8E3"
                />
                <path
                    d="M205.914 45.2308C207.762 45.2308 209.26 43.7329 209.26 41.8852C209.26 40.0374 207.762 38.5396 205.914 38.5396C204.066 38.5396 202.569 40.0374 202.569 41.8852C202.569 43.7329 204.066 45.2308 205.914 45.2308Z"
                    fill="#737383"
                />
                <path
                    d="M45.3573 96.6958C45.3573 96.6958 49.9151 87.6489 51.1868 88.4781C52.4604 89.3073 49.4255 97.0571 49.4255 97.0571C49.4255 97.0571 53.7087 87.06 55.2334 88.0626C56.758 89.0652 53.976 96.9018 53.976 96.9018C53.976 96.9018 56.3299 91.7786 57.5402 92.3205C58.7488 92.8625 56.0391 99.2466 56.0391 99.2466"
                    stroke="black"
                    strokeWidth="0.722595"
                    strokeMiterlimit="10"
                    strokeLinejoin="bevel"
                />
                <path
                    d="M43.7947 87.7139C43.7947 87.7139 52.4586 85.8587 43.3105 92.017"
                    stroke="black"
                    strokeWidth="0.722595"
                    strokeMiterlimit="10"
                    strokeLinejoin="bevel"
                />
                <path
                    d="M170.364 125.033C167.317 125.033 164.322 123.587 162.456 120.892C159.435 116.529 160.524 110.543 164.887 107.522L202.664 81.3715C207.029 78.3511 213.014 79.4404 216.034 83.8031C219.055 88.1657 217.965 94.1524 213.603 97.1729L175.825 123.324C174.156 124.478 172.25 125.033 170.364 125.033Z"
                    fill="#D8D8E3"
                />
                <path
                    d="M218.231 141.089C215.595 141.089 213.109 139.486 212.11 136.876L204.451 116.856C203.157 113.476 204.848 109.688 208.228 108.397C211.608 107.103 215.395 108.796 216.688 112.174L224.348 132.193C225.641 135.573 223.95 139.36 220.57 140.653C219.801 140.95 219.009 141.089 218.231 141.089Z"
                    fill="#737383"
                />
                <path
                    d="M244.355 79.7078L225.779 75.0504L218.931 102.363L237.507 107.021L244.355 79.7078Z"
                    fill="#737383"
                />
                <path
                    d="M268.452 78.7953L262.959 79.649L263.813 85.1415L269.305 84.2877L268.452 78.7953Z"
                    fill="#737383"
                />
                <path
                    d="M210.968 18.8396L203.6 14.5853L192.527 33.7635L199.896 38.0178L210.968 18.8396Z"
                    fill="#D8D8E3"
                />
                <path d="M152.128 68.3285H132.229L143.545 83.5265L152.128 68.3285Z" fill="#D8D8E3" />
                <path d="M149.133 122.057L170.372 127.753L154.53 142.201L149.133 122.057Z" fill="#737383" />
                <path d="M212.833 148.665L231.635 142.366L228.152 161.518L212.833 148.665Z" fill="#D8D8E3" />
                <path d="M56.0607 120.762L52.146 125.725" stroke="white" strokeWidth="0.541946" strokeMiterlimit="10" />
                <path
                    d="M38.1476 101.339L36.7512 103.109"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M104.672 132.578L102.151 137.022"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M63.3896 139.824L60.8695 144.268"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M63.8214 124.476L68.3991 123.242"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path d="M71.5839 114.99L75.5654 119.7" stroke="white" strokeWidth="0.541946" strokeMiterlimit="10" />
                <path d="M59.083 129.449L63.0627 134.159" stroke="white" strokeWidth="0.541946" strokeMiterlimit="10" />
                <path
                    d="M67.3132 106.263L68.5055 107.674"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M75.5564 110.595L76.6168 106.758"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path d="M83.3947 110.855L86.2435 115.01" stroke="white" strokeWidth="0.541946" strokeMiterlimit="10" />
                <path
                    d="M30.1809 103.421L33.0279 107.576"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M81.6677 125.404L82.9955 121.092"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M102.787 113.881L107.759 116.033"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M83.4253 136.439L86.6589 139.069"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M95.7727 122.456L98.8022 119.387"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path d="M70.545 130.824L73.5745 127.755" stroke="white" strokeWidth="0.541946" strokeMiterlimit="10" />
                <path
                    d="M97.6208 114.839L96.9542 110.485"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path d="M75.1968 139.407L74.532 135.053" stroke="white" strokeWidth="0.541946" strokeMiterlimit="10" />
                <path
                    d="M83.0604 105.544L87.8296 103.696"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M50.1462 134.406L55.0906 135.701"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path d="M107.9 86.5596L111.196 90.4616" stroke="white" strokeWidth="0.541946" strokeMiterlimit="10" />
                <path
                    d="M103.971 105.246L100.905 104.283"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path d="M71.2731 166.65L69.9128 164.291" stroke="white" strokeWidth="0.541946" strokeMiterlimit="10" />
                <path
                    d="M102.899 154.612L102.227 153.447"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path d="M94.4595 99.543L95.5271 94.9834" stroke="white" strokeWidth="0.541946" strokeMiterlimit="10" />
                <path d="M73.1753 101.644L76.707 96.5875" stroke="white" strokeWidth="0.541946" strokeMiterlimit="10" />
                <path d="M87.7032 98.553L83.3712 96.5894" stroke="white" strokeWidth="0.541946" strokeMiterlimit="10" />
                <path
                    d="M97.2234 89.4122L92.7144 89.2369"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M85.3872 84.4694L85.7991 89.7389"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M77.6283 81.5936L78.7465 84.2004"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M101.429 80.1738L98.3632 84.2619"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M103.804 97.5357L105.344 93.8632"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path d="M114.347 98.0056H112.002" stroke="white" strokeWidth="0.541946" strokeMiterlimit="10" />
                <path
                    d="M108.442 107.439L111.178 103.855"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path d="M44.015 134.939L44.5642 133.731" stroke="white" strokeWidth="0.541946" strokeMiterlimit="10" />
                <path d="M96.255 135.053L96.8295 129.205" stroke="white" strokeWidth="0.541946" strokeMiterlimit="10" />
                <path d="M103.996 147.021L101.579 143.45" stroke="white" strokeWidth="0.541946" strokeMiterlimit="10" />
                <path d="M108.624 127.301L106.209 123.73" stroke="white" strokeWidth="0.541946" strokeMiterlimit="10" />
                <path
                    d="M60.1434 156.028L57.7263 152.457"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M98.5043 150.083L94.5806 152.085"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M112.857 144.886L108.935 146.887"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M91.1517 145.746L93.3918 141.154"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path d="M85.3168 161.305L86.7511 158.36" stroke="white" strokeWidth="0.541946" strokeMiterlimit="10" />
                <path
                    d="M110.385 133.776L113.793 133.604"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M113.906 122.492L112.002 120.229"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M72.9677 158.935L74.8085 153.049"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M65.9314 159.632L62.1233 160.795"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M72.7725 144.638L68.4207 145.831"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M114.181 111.688L112.725 112.327"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M81.2341 148.342L85.6149 148.801"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M27.4424 111.124L24.9386 115.579"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path d="M31.4076 124.117L32.7516 123.75" stroke="white" strokeWidth="0.541946" strokeMiterlimit="10" />
                <path
                    d="M33.5609 115.283L37.5587 119.978"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path d="M38.751 111.921L39.7987 108.08" stroke="white" strokeWidth="0.541946" strokeMiterlimit="10" />
                <path d="M45.8252 113.09L50.8346 113.614" stroke="white" strokeWidth="0.541946" strokeMiterlimit="10" />
                <path
                    d="M43.2057 124.364L44.5172 120.048"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M87.8821 79.3933L92.1635 80.4194"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M70.9661 90.5792L75.2492 91.6053"
                    stroke="white"
                    strokeWidth="0.541946"
                    strokeMiterlimit="10"
                />
                <path
                    d="M166.784 75.9972L162.736 81.245"
                    stroke="#D14D33"
                    strokeWidth="0.722595"
                    strokeMiterlimit="10"
                />
                <path
                    d="M209.78 72.488L216.888 68.0198L211.34 58.7565L205.37 56.9548L204.232 63.2247L209.78 72.488Z"
                    fill="#4F5DFF"
                />
                <path
                    d="M205.382 57.5301C205.382 57.5301 203.489 52.7457 196.178 52.9331"
                    stroke="black"
                    strokeWidth="0.262467"
                    strokeMiterlimit="10"
                    strokeLinejoin="bevel"
                />
                <path
                    d="M207.396 61.5572C207.873 61.5572 208.259 61.1708 208.259 60.6943C208.259 60.2177 207.873 59.8313 207.396 59.8313C206.919 59.8313 206.533 60.2177 206.533 60.6943C206.533 61.1708 206.919 61.5572 207.396 61.5572Z"
                    fill="white"
                />
            </svg>
        );
    },
    rootClickGesture: (props: IconProps) => {
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="20" height="35" viewBox="1 1 18 18" fill={fg} xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M10.0015 4.29272C9.06081 4.29467 8.13771 4.5479 7.32769 5.02622C6.51768 5.50454 5.85015 6.19058 5.39418 7.01338C4.9382 7.83618 4.71032 8.76586 4.73411 9.70626C4.75791 10.6467 5.03251 11.5636 5.52951 12.3623C5.56988 12.432 5.62385 12.4928 5.6882 12.5411C5.75255 12.5894 5.82597 12.6243 5.90409 12.6437C5.98221 12.6631 6.06343 12.6666 6.14292 12.6539C6.2224 12.6413 6.29853 12.6128 6.36677 12.5701C6.43501 12.5274 6.49396 12.4714 6.54012 12.4055C6.58628 12.3395 6.6187 12.265 6.63545 12.1863C6.6522 12.1075 6.65295 12.0263 6.63763 11.9472C6.62232 11.8682 6.59126 11.7931 6.54631 11.7263C6.25234 11.2651 6.05431 10.7493 5.96404 10.2098C5.87378 9.67033 5.89312 9.1182 6.02093 8.58636C6.14874 8.05452 6.38239 7.55388 6.70793 7.11433C7.03347 6.67477 7.44423 6.30531 7.9157 6.02799C8.38716 5.75067 8.90967 5.57118 9.45202 5.50023C9.99438 5.42928 10.5455 5.46832 11.0724 5.61503C11.5993 5.76175 12.0913 6.01312 12.519 6.35414C12.9466 6.69517 13.3012 7.11886 13.5615 7.59992C13.9201 8.23479 14.0992 8.95526 14.0796 9.68414C14.06 10.413 13.8425 11.1228 13.4503 11.7375C13.4083 11.8042 13.3798 11.8785 13.3665 11.9561C13.3532 12.0338 13.3553 12.1133 13.3727 12.1902C13.3901 12.267 13.4225 12.3397 13.468 12.404C13.5135 12.4684 13.5713 12.5231 13.6379 12.5651C13.7046 12.6071 13.7789 12.6356 13.8565 12.6489C13.9342 12.6623 14.0137 12.6601 14.0906 12.6427C14.1674 12.6253 14.2401 12.5929 14.3044 12.5474C14.3687 12.5019 14.4235 12.4442 14.4655 12.3775C14.9661 11.5791 15.2439 10.6613 15.2701 9.71933C15.2962 8.77734 15.0698 7.84552 14.6143 7.02057C14.1588 6.19562 13.4908 5.5076 12.6797 5.02793C11.8686 4.54825 10.9439 4.29441 10.0015 4.29272Z"
                    fill={fg}
                />
                <path
                    d="M17.9213 6.25839C17.2552 4.71411 16.1487 3.40047 14.74 2.4817C13.3313 1.56294 11.6831 1.07983 10.0013 1.09278C8.31826 1.07997 6.66885 1.56432 5.25992 2.4851C3.85099 3.40589 2.74525 4.72213 2.08134 6.26879C1.38599 7.807 1.16028 9.51628 1.43254 11.1823C1.70479 12.8482 2.46289 14.3968 3.61174 15.6336C3.72091 15.7448 3.86903 15.8093 4.02483 15.8133C4.18062 15.8174 4.3319 15.7608 4.44673 15.6554C4.56155 15.55 4.63094 15.4041 4.64023 15.2486C4.64952 15.093 4.598 14.9399 4.49654 14.8216C3.50615 13.7559 2.85259 12.4214 2.6179 10.9857C2.3832 9.54987 2.57783 8.07676 3.17734 6.75119C3.74902 5.41841 4.7017 4.28422 5.91577 3.49102C7.12983 2.69782 8.55117 2.28096 10.0013 2.29278C11.4499 2.28175 12.8695 2.69795 14.0828 3.48936C15.2961 4.28077 16.2492 5.41226 16.8229 6.74239C17.4227 8.06423 17.6195 9.53353 17.3889 10.9666C17.1583 12.3997 16.5103 13.7331 15.5261 14.8C15.4731 14.8581 15.432 14.9262 15.4052 15.0002C15.3785 15.0742 15.3666 15.1528 15.3702 15.2314C15.3738 15.3101 15.3928 15.3872 15.4262 15.4585C15.4597 15.5298 15.5068 15.5937 15.5649 15.6468C15.6231 15.6998 15.6911 15.7409 15.7651 15.7677C15.8392 15.7945 15.9177 15.8064 15.9964 15.8028C16.075 15.7992 16.1522 15.7801 16.2234 15.7467C16.2947 15.7133 16.3587 15.6661 16.4117 15.608C17.5549 14.3707 18.3079 12.8238 18.5764 11.1608C18.8449 9.49782 18.617 7.79256 17.9213 6.25839Z"
                    fill={fg}
                />
                <path
                    d="M13.5334 14.0287L10.5398 13.1359C10.4986 13.1235 10.4624 13.0981 10.4368 13.0635C10.4111 13.0289 10.3973 12.987 10.3974 12.9439V9.2895C10.3974 9.07733 10.3131 8.87385 10.1631 8.72382C10.0131 8.57379 9.80959 8.4895 9.59742 8.4895C9.38524 8.4895 9.18176 8.57379 9.03173 8.72382C8.8817 8.87385 8.79742 9.07733 8.79742 9.2895V14.9407C8.7974 14.9833 8.78381 15.0247 8.75863 15.059C8.73345 15.0933 8.69798 15.1187 8.65738 15.1314C8.61679 15.1442 8.57318 15.1437 8.5329 15.1299C8.49262 15.1162 8.45777 15.09 8.43342 15.0551C7.24142 13.3439 5.34862 13.8951 6.22382 15.2335L8.55342 18.7271C8.58991 18.782 8.63938 18.8269 8.69744 18.8581C8.7555 18.8892 8.82034 18.9055 8.88622 18.9055H13.2342C13.328 18.9055 13.4187 18.8726 13.4906 18.8125C13.5626 18.7524 13.6111 18.6689 13.6278 18.5767C13.7878 17.6967 14.159 15.6303 14.2478 15.2447C14.3068 14.9891 14.2657 14.7206 14.1328 14.4944C13.9999 14.2682 13.7854 14.1016 13.5334 14.0287Z"
                    fill={fg}
                />
            </svg>
        );
    },
    lightningAction: (props: IconProps) => {
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="20" height="36" viewBox="0 0 20 20" fill={fg} xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#lightningClip0)">
                    <path
                        d="M16.7555 7.81583C16.7218 7.75073 16.6708 7.69616 16.6082 7.65806C16.5455 7.61997 16.4736 7.59982 16.4003 7.59983H10.4995L11.9907 0.884633C12.011 0.794512 11.9995 0.700143 11.9581 0.617551C11.9167 0.534958 11.848 0.469235 11.7637 0.431541C11.6794 0.393848 11.5846 0.386509 11.4954 0.410769C11.4063 0.43503 11.3283 0.489395 11.2747 0.564633L3.2747 11.7646C3.23122 11.8244 3.20516 11.8951 3.19944 11.9689C3.19372 12.0426 3.20855 12.1165 3.24228 12.1823C3.27602 12.2481 3.32734 12.3033 3.39054 12.3417C3.45374 12.3801 3.52635 12.4002 3.6003 12.3998H9.5011L8.0099 19.1118C7.9896 19.2019 8.00112 19.2963 8.0425 19.3789C8.08388 19.4615 8.15257 19.5272 8.23691 19.5649C8.32125 19.6026 8.41604 19.61 8.50517 19.5857C8.59431 19.5614 8.6723 19.5071 8.7259 19.4318L16.7259 8.23183C16.7686 8.17192 16.794 8.10139 16.7992 8.02799C16.8044 7.9546 16.7893 7.88119 16.7555 7.81583Z"
                        fill={fg}
                    />
                </g>
                <defs>
                    <clipPath id="lightningClip0">
                        <rect width="19.2" height="19.2" fill={fg} transform="translate(0.399902 0.399902)" />
                    </clipPath>
                </defs>
            </svg>
        );
    },
    timer: (props: IconProps) => {
        const fg = props.fgColor ?? "white";
        return (
            <svg width="18" height="30" viewBox="0 0 15 15" fill={fg} xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M13.9004 16.8999H13.1504V14.2749C13.1498 13.2862 12.8888 12.315 12.3936 11.4592C11.8984 10.6034 11.1866 9.89315 10.3296 9.3999C11.1868 8.90695 11.8989 8.19674 12.3941 7.34087C12.8893 6.48501 13.1502 5.51372 13.1504 4.5249V1.8999H13.9004C14.0993 1.8999 14.2901 1.82088 14.4307 1.68023C14.5714 1.53958 14.6504 1.34881 14.6504 1.1499C14.6504 0.95099 14.5714 0.760225 14.4307 0.619572C14.2901 0.47892 14.0993 0.399902 13.9004 0.399902H1.15039C0.951478 0.399902 0.760713 0.47892 0.62006 0.619572C0.479408 0.760225 0.400391 0.95099 0.400391 1.1499C0.400391 1.34881 0.479408 1.53958 0.62006 1.68023C0.760713 1.82088 0.951478 1.8999 1.15039 1.8999H1.90039V4.5249C1.90059 5.51372 2.16145 6.48501 2.65667 7.34087C3.15189 8.19674 3.86397 8.90695 4.72114 9.3999C3.86422 9.89315 3.15235 10.6034 2.65717 11.4592C2.16199 12.315 1.90097 13.2862 1.90039 14.2749V16.8999H1.15039C0.951478 16.8999 0.760713 16.9789 0.62006 17.1196C0.479408 17.2602 0.400391 17.451 0.400391 17.6499C0.400391 17.8488 0.479408 18.0396 0.62006 18.1802C0.760713 18.3209 0.951478 18.3999 1.15039 18.3999H13.9004C14.0993 18.3999 14.2901 18.3209 14.4307 18.1802C14.5714 18.0396 14.6504 17.8488 14.6504 17.6499C14.6504 17.451 14.5714 17.2602 14.4307 17.1196C14.2901 16.9789 14.0993 16.8999 13.9004 16.8999ZM3.40039 14.2749C3.40113 13.6985 3.52249 13.1287 3.75666 12.6021C3.99083 12.0755 4.33263 11.6037 4.76007 11.217C5.18752 10.8304 5.69115 10.5375 6.23858 10.3572C6.786 10.1769 7.36511 10.1132 7.93864 10.1702C8.97697 10.3028 9.92987 10.8139 10.6148 11.6055C11.2997 12.3971 11.6684 13.4135 11.6504 14.4602V16.5249C11.6504 16.6244 11.6109 16.7197 11.5406 16.7901C11.4702 16.8604 11.3748 16.8999 11.2754 16.8999H3.77539C3.67593 16.8999 3.58055 16.8604 3.51023 16.7901C3.4399 16.7197 3.40039 16.6244 3.40039 16.5249V14.2749ZM3.77539 1.8999H11.2754C11.3748 1.8999 11.4702 1.93941 11.5406 2.00974C11.6109 2.08006 11.6504 2.17545 11.6504 2.2749V4.3419C11.6682 5.38831 11.2994 6.4045 10.6145 7.19582C9.92956 7.98715 8.97678 8.49796 7.93864 8.6304C7.36502 8.68746 6.78581 8.62379 6.23828 8.44349C5.69075 8.26319 5.18704 7.97025 4.75955 7.58353C4.33207 7.19681 3.99028 6.72487 3.7562 6.19809C3.52211 5.67131 3.40091 5.10135 3.40039 4.5249V2.2749C3.40039 2.17545 3.4399 2.08006 3.51023 2.00974C3.58055 1.93941 3.67593 1.8999 3.77539 1.8999Z"
                    fill="white"
                />
                <path
                    d="M7.52582 7.61937C8.06004 7.59978 8.57959 7.43926 9.03177 7.15409C9.48395 6.86893 9.85268 6.46926 10.1006 5.99562C10.1337 5.9386 10.1511 5.87385 10.1512 5.80792C10.1513 5.74198 10.134 5.67719 10.1011 5.62006C10.0682 5.56294 10.0208 5.51551 9.96365 5.48256C9.90654 5.44961 9.84175 5.4323 9.77581 5.43237H5.27582C5.20988 5.4323 5.14509 5.44961 5.08798 5.48256C5.03087 5.51551 4.98345 5.56294 4.95052 5.62006C4.91759 5.67719 4.9003 5.74198 4.90039 5.80792C4.90049 5.87385 4.91797 5.9386 4.95107 5.99562C5.19895 6.46926 5.56768 6.86893 6.01986 7.15409C6.47204 7.43926 6.99159 7.59978 7.52582 7.61937Z"
                    fill="white"
                />
                <path
                    d="M8.08704 12.0924C8.01453 12.0161 7.92727 11.9554 7.83058 11.9139C7.73388 11.8725 7.62976 11.8511 7.52454 11.8511C7.41932 11.8511 7.3152 11.8725 7.2185 11.9139C7.1218 11.9554 7.03455 12.0161 6.96204 12.0924L4.46979 14.9026C4.37412 15.0107 4.31164 15.144 4.28985 15.2866C4.26806 15.4293 4.28789 15.5752 4.34695 15.7069C4.40601 15.8385 4.5018 15.9503 4.62283 16.0289C4.74386 16.1075 4.88499 16.1495 5.02929 16.1499H10.0228C10.1673 16.1498 10.3087 16.1079 10.43 16.0294C10.5513 15.9508 10.6473 15.8389 10.7065 15.707C10.7656 15.5752 10.7855 15.4291 10.7636 15.2863C10.7417 15.1434 10.679 15.0099 10.583 14.9019L8.08704 12.0924Z"
                    fill="white"
                />
            </svg>
        );
    },
    helpIcon: (props: IconProps) => {
        const fg = props.fgColor ?? "none";
        return (
            <svg width="14" height="17" viewBox="0 0 14 10" fill={fg} xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M6.99998 0.333313C3.31998 0.333313 0.333313 3.31998 0.333313 6.99998C0.333313 10.68 3.31998 13.6666 6.99998 13.6666C10.68 13.6666 13.6666 10.68 13.6666 6.99998C13.6666 3.31998 10.68 0.333313 6.99998 0.333313ZM7.66665 11.6666H6.33331V10.3333H7.66665V11.6666ZM9.04665 6.49998L8.44665 7.11331C8.11331 7.45331 7.87331 7.75998 7.75331 8.23998C7.69998 8.45331 7.66665 8.69331 7.66665 8.99998H6.33331V8.66665C6.33331 8.35998 6.38665 8.06665 6.47998 7.79331C6.61331 7.40665 6.83331 7.05998 7.11331 6.77998L7.93998 5.93998C8.24665 5.64665 8.39331 5.20665 8.30665 4.73998C8.21998 4.25998 7.84665 3.85331 7.37998 3.71998C6.63998 3.51331 5.95331 3.93331 5.73331 4.56665C5.65331 4.81331 5.44665 4.99998 5.18665 4.99998H4.98665C4.59998 4.99998 4.33331 4.62665 4.43998 4.25331C4.72665 3.27331 5.55998 2.52665 6.59331 2.36665C7.60665 2.20665 8.57331 2.73331 9.17331 3.56665C9.95998 4.65331 9.72665 5.81998 9.04665 6.49998Z"
                    fill="currentColor"
                />
            </svg>
        );
    },
    copyLink: (props: IconProps) => {
        const fg = props.fgColor ?? "none";
        return (
            <svg width="12" height="10" viewBox="0 0 12 10" fill={fg} xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M11.8512 0.275002H2.8512V6.875H11.8512V0.275002ZM1.9512 8.225H8.3616C8.56052 8.225 8.75128 8.30402 8.89193 8.44467C9.03259 8.58532 9.1116 8.77609 9.1116 8.975C9.1116 9.17391 9.03259 9.36468 8.89193 9.50533C8.75128 9.64598 8.56052 9.725 8.3616 9.725H1.9512C1.434 9.72405 0.93826 9.51817 0.572545 9.15246C0.20683 8.78674 0.000951609 8.291 0 7.7738V3.5738C0 3.37489 0.0790177 3.18412 0.21967 3.04347C0.360322 2.90282 0.551088 2.8238 0.75 2.8238C0.948913 2.8238 1.13968 2.90282 1.28033 3.04347C1.42098 3.18412 1.5 3.37489 1.5 3.5738V7.7738C1.50032 7.89337 1.54796 8.00795 1.6325 8.0925C1.71705 8.17704 1.83163 8.22468 1.9512 8.225Z"
                    fill="#B2B2C0"
                />
            </svg>
        );
    },
    analogTimer: (props: IconProps) => {
        const fg = props.fgColor ?? "none";
        return (
            <svg width="440" height="440" viewBox="0 0 240 240" fill={fg} xmlns="http://www.w3.org/2000/svg">
                <circle cx="120" cy="120" r="84" stroke="#999999" strokeWidth="6" strokeDasharray="2 6" />
            </svg>
        );
    },
    collaborators: (props: IconProps) => {
        const fg = props.fgColor ?? "none";
        return (
            <svg width="13" height="9" viewBox="0 0 13 9" fill={fg} xmlns="http://www.w3.org/2000/svg">
                <path
                    opacity="0.5"
                    d="M8.4043 4.7832C9.34473 4.7832 10.1577 3.93945 10.1577 2.84082C10.1577 1.75537 9.34033 0.951172 8.4043 0.951172C7.46387 0.951172 6.64209 1.77295 6.64648 2.84961C6.64648 3.93945 7.45947 4.7832 8.4043 4.7832ZM3.3418 4.88428C4.16357 4.88428 4.87109 4.146 4.87109 3.19238C4.87109 2.24756 4.15918 1.54883 3.3418 1.54883C2.52881 1.54883 1.80811 2.26514 1.8125 3.20117C1.8125 4.146 2.52441 4.88428 3.3418 4.88428ZM0.894043 8.85254H4.22949C3.77246 8.18896 4.33057 6.85303 5.27979 6.12354C4.7876 5.79834 4.15918 5.55664 3.3418 5.55664C1.35986 5.55664 0.120605 7.02002 0.120605 8.2373C0.120605 8.63281 0.335938 8.85254 0.894043 8.85254ZM5.64014 8.85254H11.1597C11.854 8.85254 12.1001 8.65479 12.1001 8.26807C12.1001 7.13428 10.6807 5.56982 8.3999 5.56982C6.12354 5.56982 4.7041 7.13428 4.7041 8.26807C4.7041 8.65479 4.9502 8.85254 5.64014 8.85254Z"
                    fill="#737382"
                />
            </svg>
        );
    },
    avatar: (props: IconProps) => {
        const fg = props.fgColor ?? "none";
        return (
            <svg width="17" height="17" viewBox="0 0 17 17" fill={fg} xmlns="http://www.w3.org/2000/svg">
                <path
                    opacity="0.75"
                    d="M8.36719 16.3359C12.7266 16.3359 16.3359 12.7266 16.3359 8.36719C16.3359 4.01562 12.7188 0.398438 8.35938 0.398438C4.00781 0.398438 0.398438 4.01562 0.398438 8.36719C0.398438 12.7266 4.01562 16.3359 8.36719 16.3359ZM8.35938 8.125C7.21875 8.11719 6.28906 7.15625 6.28906 5.85938C6.28906 4.65625 7.21875 3.65625 8.35938 3.65625C9.5 3.65625 10.4297 4.65625 10.4297 5.85938C10.4297 7.15625 9.5 8.13281 8.35938 8.125ZM4.67969 12.4609C4.35156 12.4609 4.20312 12.25 4.20312 11.9531C4.20312 11.0781 5.53125 8.78906 8.35938 8.78906C11.1875 8.78906 12.5156 11.0781 12.5156 11.9531C12.5156 12.25 12.3672 12.4609 12.0391 12.4609H4.67969Z"
                    fill="#737382"
                />
            </svg>
        );
    },
    formFactorPhone: (props: IconProps) => {
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect
                    x="6"
                    y="1"
                    width="12"
                    height="22"
                    rx="3.5"
                    fill={fg}
                    fillOpacity="0.16"
                    stroke={fg}
                    strokeWidth="1.5"
                    vectorEffect="non-scaling-stroke"
                />
                <rect x="9" y="20" width="6" height="1" rx="0.5" fill={fg} />
            </svg>
        );
    },
    formFactorTablet: (props: IconProps) => {
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect
                    x="1.25"
                    y="3.75"
                    width="21.5"
                    height="16.5"
                    rx="3.75"
                    fill={fg}
                    fillOpacity="0.16"
                    stroke={fg}
                    strokeWidth="1.5"
                    vectorEffect="non-scaling-stroke"
                />
                <rect x="9" y="18" width="6" height="1" rx="0.5" fill={fg} vectorEffect="non-scaling-stroke" />
            </svg>
        );
    },
    formFactorDesktop: (props: IconProps) => {
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0.333984" y="21" width="24" height="1.5" rx="0.75" fill={fg} />
                <rect
                    x="1.08398"
                    y="2.75"
                    width="22.5"
                    height="16.5"
                    rx="3.75"
                    fill={fg}
                    fillOpacity="0.16"
                    stroke={fg}
                    strokeWidth="1.5"
                    vectorEffect="non-scaling-stroke"
                />
            </svg>
        );
    },
    appLayout: (props: IconProps) => {
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect
                    x="6"
                    y="1"
                    width="12"
                    height="22"
                    rx="3.5"
                    fill={fg}
                    fillOpacity="0.16"
                    stroke={fg}
                    strokeWidth="1.5"
                />
                <rect x="9" y="20" width="6" height="1" rx="0.5" fill={fg} />
            </svg>
        );
    },
    dataEditor: (props: IconProps) => {
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect
                    x="1"
                    y="2"
                    width="22"
                    height="20"
                    rx="4"
                    fill={fg}
                    fillOpacity="0.16"
                    stroke={fg}
                    strokeWidth="1.5"
                />
                <path opacity="0.25" d="M1 4C1 2.89543 1.89543 2 3 2H21C22.1046 2 23 2.89543 23 4V8H1V4Z" fill={fg} />
                <line x1="1" y1="8.25" x2="23" y2="8.25" stroke={fg} strokeWidth="1.5" />
                <line x1="1" y1="15.25" x2="23" y2="15.25" stroke={fg} strokeWidth="1.5" />
                <line x1="12.25" y1="22" x2="12.25" y2="8" stroke={fg} strokeWidth="1.5" />
            </svg>
        );
    },
    settingsGear: (props: IconProps) => {
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.21749 3.10408L9.21748 3.10409L5.68716 5.14232L5.68716 5.14232C4.6842 5.72138 4.18272 6.01091 3.81595 6.41398C3.47754 6.78589 3.22323 7.22637 3.07035 7.70541C2.90466 8.22456 2.90466 8.80362 2.90466 9.96175V14.0382C2.90466 15.1963 2.90466 15.7754 3.07035 16.2946C3.22323 16.7736 3.47754 17.2141 3.81595 17.586C4.18272 17.989 4.6842 18.2786 5.68716 18.8576L9.21748 20.8959C10.2204 21.4749 10.7219 21.7645 11.2544 21.8806C11.7457 21.9877 12.2543 21.9877 12.7456 21.8806C13.278 21.7645 13.7795 21.4749 14.7825 20.8959L18.3128 18.8576L18.3128 18.8576C19.3158 18.2786 19.8172 17.989 20.184 17.586C20.5224 17.2141 20.7767 16.7736 20.9296 16.2946C21.0953 15.7754 21.0953 15.1963 21.0953 14.0383V14.0382V9.96175V9.96171C21.0953 8.80361 21.0953 8.22456 20.9296 7.70541C20.7767 7.22637 20.5224 6.78589 20.184 6.41398C19.8172 6.01091 19.3158 5.72138 18.3128 5.14232L18.3128 5.14232L14.7825 3.10409C13.7795 2.52503 13.278 2.2355 12.7456 2.1194C12.2543 2.01228 11.7457 2.01228 11.2544 2.1194C10.7219 2.2355 10.2204 2.52502 9.21749 3.10408ZM15.5001 12C15.5001 10.067 13.9331 8.49998 12.0001 8.49998C10.0671 8.49998 8.50008 10.067 8.50008 12C8.50008 13.933 10.0671 15.5 12.0001 15.5C13.9331 15.5 15.5001 13.933 15.5001 12Z"
                    fill={fg}
                    fillOpacity="0.16"
                />
                <path
                    d="M9.21748 3.10409L9.59254 3.7537L9.60336 3.74721L9.21748 3.10409ZM9.21749 3.10408L8.84244 2.45447L8.83162 2.46096L9.21749 3.10408ZM5.68716 5.14232L5.31216 4.4928L5.22665 4.54217L5.15683 4.61199L5.68716 5.14232ZM5.68716 5.14232L6.06216 5.79184L6.14767 5.74247L6.21749 5.67265L5.68716 5.14232ZM3.81595 6.41398L4.37068 6.91874L3.81595 6.41398ZM3.07035 7.70541L2.35585 7.47738L2.35585 7.47738L3.07035 7.70541ZM3.07035 16.2946L2.35585 16.5226L2.35585 16.5226L3.07035 16.2946ZM3.81595 17.586L3.26123 18.0907L3.26123 18.0907L3.81595 17.586ZM5.68716 18.8576L6.06216 18.2081L6.06216 18.2081L5.68716 18.8576ZM9.21748 20.8959L9.59248 20.2464L9.59248 20.2464L9.21748 20.8959ZM11.2544 21.8806L11.0946 22.6133L11.0946 22.6133L11.2544 21.8806ZM12.7456 21.8806L12.9054 22.6133L12.9054 22.6133L12.7456 21.8806ZM14.7825 20.8959L14.4075 20.2464L14.4075 20.2464L14.7825 20.8959ZM18.3128 18.8576L18.6878 19.5072L18.737 19.4788L18.7813 19.4433L18.3128 18.8576ZM18.3128 18.8576L17.9378 18.2081L17.8886 18.2365L17.8443 18.272L18.3128 18.8576ZM20.184 17.586L19.6293 17.0812L19.6293 17.0812L20.184 17.586ZM20.9296 16.2946L20.2151 16.0665L20.2151 16.0665L20.9296 16.2946ZM20.9296 7.70541L20.2151 7.93343L20.2151 7.93343L20.9296 7.70541ZM20.184 6.41398L19.6293 6.91874L19.6293 6.91874L20.184 6.41398ZM18.3128 5.14232L17.9349 5.79016L17.9378 5.79184L18.3128 5.14232ZM18.3128 5.14232L18.6907 4.49448L18.6878 4.4928L18.3128 5.14232ZM14.7825 3.10409L14.4075 3.75361L14.4075 3.75361L14.7825 3.10409ZM12.7456 2.1194L12.9054 1.38662L12.9054 1.38662L12.7456 2.1194ZM11.2544 2.1194L11.4141 2.85219L11.4141 2.85219L11.2544 2.1194ZM9.60336 3.74721L9.60336 3.7472L8.83162 2.46096L8.83161 2.46097L9.60336 3.74721ZM6.06216 5.79184L9.59248 3.75361L8.84248 2.45457L5.31216 4.4928L6.06216 5.79184ZM6.21749 5.67265L6.21749 5.67265L5.15683 4.61199L5.15683 4.61199L6.21749 5.67265ZM4.37068 6.91874C4.64019 6.62255 5.01615 6.39575 6.06216 5.79184L5.31216 4.4928C4.35224 5.04701 3.72524 5.39927 3.26123 5.90922L4.37068 6.91874ZM3.78484 7.93343C3.90496 7.55704 4.10478 7.21096 4.37068 6.91874L3.26123 5.90922C2.85029 6.36083 2.54149 6.89569 2.35585 7.47738L3.78484 7.93343ZM3.65466 9.96175C3.65466 8.75392 3.66309 8.31493 3.78484 7.93343L2.35585 7.47738C2.14623 8.1342 2.15466 8.85333 2.15466 9.96175H3.65466ZM3.65466 14.0382V9.96175H2.15466V14.0382H3.65466ZM3.78484 16.0665C3.66309 15.685 3.65466 15.246 3.65466 14.0382H2.15466C2.15466 15.1466 2.14623 15.8658 2.35585 16.5226L3.78484 16.0665ZM4.37068 17.0812C4.10478 16.789 3.90496 16.4429 3.78484 16.0665L2.35585 16.5226C2.54149 17.1043 2.85029 17.6391 3.26123 18.0907L4.37068 17.0812ZM6.06216 18.2081C5.01615 17.6042 4.64019 17.3774 4.37068 17.0812L3.26123 18.0907C3.72524 18.6007 4.35224 18.953 5.31216 19.5072L6.06216 18.2081ZM9.59248 20.2464L6.06216 18.2081L5.31216 19.5072L8.84248 21.5454L9.59248 20.2464ZM11.4141 21.1478C11.0229 21.0625 10.6385 20.8503 9.59248 20.2464L8.84248 21.5454C9.8024 22.0996 10.421 22.4665 11.0946 22.6133L11.4141 21.1478ZM12.5858 21.1478C12.1998 21.2319 11.8002 21.2319 11.4141 21.1478L11.0946 22.6133C11.6912 22.7434 12.3088 22.7434 12.9054 22.6133L12.5858 21.1478ZM14.4075 20.2464C13.3615 20.8503 12.9771 21.0625 12.5858 21.1478L12.9054 22.6133C13.579 22.4665 14.1976 22.0996 15.1575 21.5454L14.4075 20.2464ZM17.9378 18.2081L14.4075 20.2464L15.1575 21.5454L18.6878 19.5072L17.9378 18.2081ZM17.8443 18.272L17.8443 18.272L18.7813 19.4433L18.7813 19.4433L17.8443 18.272ZM19.6293 17.0812C19.3598 17.3774 18.9838 17.6042 17.9378 18.2081L18.6878 19.5071C19.6477 18.9529 20.2747 18.6007 20.7387 18.0907L19.6293 17.0812ZM20.2151 16.0665C20.095 16.4429 19.8952 16.789 19.6293 17.0812L20.7387 18.0907C21.1497 17.6391 21.4585 17.1043 21.6441 16.5226L20.2151 16.0665ZM20.3453 14.0383C20.3453 15.2461 20.3369 15.685 20.2151 16.0665L21.6441 16.5226C21.8537 15.8658 21.8453 15.1466 21.8453 14.0383H20.3453ZM20.3453 14.0382V14.0383H21.8453V14.0382H20.3453ZM20.3453 9.96175V14.0382H21.8453V9.96175H20.3453ZM20.3453 9.96171V9.96175H21.8453V9.96171H20.3453ZM20.2151 7.93343C20.3369 8.31492 20.3453 8.75391 20.3453 9.96171H21.8453C21.8453 8.85332 21.8537 8.13419 21.6441 7.47738L20.2151 7.93343ZM19.6293 6.91874C19.8952 7.21096 20.095 7.55704 20.2151 7.93343L21.6441 7.47738C21.4585 6.89569 21.1497 6.36083 20.7387 5.90922L19.6293 6.91874ZM17.9378 5.79184C18.9838 6.39575 19.3598 6.62255 19.6293 6.91874L20.7387 5.90922C20.2747 5.39928 19.6477 5.04701 18.6878 4.4928L17.9378 5.79184ZM17.9349 5.79015L17.9349 5.79016L18.6907 4.49449L18.6907 4.49448L17.9349 5.79015ZM14.4075 3.75361L17.9378 5.79184L18.6878 4.4928L15.1575 2.45457L14.4075 3.75361ZM12.5858 2.85219C12.9771 2.9375 13.3615 3.14969 14.4075 3.75361L15.1575 2.45457C14.1976 1.90036 13.579 1.53349 12.9054 1.38662L12.5858 2.85219ZM11.4141 2.85219C11.8002 2.76802 12.1998 2.76802 12.5858 2.85219L12.9054 1.38662C12.3088 1.25654 11.6912 1.25654 11.0946 1.38662L11.4141 2.85219ZM9.59249 3.7536C10.6385 3.14969 11.0229 2.9375 11.4141 2.85219L11.0946 1.38662C10.421 1.53349 9.8024 1.90036 8.84249 2.45456L9.59249 3.7536ZM12.0001 9.24998C13.5189 9.24998 14.7501 10.4812 14.7501 12H16.2501C16.2501 9.65277 14.3473 7.74998 12.0001 7.74998V9.24998ZM9.25008 12C9.25008 10.4812 10.4813 9.24998 12.0001 9.24998V7.74998C9.65287 7.74998 7.75008 9.65277 7.75008 12H9.25008ZM12.0001 14.75C10.4813 14.75 9.25008 13.5188 9.25008 12H7.75008C7.75008 14.3472 9.65287 16.25 12.0001 16.25V14.75ZM14.7501 12C14.7501 13.5188 13.5189 14.75 12.0001 14.75V16.25C14.3473 16.25 16.2501 14.3472 16.2501 12H14.7501Z"
                    fill={fg}
                />
            </svg>
        );
    },
    caret: (props: IconProps) => {
        const { stroke, strokeWidth, size, degreesOfRotation } = props;
        const lineColor = stroke ?? "currentColor";
        const lineWidth = strokeWidth ?? 1.5;
        const iconSize = size ?? 10;
        const degrees: number = degreesOfRotation ?? 0;
        return (
            <svg
                style={{
                    width: `${iconSize}px`,
                    height: `${iconSize}px`,
                    fill: `none`,
                    transformOrigin: "50% 50%",
                    transition: "150ms ease-out transform",
                    transform: `rotate(${degrees}deg)`,
                }}
                viewBox={`0 0 10 6`}
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                    vectorEffect="non-scaling-stroke"
                    d="M1 1L5.18182 5L9 1"
                    stroke={lineColor}
                    strokeWidth={lineWidth}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        );
    },
    reloadArrow: (props: IconProps) => {
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    vectorEffect="non-scaling-stroke"
                    d="M17.8336 17.8336C16.6798 18.9874 15.2098 19.7732 13.6095 20.0915C12.0092 20.4098 10.3504 20.2464 8.84286 19.622C7.33537 18.9976 6.0469 17.9402 5.14038 16.5835C4.23385 15.2268 3.75 13.6317 3.75 12C3.75 10.3683 4.23385 8.77325 5.14038 7.41655C6.0469 6.05984 7.33537 5.00242 8.84286 4.378C10.3504 3.75357 12.0092 3.5902 13.6095 3.90853C15.2098 4.22685 16.6798 5.01259 17.8336 6.16637L21.0156 9.34835M14.5156 9.34815H21.0156V2.84814"
                    stroke={fg}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                />
            </svg>
        );
    },
    plusStroke: (props: IconProps) => {
        const { stroke, degreesOfRotation } = props;
        const fg = props.fgColor ?? "currentColor";
        const strokeWidth = stroke ?? "1.5";
        const degrees = degreesOfRotation ?? 0;
        return (
            <svg
                style={{
                    transformOrigin: "50% 50%",
                    transition: "transform 150ms",
                    transform: `rotate(${degrees}deg)`,
                }}
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                    vectorEffect="non-scaling-stroke"
                    d="M12 20.4V12M12 12V3.60001M12 12H20.4M12 12L3.59998 12"
                    stroke={fg}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        );
    },
    undoStroke: (props: IconProps) => {
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    vectorEffect="non-scaling-stroke"
                    d="M4.12367 16.0593L4.34784 8.78262M4.12367 16.0593L11.3478 16.0593M4.12367 16.0593L5.26088 16.0593C6.17392 9.39131 18.0435 7.59235 20.4925 14.94"
                    stroke={fg}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        );
    },
    redoStroke: (props: IconProps) => {
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M20.0055 16.0592L19.7814 8.78256M20.0055 16.0592L12.7814 16.0592M20.0055 16.0592L18.8683 16.0592C17.9553 9.39125 6.08571 7.59229 3.63664 14.9399"
                    stroke={fg}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        );
    },
    duplicate: (props: IconProps) => {
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M14.5 3.5H5.5C4.39543 3.5 3.5 4.39543 3.5 5.5V14.5C3.5 15.2145 3.87462 15.8414 4.43815 16.1951C4.74427 16.3873 5 16.6896 5 17.0511C5 17.5332 4.56597 17.9085 4.12282 17.7186C2.87451 17.1838 2 15.944 2 14.5V5.5C2 3.567 3.567 2 5.5 2H14.5C15.944 2 17.1838 2.87451 17.7186 4.12282C17.9085 4.56597 17.5332 5 17.0511 5C16.6896 5 16.3873 4.74427 16.1951 4.43815C15.8414 3.87462 15.2145 3.5 14.5 3.5ZM9.5 7.5H18.5C19.6046 7.5 20.5 8.39543 20.5 9.5V18.5C20.5 19.6046 19.6046 20.5 18.5 20.5H9.5C8.39543 20.5 7.5 19.6046 7.5 18.5V9.5C7.5 8.39543 8.39543 7.5 9.5 7.5ZM6 9.5C6 7.567 7.567 6 9.5 6H18.5C20.433 6 22 7.567 22 9.5V18.5C22 20.433 20.433 22 18.5 22H9.5C7.567 22 6 20.433 6 18.5V9.5Z"
                    fill={fg}
                />
            </svg>
        );
    },
    glideTable: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2 6C2 4.34315 3.34315 3 5 3H19C20.6569 3 22 4.34315 22 6V18C22 19.6569 20.6569 21 19 21H11.6586C11.8797 20.3744 12 19.7013 12 19C12 15.6863 9.31371 13 6 13C4.46329 13 3.06151 13.5777 2 14.5278V6ZM15.75 5.875C15.75 5.39175 15.3582 5 14.875 5C14.3918 5 14 5.39175 14 5.875V8H4.875C4.39175 8 4 8.39175 4 8.875C4 9.35825 4.39175 9.75 4.875 9.75H14V18.125C14 18.6082 14.3918 19 14.875 19C15.3582 19 15.75 18.6082 15.75 18.125V9.75H19.125C19.6082 9.75 20 9.35825 20 8.875C20 8.39175 19.6082 8 19.125 8H15.75V5.875Z"
                    fill={bg}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M11 19C11 21.7614 8.76142 24 6 24C3.23858 24 1 21.7614 1 19C1 16.2386 3.23858 14 6 14C8.76142 14 11 16.2386 11 19ZM6.375 15.25L2.25 19.375H4.275C5.4348 19.375 6.375 18.4348 6.375 17.275V15.25ZM7.725 18.625C6.5652 18.625 5.625 19.5652 5.625 20.725V22.75L9.75 18.625H7.725Z"
                    fill={fg}
                />
            </svg>
        );
    },
    glideTableFixedColor: (props: IconProps) => {
        const bg = props.bgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2 6C2 4.34315 3.34315 3 5 3H19C20.6569 3 22 4.34315 22 6V18C22 19.6569 20.6569 21 19 21H11.6586C11.8797 20.3744 12 19.7013 12 19C12 15.6863 9.31371 13 6 13C4.46329 13 3.06151 13.5777 2 14.5278V6ZM15.75 5.875C15.75 5.39175 15.3582 5 14.875 5C14.3918 5 14 5.39175 14 5.875V8H4.875C4.39175 8 4 8.39175 4 8.875C4 9.35825 4.39175 9.75 4.875 9.75H14V18.125C14 18.6082 14.3918 19 14.875 19C15.3582 19 15.75 18.6082 15.75 18.125V9.75H19.125C19.6082 9.75 20 9.35825 20 8.875C20 8.39175 19.6082 8 19.125 8H15.75V5.875Z"
                    fill={bg}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M11 19C11 21.7614 8.76142 24 6 24C3.23858 24 1 21.7614 1 19C1 16.2386 3.23858 14 6 14C8.76142 14 11 16.2386 11 19ZM6.375 15.25L2.25 19.375H4.275C5.4348 19.375 6.375 18.4348 6.375 17.275V15.25ZM7.725 18.625C6.5652 18.625 5.625 19.5652 5.625 20.725V22.75L9.75 18.625H7.725Z"
                    fill={bg}
                />
            </svg>
        );
    },
    avatarEmpty: (props: IconProps) => {
        const bg = props.fgColor ?? "currentColor";
        return (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="10" r="10" fill={bg} />
                <path
                    d="M3.0305 17.1712C3.3373 15.1392 5.93879 12.6182 9.90105 12.6182C13.9658 12.6182 16.6081 15.2815 16.7918 17.3397C15.0084 18.9908 12.6221 20 10 20C7.28988 20 4.83156 18.9219 3.0305 17.1712Z"
                    fill="white"
                />
                <path
                    d="M13.2132 7.54818C13.2132 9.59082 11.7077 11.1859 9.90105 11.1859C8.09441 11.1859 6.59701 9.59082 6.59701 7.55631C6.58888 5.57878 8.11069 4 9.90105 4C11.6996 4 13.2132 5.5625 13.2132 7.54818Z"
                    fill="white"
                />
            </svg>
        );
    },
    moreVertical: (props: IconProps) => {
        const bg = props.fgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M12 6C13.6569 6 15 4.65685 15 3C15 1.34315 13.6569 0 12 0C10.3431 0 9 1.34315 9 3C9 4.65685 10.3431 6 12 6Z"
                    fill={bg}
                />
                <path
                    d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                    fill={bg}
                />
                <path
                    d="M12 24C13.6569 24 15 22.6569 15 21C15 19.3431 13.6569 18 12 18C10.3431 18 9 19.3431 9 21C9 22.6569 10.3431 24 12 24Z"
                    fill={bg}
                />
            </svg>
        );
    },
    users: (props: IconProps) => {
        const fg = props.fgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M16.5586 11.6641C18.4482 11.6641 20.0742 9.97656 20.0742 7.7793C20.0742 5.6084 18.4395 4 16.5586 4C14.6865 4 13.043 5.64355 13.0518 7.79688C13.0518 9.97656 14.6777 11.6641 16.5586 11.6641ZM6.44238 11.8662C8.08594 11.8662 9.50098 10.3896 9.50098 8.48242C9.50098 6.59277 8.07715 5.19531 6.44238 5.19531C4.81641 5.19531 3.375 6.62793 3.38379 8.5C3.38379 10.3896 4.80762 11.8662 6.44238 11.8662ZM1.54688 19.8027C5.5 20.5 8.21777 20 8.21777 19.8027C7.30371 18.4756 8.41992 15.8037 10.3184 14.3447C9.33398 13.6943 8.07715 13.2109 6.44238 13.2109C2.47852 13.2109 0 16.1377 0 18.5723C0 19.3633 0.59375 19.6055 1.54688 19.8027ZM11.0391 20C15.0665 20.6242 18.0704 20.5471 22.0781 20C23.1562 19.8027 23.9502 19.6045 23.9502 18.8311C23.9502 16.5635 21.1113 13.2373 16.5586 13.2373C11.9971 13.2373 9.1582 16.5635 9.1582 18.8311C9.1582 19.6045 10.0781 19.8027 11.0391 20Z"
                    fill={fg}
                />
            </svg>
        );
    },
    successCheck: (props: IconProps) => {
        const { fgColor } = props;
        const fg = fgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fill="none"
                    vectorEffect="non-scaling-stroke"
                    d="M19 6L10.3802 17L5.34071 11.8758"
                    stroke={fg}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        );
    },
    close: (props: IconProps) => {
        const { fgColor } = props;
        const fg = fgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    vectorEffect="non-scaling-stroke"
                    d="M17.9397 17.9398L12 12.0001M12 12.0001L6.06028 6.06043M12 12.0001L17.9397 6.06042M12 12.0001L6.06028 17.9398"
                    stroke={fg}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        );
    },
    lock: (props: IconProps) => {
        const { fgColor, size } = props;
        const fg = fgColor ?? "currentColor";
        const iconSize = size ?? 24;
        return (
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7.5 10.8202V7.99951C7.5 5.51423 9.51472 3.49951 12 3.49951C14.4853 3.49951 16.5 5.51423 16.5 7.99951V10.8202C16.6029 10.8512 16.6976 10.8892 16.7872 10.9357C17.1981 11.1492 17.5332 11.4842 17.7467 11.8952C18 12.3828 18 13.0235 18 14.3049V17.9813C18 19.0491 18 19.583 17.7889 19.9894C17.611 20.3318 17.3318 20.611 16.9893 20.7889C16.5829 21 16.049 21 14.9813 21H9.01875C7.95095 21 7.41706 21 7.01067 20.7889C6.66822 20.611 6.38899 20.3318 6.2111 19.9894C6 19.583 6 19.0491 6 17.9813V14.3049C6 13.0235 6 12.3828 6.25332 11.8952C6.46679 11.4842 6.80186 11.1492 7.21281 10.9357C7.30237 10.8892 7.39709 10.8512 7.5 10.8202ZM9 7.99951C9 6.34266 10.3431 4.99951 12 4.99951C13.6569 4.99951 15 6.34266 15 7.99951V10.6838C14.8107 10.6824 14.6041 10.6824 14.3775 10.6824H9.6225C9.39585 10.6824 9.18925 10.6824 9 10.6838V7.99951Z"
                    fill={fg}
                />
            </svg>
        );
    },
    envelope: (props: IconProps) => {
        const { fgColor } = props;
        const fg = fgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z"
                    stroke={fg}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                    vectorEffect="non-scaling-stroke"
                />
                <path
                    d="M22 6L12 13L2 6"
                    stroke={fg}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                    vectorEffect="non-scaling-stroke"
                />
            </svg>
        );
    },
    play: (props: IconProps) => {
        const { fgColor } = props;
        const fg = fgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M6 18.567V4.71319C6 3.90175 7.19313 3.42677 8.03085 3.92155L19.8859 10.8485C20.5714 11.2443 20.5714 12.0359 19.8859 12.4318L8.03085 19.3587C7.19313 19.8535 6 19.3785 6 18.567Z"
                    fill={fg}
                />
            </svg>
        );
    },
    infoCircle: (props: IconProps) => {
        const { fgColor } = props;
        const fg = fgColor ?? "currentColor";
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.66667 12C3.66667 7.39763 7.39763 3.66667 12 3.66667C16.6024 3.66667 20.3333 7.39763 20.3333 12C20.3333 16.6024 16.6024 20.3333 12 20.3333C7.39763 20.3333 3.66667 16.6024 3.66667 12ZM12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM12.1623 9.82496C12.7061 9.82496 13.1453 10.1282 13.1558 10.6825L13.1558 16.3818C13.1663 16.9361 12.7584 17.3335 12.1728 17.3335C11.5976 17.3335 11.1689 16.9256 11.1793 16.3714L11.1793 10.6825C11.1898 10.1178 11.6395 9.82496 12.1623 9.82496ZM12.1623 6.3217C12.7898 6.3217 13.3336 6.82366 13.3336 7.44065C13.3336 8.05764 12.8002 8.57006 12.1623 8.57006C11.5244 8.57006 10.9911 8.0681 10.9911 7.44065C10.9911 6.8132 11.5349 6.3217 12.1623 6.3217Z"
                    fill={fg}
                />
            </svg>
        );
    },
    gridLoose: ({ fgColor }: IconProps) => {
        return (
            <svg viewBox="0 0 253 118" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0)">
                    <g opacity="0.75">
                        <line y1="15.125" x2="253" y2="15.125" stroke={fgColor} />
                        <line y1="29.75" x2="253" y2="29.75" stroke={fgColor} />
                        <line y1="44.375" x2="253" y2="44.375" stroke={fgColor} />
                        <line y1="59" x2="253" y2="59" stroke={fgColor} />
                        <line y1="73.625" x2="253" y2="73.625" stroke={fgColor} />
                        <line y1="88.25" x2="253" y2="88.25" stroke={fgColor} />
                        <line y1="102.875" x2="253" y2="102.875" stroke={fgColor} />
                        <line y1="117.5" x2="253" y2="117.5" stroke={fgColor} />
                    </g>
                    <g opacity="0.75">
                        <line x1="42.5" y1="2.18557e-08" x2="42.5" y2="119" stroke={fgColor} />
                        <line x1="84.5" y1="2.18557e-08" x2="84.5" y2="119" stroke={fgColor} />
                        <line x1="126.5" y1="2.18557e-08" x2="126.5" y2="119" stroke={fgColor} />
                        <line x1="168.5" y1="2.18557e-08" x2="168.5" y2="119" stroke={fgColor} />
                        <line x1="210.5" y1="2.18557e-08" x2="210.5" y2="119" stroke={fgColor} />
                    </g>
                </g>
                <defs>
                    <clipPath id="clip0">
                        <path d="M0 9C0 4.02944 4.02944 0 9 0H244C248.971 0 253 4.02944 253 9V118H0V9Z" fill="none" />
                    </clipPath>
                </defs>
            </svg>
        );
    },
    gridTight: (p: IconProps) => {
        const { fgColor } = p;
        return (
            <svg viewBox="0 0 253 118" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0)">
                    <g opacity="0.75">
                        <line y1="11.1364" x2="253" y2="11.1364" stroke={fgColor} />
                        <line y1="21.7727" x2="253" y2="21.7727" stroke={fgColor} />
                        <line y1="32.4091" x2="253" y2="32.4091" stroke={fgColor} />
                        <line y1="43.0454" x2="253" y2="43.0454" stroke={fgColor} />
                        <line y1="53.6818" x2="253" y2="53.6818" stroke={fgColor} />
                        <line y1="64.3182" x2="253" y2="64.3182" stroke={fgColor} />
                        <line y1="74.9546" x2="253" y2="74.9546" stroke={fgColor} />
                        <line y1="85.5909" x2="253" y2="85.5909" stroke={fgColor} />
                        <line y1="96.2273" x2="253" y2="96.2273" stroke={fgColor} />
                        <line y1="106.864" x2="253" y2="106.864" stroke={fgColor} />
                        <line y1="117.5" x2="253" y2="117.5" stroke={fgColor} />
                    </g>
                    <g opacity="0.75">
                        <line x1="28.5" y1="2.18557e-08" x2="28.5" y2="119" stroke={fgColor} />
                        <line x1="56.5" y1="2.18557e-08" x2="56.5" y2="119" stroke={fgColor} />
                        <line x1="84.5" y1="2.18557e-08" x2="84.5" y2="119" stroke={fgColor} />
                        <line x1="112.5" y1="2.18557e-08" x2="112.5" y2="119" stroke={fgColor} />
                        <line x1="140.5" y1="2.18557e-08" x2="140.5" y2="119" stroke={fgColor} />
                        <line x1="168.5" y1="2.18557e-08" x2="168.5" y2="119" stroke={fgColor} />
                        <line x1="196.5" y1="2.18557e-08" x2="196.5" y2="119" stroke={fgColor} />
                        <line x1="224.5" y1="2.18557e-08" x2="224.5" y2="119" stroke={fgColor} />
                    </g>
                </g>
                <defs>
                    <clipPath id="clip0">
                        <path d="M0 9C0 4.02944 4.02944 0 9 0H244C248.971 0 253 4.02944 253 9V118H0V9Z" fill="none" />
                    </clipPath>
                </defs>
            </svg>
        );
    },
    lockIcon: (p: IconProps) => {
        const { fgColor, bgColor } = p;
        return (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="16" height="16" rx="8" fill={bgColor} />
                <path
                    d="M5.83984 11.957H9.74609C10.3125 11.957 10.5898 11.6758 10.5898 11.0625V8.04297C10.5898 7.49219 10.3594 7.20703 9.89453 7.16016V6.23828C9.89453 4.73438 8.85938 4 7.79297 4C6.72656 4 5.69141 4.73438 5.69141 6.23828V7.16797C5.24609 7.22656 5 7.50781 5 8.04297V11.0625C5 11.6758 5.27734 11.957 5.83984 11.957ZM6.66406 6.18359C6.66406 5.39844 7.15625 4.9375 7.79297 4.9375C8.42969 4.9375 8.92578 5.39844 8.92578 6.18359V7.15625H6.66406V6.18359Z"
                    fill={fgColor}
                />
            </svg>
        );
    },
    listCheck: (p: IconProps) => {
        const { fgColor } = p;
        return (
            <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M12.6667 4.5L6.92023 11.8333L3.56055 8.4172"
                    stroke={fgColor}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        );
    },
    listReturn: (p: IconProps) => {
        const { fgColor } = p;
        return (
            <svg width="13" height="11" viewBox="0 0 13 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M1.50098 0.0532227C0.974121 0.0532227 0.751953 0.459473 0.688477 0.814941C0.631348 1.11963 0.618652 1.4624 0.618652 1.83057C0.618652 4.73779 1.86279 6.12158 4.73193 6.12158H8.72461L10.2036 6.03906L8.55957 7.52441L7.13135 8.96533C6.979 9.11768 6.88379 9.30811 6.88379 9.54932C6.88379 9.99365 7.21387 10.3237 7.68359 10.3237C7.88672 10.3237 8.0835 10.2412 8.26758 10.0571L12.4189 5.91211C12.5776 5.75342 12.6729 5.53125 12.6729 5.30908C12.6729 5.08691 12.5776 4.8584 12.4189 4.69971L8.25488 0.54834C8.0835 0.383301 7.88672 0.294434 7.68359 0.294434C7.21387 0.294434 6.88379 0.630859 6.88379 1.06885C6.88379 1.31006 6.979 1.50684 7.13135 1.65283L8.55957 3.09375L10.21 4.58545L8.72461 4.49658H4.84619C2.94824 4.49658 2.2627 3.71582 2.2627 1.79883C2.2627 1.40527 2.28809 1.11328 2.28809 0.859375C2.28809 0.376953 1.9707 0.0532227 1.50098 0.0532227Z"
                    fill={fgColor}
                />
            </svg>
        );
    },
    listPlus: (p: IconProps) => {
        const { fgColor } = p;
        return (
            <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M6.99985 10.2V6.00002M6.99985 6.00002V1.80002M6.99985 6.00002H11.8999M6.99985 6.00002L2.09985 6.00002"
                    stroke={fgColor}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        );
    },
    mapPin: (p: IconProps) => {
        const { bgColor, accentColor } = p;
        // this color was the original hardcoded value
        const accent = accentColor ?? "#EA585A";
        const bg = bgColor ?? "transparent";
        return (
            <svg width="20" height="24" viewBox="0 0 20 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M20 9.81818C20 16.1306 13.1668 21.6976 10.7975 23.4382C10.3169 23.7913 9.68311 23.7913 9.20251 23.4382C6.83318 21.6976 0 16.1306 0 9.81818C8.22968e-08 4.39575 4.47715 0 10 0C15.5228 0 20 4.39575 20 9.81818Z"
                    fill={accent}
                />
                <circle cx="10" cy="9" r="3" fill={bg} />
            </svg>
        );
    },
    componentWeather: (p: IconProps) => {
        return <AppIcon color={p.bgColor} size={p.size} icon={"34-05-weather-app-sun-cloud-location"} />;
    },
    componentLocalTime: (p: IconProps) => {
        return <AppIcon color={p.bgColor} size={p.size} icon={"48-13-earth-time"} />;
    },
    install: (p: IconProps) => {
        const { fgColor } = p;
        return (
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M1.92857 11.9998V16.9998H4.07143V11.9998H1.92857ZM7 22.0712H17V19.9283H7V22.0712ZM22.0714 16.9998V11.9998H19.9286V16.9998H22.0714ZM17 22.0712C19.8009 22.0712 22.0714 19.8006 22.0714 16.9998H19.9286C19.9286 18.6172 18.6174 19.9283 17 19.9283V22.0712ZM1.92857 16.9998C1.92857 19.8006 4.19913 22.0712 7 22.0712V19.9283C5.38259 19.9283 4.07143 18.6172 4.07143 16.9998H1.92857Z"
                    fill={fgColor}
                />
                <path
                    d="M12 16L11.2424 16.7576C11.6608 17.176 12.3392 17.176 12.7576 16.7576L12 16ZM6.24239 11.7576L11.2424 16.7576L12.7576 15.2424L7.75761 10.2424L6.24239 11.7576ZM12.7576 16.7576L17.7576 11.7576L16.2424 10.2424L11.2424 15.2424L12.7576 16.7576Z"
                    fill={fgColor}
                />
                <path
                    d="M10.9286 3.00024L10.9286 15.5002L13.0714 15.5002L13.0714 3.00024L10.9286 3.00024Z"
                    fill={fgColor}
                />
            </svg>
        );
    },
    safari: () => {
        return (
            <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M10 18.75C14.8325 18.75 18.75 14.8325 18.75 10C18.75 5.16751 14.8325 1.25 10 1.25C5.16751 1.25 1.25 5.16751 1.25 10C1.25 14.8325 5.16751 18.75 10 18.75Z"
                    fill="url(#paint0_linear_358_6157)"
                />
                <path d="M15.9766 3.98438L9.25781 9.25781L10.8203 10.8203L15.9766 3.98438Z" fill="#FF0000" />
                <path d="M3.98438 15.9766L9.25781 9.25781L10.8203 10.8203L3.98438 15.9766Z" fill="white" />
                <path d="M10 2.10938V3.39844" stroke="white" strokeWidth="0.3" />
                <path d="M10 16.5625V17.8516" stroke="white" strokeWidth="0.3" />
                <path d="M11.3672 2.23047L11.1445 3.5" stroke="white" strokeWidth="0.3" />
                <path d="M8.85547 16.4609L8.63281 17.7305" stroke="white" strokeWidth="0.3" />
                <path d="M12.6914 2.58594L12.25 3.79688" stroke="white" strokeWidth="0.3" />
                <path d="M7.75 16.1641L7.30859 17.375" stroke="white" strokeWidth="0.3" />
                <path d="M8.63281 2.23047L8.85547 3.5" stroke="white" strokeWidth="0.3" />
                <path d="M11.1445 16.4609L11.3672 17.7305" stroke="white" strokeWidth="0.3" />
                <path d="M7.30859 2.58594L7.75 3.79688" stroke="white" strokeWidth="0.3" />
                <path d="M12.25 16.1641L12.6914 17.375" stroke="white" strokeWidth="0.3" />
                <path d="M6.0625 3.16406L6.71094 4.28125" stroke="white" strokeWidth="0.3" />
                <path d="M13.293 15.6797L13.9375 16.7969" stroke="white" strokeWidth="0.3" />
                <path d="M4.94141 3.94922L5.76953 4.9375" stroke="white" strokeWidth="0.3" />
                <path d="M14.2305 15.0234L15.0586 16.0117" stroke="white" strokeWidth="0.3" />
                <path d="M3.96875 4.92188L4.95703 5.75" stroke="white" strokeWidth="0.3" />
                <path d="M15.043 14.2109L16.0312 15.0391" stroke="white" strokeWidth="0.3" />
                <path d="M3.18359 6.04688L4.30078 6.69141" stroke="white" strokeWidth="0.3" />
                <path d="M15.6992 13.2734L16.8164 13.918" stroke="white" strokeWidth="0.3" />
                <path d="M2.60547 7.28906L3.81641 7.73047" stroke="white" strokeWidth="0.3" />
                <path d="M16.1836 12.2305L17.3945 12.6719" stroke="white" strokeWidth="0.3" />
                <path d="M2.25 8.61328L3.51953 8.83594" stroke="white" strokeWidth="0.3" />
                <path d="M16.4805 11.125L17.75 11.3477" stroke="white" strokeWidth="0.3" />
                <path d="M2.12891 9.98047H3.41797" stroke="white" strokeWidth="0.3" />
                <path d="M16.582 9.98047H17.8711" stroke="white" strokeWidth="0.3" />
                <path d="M2.25 11.3477L3.51953 11.125" stroke="white" strokeWidth="0.3" />
                <path d="M16.4805 8.83594L17.75 8.61328" stroke="white" strokeWidth="0.3" />
                <path d="M2.60547 12.6719L3.81641 12.2305" stroke="white" strokeWidth="0.3" />
                <path d="M16.1836 7.73047L17.3945 7.28906" stroke="white" strokeWidth="0.3" />
                <path d="M3.18359 13.918L4.30078 13.2734" stroke="white" strokeWidth="0.3" />
                <path d="M15.6992 6.69141L16.8164 6.04688" stroke="white" strokeWidth="0.3" />
                <path d="M3.96875 15.0391L4.95703 14.2109" stroke="white" strokeWidth="0.3" />
                <path d="M15.043 5.75L16.0312 4.92188" stroke="white" strokeWidth="0.3" />
                <path d="M4.94141 16.0117L5.76953 15.0234" stroke="white" strokeWidth="0.3" />
                <path d="M14.2305 4.9375L15.0586 3.94922" stroke="white" strokeWidth="0.3" />
                <path d="M6.0625 16.7969L6.71094 15.6797" stroke="white" strokeWidth="0.3" />
                <path d="M13.293 4.28125L13.9375 3.16406" stroke="white" strokeWidth="0.3" />
                <path d="M9.3125 2.14062L9.375 2.83984" stroke="white" strokeWidth="0.3" />
                <path d="M10.625 17.1211L10.6875 17.8203" stroke="white" strokeWidth="0.3" />
                <path d="M7.96094 2.37891L8.14453 3.05859" stroke="white" strokeWidth="0.3" />
                <path d="M11.8555 16.9023L12.0391 17.582" stroke="white" strokeWidth="0.3" />
                <path d="M6.67188 2.84766L6.96875 3.48437" stroke="white" strokeWidth="0.3" />
                <path d="M13.0273 16.4766L13.3281 17.1133" stroke="white" strokeWidth="0.3" />
                <path d="M5.48438 3.53125L5.88672 4.10937" stroke="white" strokeWidth="0.3" />
                <path d="M14.1133 15.8516L14.5156 16.4297" stroke="white" strokeWidth="0.3" />
                <path d="M4.43359 4.41406L4.92969 4.91016" stroke="white" strokeWidth="0.3" />
                <path d="M15.0703 15.0508L15.5664 15.5469" stroke="white" strokeWidth="0.3" />
                <path d="M3.55078 5.46484L4.12891 5.86719" stroke="white" strokeWidth="0.3" />
                <path d="M15.8711 14.0938L16.4492 14.4961" stroke="white" strokeWidth="0.3" />
                <path d="M2.86719 6.65234L3.50391 6.94922" stroke="white" strokeWidth="0.3" />
                <path d="M16.4961 13.0078L17.1328 13.3086" stroke="white" strokeWidth="0.3" />
                <path d="M2.39844 7.94141L3.07813 8.125" stroke="white" strokeWidth="0.3" />
                <path d="M16.9219 11.8359L17.6016 12.0195" stroke="white" strokeWidth="0.3" />
                <path d="M2.16016 9.29297L2.85937 9.35547" stroke="white" strokeWidth="0.3" />
                <path d="M17.1406 10.6055L17.8398 10.668" stroke="white" strokeWidth="0.3" />
                <path d="M2.16016 10.668L2.85937 10.6055" stroke="white" strokeWidth="0.3" />
                <path d="M17.1406 9.35547L17.8398 9.29297" stroke="white" strokeWidth="0.3" />
                <path d="M2.39844 12.0195L3.07813 11.8359" stroke="white" strokeWidth="0.3" />
                <path d="M16.9219 8.125L17.6016 7.94141" stroke="white" strokeWidth="0.3" />
                <path d="M2.86719 13.3086L3.50391 13.0117" stroke="white" strokeWidth="0.3" />
                <path d="M16.4961 6.94922L17.1328 6.65234" stroke="white" strokeWidth="0.3" />
                <path d="M3.55078 14.4961L4.12891 14.0938" stroke="white" strokeWidth="0.3" />
                <path d="M15.8711 5.86719L16.4492 5.46484" stroke="white" strokeWidth="0.3" />
                <path d="M5.48438 16.4297L5.88672 15.8516" stroke="white" strokeWidth="0.3" />
                <path d="M14.1094 4.10937L14.5156 3.53125" stroke="white" strokeWidth="0.3" />
                <path d="M6.67188 17.1133L6.97266 16.4766" stroke="white" strokeWidth="0.3" />
                <path d="M13.0273 3.48437L13.3281 2.84766" stroke="white" strokeWidth="0.3" />
                <path d="M7.96094 17.582L8.14453 16.9023" stroke="white" strokeWidth="0.3" />
                <path d="M11.8555 3.05859L12.0391 2.37891" stroke="white" strokeWidth="0.3" />
                <path d="M9.3125 17.8203L9.375 17.1211" stroke="white" strokeWidth="0.3" />
                <path d="M10.625 2.83984L10.6875 2.14062" stroke="white" strokeWidth="0.3" />
                <defs>
                    <linearGradient
                        id="paint0_linear_358_6157"
                        x1="1.25"
                        y1="1.25"
                        x2="1.25"
                        y2="18.75"
                        gradientUnits="userSpaceOnUse">
                        <stop stopColor="#19D7FF" />
                        <stop offset="1" stopColor="#1E64F0" />
                    </linearGradient>
                </defs>
            </svg>
        );
    },
    ...namedIconsLite,
};

type NamedIcons = typeof namedIcons;
export type IconName = keyof NamedIcons;
export function isIconName(icon: unknown): icon is IconName {
    return typeof icon === "string" && icon in namedIcons;
}

export function getNamedIcon(icon: IconName): NamedIcon {
    return namedIcons[icon];
}

function tryGetNamedIcon(icon: string): NamedIcon | undefined {
    return namedIcons[icon as IconName];
}

const Wrapper = styled.div<{ size: number; color: string }>`
    width: ${p => p.size}px;
    height: ${p => p.size}px;

    display: inline-flex;

    justify-content: center;
    align-items: center;

    & svg {
        fill: ${p => p.color};

        width: ${p => p.size}px;
        height: ${p => p.size}px;

        ${p => (p.color === undefined ? "--stroke-color: currentColor" : `--stroke-color: ${p.color}`)};
    }
`;

interface Props extends React.HTMLAttributes<HTMLDivElement> {
    icon: string | IconName;
    size?: number;
    color?: string;
    strokeWidth?: number;
    degreesOfRotation?: number;
    className?: string;
}

export const AppIcon: React.VFC<Props> = React.memo(p => {
    const { className, size, color, degreesOfRotation, strokeWidth, ...rest } = p;
    let icon = p.icon;

    let svgContent: React.ReactNode;

    const isPathIcon = icon.startsWith("path:");
    const isRemoteIcon = icon.match(/^[0-9][0-9]-.*/);
    let Icon: NamedIcon | undefined;

    if (isPathIcon) {
        const path = icon.split("path:")[1];

        svgContent = <SVG href={path} />;
    } else if (isRemoteIcon || (Icon = tryGetNamedIcon(icon)) === undefined) {
        if (!isRemoteIcon) {
            icon = map[icon];
            if (icon === undefined) {
                icon = "48-sign-badge-circle";
            }
        }

        svgContent = (
            <svg xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
                <use xlinkHref={`${getIconPath(icon)}#Bold`} />
            </svg>
        );
    } else {
        svgContent = (
            <Icon
                strokeWidth={strokeWidth}
                degreesOfRotation={degreesOfRotation}
                fgColor={color ?? "currentColor"}
                size={size ?? 24}
            />
        );
    }

    return (
        <Wrapper className={className} size={size ?? 24} color={color ?? "currentColor"} {...rest}>
            {svgContent}
        </Wrapper>
    );
});
